import React from "react";

import landing from "./pages/landing";
import V2Redirect from "./pages/landing-v2/V2Redirect";
import { DOUBLE_DASH_SERVICE_PREFIXES } from "./pages/location-based-pages/resolveLocationDataPath";

// Lazy-loaded page components
const landing2 = React.lazy(() => import("./pages/landing-2"));
const landing3 = React.lazy(() => import("./pages/landing-3"));
const landingV2 = React.lazy(() => import("./pages/landing-v2"));
const chatwithadmin = React.lazy(() => import("./pages/landing-2/chat-with-admin"));
const truckHire = React.lazy(() => import("./pages/truck-hire"));
const regularDeliveries = React.lazy(() => import("./pages/regular-deliveries"));
const truckHireThanks = React.lazy(() => import("./pages/truck-hire-thanks"));
const bakkieHire = React.lazy(() => import("./pages/bakkie-hire"));
const vehicleHirePriceSurvey = React.lazy(() => import("./pages/vehicle-hire-price-survey"));
const deliveriesForYourHome = React.lazy(() => import("./pages/furniture-delivery-services"));
const about = React.lazy(() => import("./pages/about"));
const goodslist = React.lazy(() => import("./pages/goods-list"));
const faqs = React.lazy(() => import("./pages/faqs"));
const vacancies = React.lazy(() => import("./pages/vacancies"));
const inServiceTraining = React.lazy(() => import("./pages/in-service-training"));
const marketplace = React.lazy(() => import("./pages/marketplace"));
const marketplaceBuilderProgram = React.lazy(() => import("./pages/marketplace-builder-program"));
const creatorProgram = React.lazy(() => import("./pages/creator-program"));
const promoters = React.lazy(() => import("./pages/promoters"));
const promoterChallenges = React.lazy(() => import("./pages/promoter-challenges"));
const customerApp = React.lazy(() => import("./pages/customer-app"));
const investors = React.lazy(() => import("./pages/investors"));
const deliverwithprice = React.lazy(() => import("./pages/deliver-with-price"));
const f404 = React.lazy(() => import("./pages/404"));
const promo = React.lazy(() => import("./pages/promo"));
const pickYourTruck = React.lazy(() => import("./pages/pick-your-truck"));
const drivertraining = React.lazy(() => import("./pages/driver-training"));
const developers = React.lazy(() => import("./pages/developers"));
const trackerPartnership = React.lazy(() => import("./pages/tracker-partnership"));
const welo = React.lazy(() => import("./pages/welo"));
const driverOfficeVisit = React.lazy(() => import("./pages/driver-office-visit"));
const insurance = React.lazy(() => import("./pages/insurance"));
const fuelOrder = React.lazy(() => import("./pages/fuel-order"));
const incidentManagement = React.lazy(() => import("./pages/driver-support/incident-management"));
const payments = React.lazy(() => import("./pages/driver-support/payments"));
const safetyGuidelines = React.lazy(() => import("./pages/driver-support/safety-guidelines"));
const vehicleMaintenance = React.lazy(() => import("./pages/driver-support/vehicle-maintenance"));
const deliveryTips = React.lazy(() => import("./pages/driver-support/delivery-tips"));
const vehicleInsurance = React.lazy(() => import("./pages/driver-support/vehicle-insurance"));
const gitInsurance = React.lazy(() => import("./pages/driver-support/git-insurance"));
const transportationnetwork = React.lazy(() => import("./pages/transportation-network"));
const unsubscribe = React.lazy(() => import("./pages/unsubscribe"));
const remoteworkatqiktruck = React.lazy(() => import("./pages/remote-work-at-qiktruck"));
const ads = React.lazy(() => import("./pages/ads"));
const driverpackages = React.lazy(() => import("./pages/driver-packages"));
const boost = React.lazy(() => import("./pages/boost"));
const articles = React.lazy(() => import("./pages/articles"));
const footer = React.lazy(() => import("./components/Footer/footer"));
const pricing = React.lazy(() => import("./pages/pricing"));
const vehicleRegistration = React.lazy(() => import("./pages/partner-drivers"));
const supplierregistration = React.lazy(() => import("./pages/supplier-registration"));
const trackorder = React.lazy(() => import("./pages/track-order"));
const rateDriver = React.lazy(() => import("./pages/rate-driver"));
const orders = React.lazy(() => import("./pages/orders"));
const realestatepartnerships = React.lazy(() => import("./pages/realestate-partnerships"));
const privacy = React.lazy(() => import("./pages/privacy"));
const terms = React.lazy(() => import("./pages/terms"));
const cancellationPolicy = React.lazy(() => import("./pages/cancellation-policy"));
const partnerDriverAgreement = React.lazy(() => import("./pages/partner-driver-agreement"));
const driverguide = React.lazy(() => import("./pages/driverguide"));

// Customer service hub pages
const movingCompanies          = React.lazy(() => import("./pages/moving-companies"));
const vanHire                  = React.lazy(() => import("./pages/van-hire"));
const furnitureRemovals        = React.lazy(() => import("./pages/furniture-removals"));
const sameDayCourier           = React.lazy(() => import("./pages/same-day-courier"));
const courierServices          = React.lazy(() => import("./pages/courier-services"));
const oneTonTruckHire          = React.lazy(() => import("./pages/1-ton-truck-hire"));
const fourTonTruckHire         = React.lazy(() => import("./pages/4-ton-truck-hire"));
const eightTonTruckHire        = React.lazy(() => import("./pages/8-ton-truck-hire"));
const flatbedTruckHire         = React.lazy(() => import("./pages/flatbed-truck-hire"));
const tipperTruckHire          = React.lazy(() => import("./pages/tipper-truck-hire"));
const refrigeratedTruckHire    = React.lazy(() => import("./pages/refrigerated-truck-hire"));
const bakkieHireNearMe         = React.lazy(() => import("./pages/bakkie-hire-near-me"));
const truckHireNearMe          = React.lazy(() => import("./pages/truck-hire-near-me"));
const movingCompaniesJhb       = React.lazy(() => import("./pages/moving-companies-johannesburg"));
const movingCompaniesCpt       = React.lazy(() => import("./pages/moving-companies-cape-town"));
const movingCompaniesDbn       = React.lazy(() => import("./pages/moving-companies-durban"));
const movingChecklist          = React.lazy(() => import("./pages/moving-checklist"));

// Driver / job content pages
const ownerDriverJobs              = React.lazy(() => import("./pages/owner-driver-jobs"));
const partTimeDriverJobs           = React.lazy(() => import("./pages/part-time-driver-jobs"));
const earnExtraIncomeWithVehicle   = React.lazy(() => import("./pages/earn-extra-income-with-vehicle"));
const pdpLicenceSouthAfrica        = React.lazy(() => import("./pages/pdp-licence-south-africa"));
const driverJobs                    = React.lazy(() => import("./pages/driver-jobs"));
const truckDriverJobs               = React.lazy(() => import("./pages/truck-driver-jobs"));
const earningsGuide                 = React.lazy(() => import("./pages/how-much-can-you-earn-as-a-qiktruck-driver"));
const requirementsGuide             = React.lazy(() => import("./pages/requirements-to-become-a-truck-driver"));
const bakkieOwnerIncome             = React.lazy(() => import("./pages/bakkie-owner-income"));

// Service-specific landing pages
const officeRemovals            = React.lazy(() => import("./pages/office-removals"));
const applianceDelivery         = React.lazy(() => import("./pages/appliance-delivery"));
const houseMoves                = React.lazy(() => import("./pages/house-moves"));
const lastMileDelivery          = React.lazy(() => import("./pages/last-mile-delivery"));
const pianoMoving               = React.lazy(() => import("./pages/piano-moving"));
const mattressDelivery          = React.lazy(() => import("./pages/mattress-delivery"));
const studentMoves              = React.lazy(() => import("./pages/student-moves"));
const constructionMaterialDelivery = React.lazy(() => import("./pages/construction-material-delivery"));

// Dynamic location page component (replaces 3,222 individual imports)
const LocationPage = React.lazy(() => import("./pages/location-based-pages/LocationPage"));

const doubleDashAliasRoutes = DOUBLE_DASH_SERVICE_PREFIXES.map((svc) => ({
  path: `/${svc}-:city`,
  component: LocationPage,
}));

const routes = [
  // Driver Support Routes
  { path: "/driver-support/incident-management", component: incidentManagement },
  { path: "/driver-support/payments", component: payments },
  { path: "/driver-support/safety-guidelines", component: safetyGuidelines },
  { path: "/driver-support/vehicle-maintenance", component: vehicleMaintenance },
  { path: "/driver-support/delivery-tips", component: deliveryTips },
  { path: "/driver-support/vehicle-insurance", component: vehicleInsurance },
  { path: "/driver-support/git-insurance", component: gitInsurance },
  { path: "/moving-companies", component: movingCompanies },
  { path: "/van-hire", component: vanHire },
  { path: "/furniture-removals", component: furnitureRemovals },
  { path: "/same-day-courier", component: sameDayCourier },
  { path: "/courier-services", component: courierServices },
  { path: "/1-ton-truck-hire", component: oneTonTruckHire },
  { path: "/4-ton-truck-hire", component: fourTonTruckHire },
  { path: "/8-ton-truck-hire", component: eightTonTruckHire },
  { path: "/flatbed-truck-hire", component: flatbedTruckHire },
  { path: "/tipper-truck-hire", component: tipperTruckHire },
  { path: "/refrigerated-truck-hire", component: refrigeratedTruckHire },
  { path: "/bakkie-hire-near-me", component: bakkieHireNearMe },
  { path: "/truck-hire-near-me", component: truckHireNearMe },
  { path: "/moving-companies-johannesburg", component: movingCompaniesJhb },
  { path: "/moving-companies-cape-town", component: movingCompaniesCpt },
  { path: "/moving-companies-durban", component: movingCompaniesDbn },
  { path: "/moving-checklist", component: movingChecklist },
  { path: "/owner-driver-jobs", component: ownerDriverJobs },
  { path: "/part-time-driver-jobs", component: partTimeDriverJobs },
  { path: "/earn-extra-income-with-vehicle", component: earnExtraIncomeWithVehicle },
  { path: "/pdp-licence-south-africa", component: pdpLicenceSouthAfrica },
  { path: "/driver-jobs", component: driverJobs },
  { path: "/truck-driver-jobs", component: truckDriverJobs },
  { path: "/how-much-can-you-earn-as-a-qiktruck-driver", component: earningsGuide },
  { path: "/requirements-to-become-a-truck-driver", component: requirementsGuide },
  { path: "/bakkie-owner-income", component: bakkieOwnerIncome },
  { path: "/furniture-delivery-services", component: deliveriesForYourHome },
  { path: "/office-removals", component: officeRemovals },
  { path: "/appliance-delivery", component: applianceDelivery },
  { path: "/house-moves", component: houseMoves },
  { path: "/last-mile-delivery", component: lastMileDelivery },
  { path: "/piano-moving", component: pianoMoving },
  { path: "/mattress-delivery", component: mattressDelivery },
  { path: "/student-moves", component: studentMoves },
  { path: "/construction-material-delivery", component: constructionMaterialDelivery },
  { path: "/bakkie-hire", component: bakkieHire },
  { path: "/bakkie-hire-johannesburg", component: LocationPage },
  { path: "/bakkie-hire-randburg", component: LocationPage },
  { path: "/randburg-furniture-transport", component: LocationPage },
  { path: "/sandton-furniture-transport", component: LocationPage },
  { path: "/midrand-furniture-transport", component: LocationPage },
  { path: "/centurion-furniture-transport", component: LocationPage },
  { path: "/pretoria-furniture-transport", component: LocationPage },
  { path: "/johannesburg-furniture-transport", component: LocationPage },
  { path: "/bakkie-hire-pretoria", component: LocationPage },
  { path: "/bakkie-hire-sandton", component: LocationPage },
  { path: "/bakkie-hire-roodeport", component: LocationPage },
  { path: "/bakkie-hire-soweto", component: LocationPage },
  { path: "/bakkie-hire-gauteng", component: bakkieHire },
  { path: "/bakkie-hire-midrand", component: LocationPage },
  { path: "/bakkie-hire-centurion", component: LocationPage },
  { path: "/vehicle-hire-price-survey", component: vehicleHirePriceSurvey },
  { path: "/truck-hire", component: truckHire },
  { path: "/regular-deliveries", component: regularDeliveries },
  { path: "/truck-hire-thanks", component: truckHireThanks },
  { path: "/customer-truck-hire", component: landing },
  { path: "/marketplace", component: marketplace },
  { path: "/tracker-partnership", component: trackerPartnership },
  { path: "/welo", component: welo },
  { path: "/terms", component: terms },
  { path: "/cancellation-policy", component: cancellationPolicy },
  { path: "/partner-driver-agreement", component: partnerDriverAgreement },
  { path: "/driver-training", component: drivertraining },
  { path: "/privacy", component: privacy },
  { path: "/about", component: about },
  { path: "/what-is-qiktruck", component: investors },
  { path: "/remote-work-at-qiktruck", component: remoteworkatqiktruck },
  { path: "/faqs", component: faqs },
  { path: "/vacancies", component: vacancies },
  { path: "/in-service-training", component: inServiceTraining },
  { path: "/customer-truck-hire-contracts", component: landing },
  { path: "/investors", component: investors },
  { path: "/deliver-with-price", component: deliverwithprice },
  { path: "/promo", component: promo },
  { path: "/pick-your-truck", component: pickYourTruck },
  { path: "/insurance", component: insurance },
  { path: "/developers", component: developers },
  { path: "/transportation-network", component: transportationnetwork },
  { path: "/unsubscribe", component: unsubscribe },
  { path: "/ads", component: ads },
  { path: "/driver-packages", component: driverpackages },
  { path: "/boost", component: boost },
  { path: "/articles/:id?", component: articles },
  { path: "/howitworks", component: about },
  { path: "/contact", component: about },
  { path: "/pricing", component: pricing },
  { path: "/footer", component: footer },
  { path: "/driver-guide", component: driverguide },
  { path: "/track-order", component: trackorder },
  { path: "/rate-driver", component: rateDriver },
  { path: "/orders", component: orders },
  { path: "/customer-profile", component: landing },
  { path: "/truck-hire", component: truckHire },
  { path: "/partner-drivers", component: vehicleRegistration },
  { path: "/register-your-vehicle", component: vehicleRegistration },
  { path: "/goods-list", component: goodslist },
  { path: "/supplier-registration", component: supplierregistration },
  { path: "/v2", component: V2Redirect },
  { path: "/", component: landingV2 },
  { path: "/quote", component: landing2 },
  { path: "/book", component: landing3 },
  { path: "/book-a-truck", component: landing3 },
  { path: "/instant-quote", component: landing3 },
  { path: "/get-quote", component: landing3 },
  { path: "/landing-3", component: landing3 },
  { path: "/chat", component: chatwithadmin },
  { path: "/realestate-partnerships", component: realestatepartnerships },
  { path: "/app", component: customerApp },
  { path: "/marketplace-builder-program", component: marketplaceBuilderProgram },
  { path: "/creator-program", component: creatorProgram },
  { path: "/promoters", component: promoters },
  { path: "/promoters/challenges", component: promoterChallenges },
  { path: "/driver-office-visit", component: driverOfficeVisit },
  { path: "/fuel-order", component: fuelOrder },
  { path: "/customer-regular-trip-driver", component: landing },

  // Location-based pages (all use shared LocationPage component)
  { path: "/1-ton-alberton", component: LocationPage },
  { path: "/1-ton-aliwal-north", component: LocationPage },
  { path: "/1-ton-beaufort-west", component: LocationPage },
  { path: "/1-ton-benoni", component: LocationPage },
  { path: "/1-ton-bethal", component: LocationPage },
  { path: "/1-ton-bhisho", component: LocationPage },
  { path: "/1-ton-bloemfontein", component: LocationPage },
  { path: "/1-ton-brakpan", component: LocationPage },
  { path: "/1-ton-brits", component: LocationPage },
  { path: "/1-ton-cape-town", component: LocationPage },
  { path: "/1-ton-carletonville", component: LocationPage },
  { path: "/1-ton-centurion", component: LocationPage },
  { path: "/1-ton-cradock", component: LocationPage },
  { path: "/1-ton-de-aar", component: LocationPage },
  { path: "/1-ton-durban", component: LocationPage },
  { path: "/1-ton-east-london", component: LocationPage },
  { path: "/1-ton-ferndale", component: LocationPage },
  { path: "/1-ton-george", component: LocationPage },
  { path: "/1-ton-graaff-reinet", component: LocationPage },
  { path: "/1-ton-grahamstown", component: LocationPage },
  { path: "/1-ton-hermanus", component: LocationPage },
  { path: "/1-ton-jeffreys-bay", component: LocationPage },
  { path: "/1-ton-johannesburg", component: LocationPage },
  { path: "/1-ton-kimberley", component: LocationPage },
  { path: "/1-ton-klerksdorp", component: LocationPage },
  { path: "/1-ton-knysna", component: LocationPage },
  { path: "/1-ton-kroonstad", component: LocationPage },
  { path: "/1-ton-kuilsrivier", component: LocationPage },
  { path: "/1-ton-ladysmith", component: LocationPage },
  { path: "/1-ton-lebowakgomo", component: LocationPage },
  { path: "/1-ton-lichtenburg", component: LocationPage },
  { path: "/1-ton-mahikeng", component: LocationPage },
  { path: "/1-ton-malmesbury", component: LocationPage },
  { path: "/1-ton-middelburg", component: LocationPage },
  { path: "/1-ton-midrand", component: LocationPage },
  { path: "/1-ton-mmabatho", component: LocationPage },
  { path: "/1-ton-mossel-bay", component: LocationPage },
  { path: "/1-ton-musina", component: LocationPage },
  { path: "/1-ton-nelspruit", component: LocationPage },
  { path: "/1-ton-oudtshoorn", component: LocationPage },
  { path: "/1-ton-paarl", component: LocationPage },
  { path: "/1-ton-pietermaritzburg", component: LocationPage },
  { path: "/1-ton-polokwane", component: LocationPage },
  { path: "/1-ton-port-elizabeth", component: LocationPage },
  { path: "/1-ton-port-shepstone", component: LocationPage },
  { path: "/1-ton-potchefstroom", component: LocationPage },
  { path: "/1-ton-pretoria", component: LocationPage },
  { path: "/1-ton-queenstown", component: LocationPage },
  { path: "/1-ton-randburg", component: LocationPage },
  { path: "/1-ton-roodepoort", component: LocationPage },
  { path: "/1-ton-rustenburg", component: LocationPage },
  { path: "/1-ton-saldanha", component: LocationPage },
  { path: "/1-ton-sandton", component: LocationPage },
  { path: "/1-ton-soweto", component: LocationPage },
  { path: "/1-ton-springs", component: LocationPage },
  { path: "/1-ton-standerton", component: LocationPage },
  { path: "/1-ton-thohoyandou", component: LocationPage },
  { path: "/1-ton-tzaneen", component: LocationPage },
  { path: "/1-ton-uitenhage", component: LocationPage },
  { path: "/1-ton-ulundi", component: LocationPage },
  { path: "/1-ton-umtata", component: LocationPage },
  { path: "/1-ton-upington", component: LocationPage },
  { path: "/1-ton-vanderbijlpark", component: LocationPage },
  { path: "/1-ton-vereeniging", component: LocationPage },
  { path: "/1-ton-volksrust", component: LocationPage },
  { path: "/1-ton-vredenburg", component: LocationPage },
  { path: "/1-ton-vryburg", component: LocationPage },
  { path: "/1-ton-vryheid", component: LocationPage },
  { path: "/1-ton-welkom", component: LocationPage },
  { path: "/1-ton-worcester", component: LocationPage },
  { path: "/1.5-ton-alberton", component: LocationPage },
  { path: "/1.5-ton-aliwal-north", component: LocationPage },
  { path: "/1.5-ton-beaufort-west", component: LocationPage },
  { path: "/1.5-ton-benoni", component: LocationPage },
  { path: "/1.5-ton-bethal", component: LocationPage },
  { path: "/1.5-ton-bhisho", component: LocationPage },
  { path: "/1.5-ton-bloemfontein", component: LocationPage },
  { path: "/1.5-ton-brakpan", component: LocationPage },
  { path: "/1.5-ton-brits", component: LocationPage },
  { path: "/1.5-ton-cape-town", component: LocationPage },
  { path: "/1.5-ton-carletonville", component: LocationPage },
  { path: "/1.5-ton-centurion", component: LocationPage },
  { path: "/1.5-ton-cradock", component: LocationPage },
  { path: "/1.5-ton-de-aar", component: LocationPage },
  { path: "/1.5-ton-durban", component: LocationPage },
  { path: "/1.5-ton-east-london", component: LocationPage },
  { path: "/1.5-ton-ferndale", component: LocationPage },
  { path: "/1.5-ton-george", component: LocationPage },
  { path: "/1.5-ton-graaff-reinet", component: LocationPage },
  { path: "/1.5-ton-grahamstown", component: LocationPage },
  { path: "/1.5-ton-hermanus", component: LocationPage },
  { path: "/1.5-ton-jeffreys-bay", component: LocationPage },
  { path: "/1.5-ton-johannesburg", component: LocationPage },
  { path: "/1.5-ton-kimberley", component: LocationPage },
  { path: "/1.5-ton-klerksdorp", component: LocationPage },
  { path: "/1.5-ton-knysna", component: LocationPage },
  { path: "/1.5-ton-kroonstad", component: LocationPage },
  { path: "/1.5-ton-kuilsrivier", component: LocationPage },
  { path: "/1.5-ton-ladysmith", component: LocationPage },
  { path: "/1.5-ton-lebowakgomo", component: LocationPage },
  { path: "/1.5-ton-lichtenburg", component: LocationPage },
  { path: "/1.5-ton-mahikeng", component: LocationPage },
  { path: "/1.5-ton-malmesbury", component: LocationPage },
  { path: "/1.5-ton-middelburg", component: LocationPage },
  { path: "/1.5-ton-midrand", component: LocationPage },
  { path: "/1.5-ton-mmabatho", component: LocationPage },
  { path: "/1.5-ton-mossel-bay", component: LocationPage },
  { path: "/1.5-ton-musina", component: LocationPage },
  { path: "/1.5-ton-nelspruit", component: LocationPage },
  { path: "/1.5-ton-oudtshoorn", component: LocationPage },
  { path: "/1.5-ton-paarl", component: LocationPage },
  { path: "/1.5-ton-pietermaritzburg", component: LocationPage },
  { path: "/1.5-ton-polokwane", component: LocationPage },
  { path: "/1.5-ton-port-elizabeth", component: LocationPage },
  { path: "/1.5-ton-port-shepstone", component: LocationPage },
  { path: "/1.5-ton-potchefstroom", component: LocationPage },
  { path: "/1.5-ton-pretoria", component: LocationPage },
  { path: "/1.5-ton-queenstown", component: LocationPage },
  { path: "/1.5-ton-randburg", component: LocationPage },
  { path: "/1.5-ton-roodepoort", component: LocationPage },
  { path: "/1.5-ton-rustenburg", component: LocationPage },
  { path: "/1.5-ton-saldanha", component: LocationPage },
  { path: "/1.5-ton-sandton", component: LocationPage },
  { path: "/1.5-ton-soweto", component: LocationPage },
  { path: "/1.5-ton-springs", component: LocationPage },
  { path: "/1.5-ton-standerton", component: LocationPage },
  { path: "/1.5-ton-thohoyandou", component: LocationPage },
  { path: "/1.5-ton-tzaneen", component: LocationPage },
  { path: "/1.5-ton-uitenhage", component: LocationPage },
  { path: "/1.5-ton-ulundi", component: LocationPage },
  { path: "/1.5-ton-umtata", component: LocationPage },
  { path: "/1.5-ton-upington", component: LocationPage },
  { path: "/1.5-ton-vanderbijlpark", component: LocationPage },
  { path: "/1.5-ton-vereeniging", component: LocationPage },
  { path: "/1.5-ton-volksrust", component: LocationPage },
  { path: "/1.5-ton-vredenburg", component: LocationPage },
  { path: "/1.5-ton-vryburg", component: LocationPage },
  { path: "/1.5-ton-vryheid", component: LocationPage },
  { path: "/1.5-ton-welkom", component: LocationPage },
  { path: "/1.5-ton-worcester", component: LocationPage },
  { path: "/4-ton-alberton", component: LocationPage },
  { path: "/4-ton-aliwal-north", component: LocationPage },
  { path: "/4-ton-beaufort-west", component: LocationPage },
  { path: "/4-ton-benoni", component: LocationPage },
  { path: "/4-ton-bethal", component: LocationPage },
  { path: "/4-ton-bhisho", component: LocationPage },
  { path: "/4-ton-bloemfontein", component: LocationPage },
  { path: "/4-ton-brakpan", component: LocationPage },
  { path: "/4-ton-brits", component: LocationPage },
  { path: "/4-ton-cape-town", component: LocationPage },
  { path: "/4-ton-carletonville", component: LocationPage },
  { path: "/4-ton-centurion", component: LocationPage },
  { path: "/4-ton-cradock", component: LocationPage },
  { path: "/4-ton-de-aar", component: LocationPage },
  { path: "/4-ton-durban", component: LocationPage },
  { path: "/4-ton-east-london", component: LocationPage },
  { path: "/4-ton-ferndale", component: LocationPage },
  { path: "/4-ton-george", component: LocationPage },
  { path: "/4-ton-graaff-reinet", component: LocationPage },
  { path: "/4-ton-grahamstown", component: LocationPage },
  { path: "/4-ton-hermanus", component: LocationPage },
  { path: "/4-ton-jeffreys-bay", component: LocationPage },
  { path: "/4-ton-johannesburg", component: LocationPage },
  { path: "/4-ton-kimberley", component: LocationPage },
  { path: "/4-ton-klerksdorp", component: LocationPage },
  { path: "/4-ton-knysna", component: LocationPage },
  { path: "/4-ton-kroonstad", component: LocationPage },
  { path: "/4-ton-kuilsrivier", component: LocationPage },
  { path: "/4-ton-ladysmith", component: LocationPage },
  { path: "/4-ton-lebowakgomo", component: LocationPage },
  { path: "/4-ton-lichtenburg", component: LocationPage },
  { path: "/4-ton-mahikeng", component: LocationPage },
  { path: "/4-ton-malmesbury", component: LocationPage },
  { path: "/4-ton-middelburg", component: LocationPage },
  { path: "/4-ton-midrand", component: LocationPage },
  { path: "/4-ton-mmabatho", component: LocationPage },
  { path: "/4-ton-mossel-bay", component: LocationPage },
  { path: "/4-ton-musina", component: LocationPage },
  { path: "/4-ton-nelspruit", component: LocationPage },
  { path: "/4-ton-oudtshoorn", component: LocationPage },
  { path: "/4-ton-paarl", component: LocationPage },
  { path: "/4-ton-pietermaritzburg", component: LocationPage },
  { path: "/4-ton-polokwane", component: LocationPage },
  { path: "/4-ton-port-elizabeth", component: LocationPage },
  { path: "/4-ton-port-shepstone", component: LocationPage },
  { path: "/4-ton-potchefstroom", component: LocationPage },
  { path: "/4-ton-pretoria", component: LocationPage },
  { path: "/4-ton-queenstown", component: LocationPage },
  { path: "/4-ton-randburg", component: LocationPage },
  { path: "/4-ton-roodepoort", component: LocationPage },
  { path: "/4-ton-rustenburg", component: LocationPage },
  { path: "/4-ton-saldanha", component: LocationPage },
  { path: "/4-ton-sandton", component: LocationPage },
  { path: "/4-ton-soweto", component: LocationPage },
  { path: "/4-ton-springs", component: LocationPage },
  { path: "/4-ton-standerton", component: LocationPage },
  { path: "/4-ton-thohoyandou", component: LocationPage },
  { path: "/4-ton-tzaneen", component: LocationPage },
  { path: "/4-ton-uitenhage", component: LocationPage },
  { path: "/4-ton-ulundi", component: LocationPage },
  { path: "/4-ton-umtata", component: LocationPage },
  { path: "/4-ton-upington", component: LocationPage },
  { path: "/4-ton-vanderbijlpark", component: LocationPage },
  { path: "/4-ton-vereeniging", component: LocationPage },
  { path: "/4-ton-volksrust", component: LocationPage },
  { path: "/4-ton-vredenburg", component: LocationPage },
  { path: "/4-ton-vryburg", component: LocationPage },
  { path: "/4-ton-vryheid", component: LocationPage },
  { path: "/4-ton-welkom", component: LocationPage },
  { path: "/4-ton-worcester", component: LocationPage },
  { path: "/8-ton-alberton", component: LocationPage },
  { path: "/8-ton-aliwal-north", component: LocationPage },
  { path: "/8-ton-beaufort-west", component: LocationPage },
  { path: "/8-ton-benoni", component: LocationPage },
  { path: "/8-ton-bethal", component: LocationPage },
  { path: "/8-ton-bhisho", component: LocationPage },
  { path: "/8-ton-bloemfontein", component: LocationPage },
  { path: "/8-ton-brakpan", component: LocationPage },
  { path: "/8-ton-brits", component: LocationPage },
  { path: "/8-ton-cape-town", component: LocationPage },
  { path: "/8-ton-carletonville", component: LocationPage },
  { path: "/8-ton-centurion", component: LocationPage },
  { path: "/8-ton-cradock", component: LocationPage },
  { path: "/8-ton-de-aar", component: LocationPage },
  { path: "/8-ton-durban", component: LocationPage },
  { path: "/8-ton-east-london", component: LocationPage },
  { path: "/8-ton-ferndale", component: LocationPage },
  { path: "/8-ton-george", component: LocationPage },
  { path: "/8-ton-graaff-reinet", component: LocationPage },
  { path: "/8-ton-grahamstown", component: LocationPage },
  { path: "/8-ton-hermanus", component: LocationPage },
  { path: "/8-ton-jeffreys-bay", component: LocationPage },
  { path: "/8-ton-johannesburg", component: LocationPage },
  { path: "/8-ton-kimberley", component: LocationPage },
  { path: "/8-ton-klerksdorp", component: LocationPage },
  { path: "/8-ton-knysna", component: LocationPage },
  { path: "/8-ton-kroonstad", component: LocationPage },
  { path: "/8-ton-kuilsrivier", component: LocationPage },
  { path: "/8-ton-ladysmith", component: LocationPage },
  { path: "/8-ton-lebowakgomo", component: LocationPage },
  { path: "/8-ton-lichtenburg", component: LocationPage },
  { path: "/8-ton-mahikeng", component: LocationPage },
  { path: "/8-ton-malmesbury", component: LocationPage },
  { path: "/8-ton-middelburg", component: LocationPage },
  { path: "/8-ton-midrand", component: LocationPage },
  { path: "/8-ton-mmabatho", component: LocationPage },
  { path: "/8-ton-mossel-bay", component: LocationPage },
  { path: "/8-ton-musina", component: LocationPage },
  { path: "/8-ton-nelspruit", component: LocationPage },
  { path: "/8-ton-oudtshoorn", component: LocationPage },
  { path: "/8-ton-paarl", component: LocationPage },
  { path: "/8-ton-pietermaritzburg", component: LocationPage },
  { path: "/8-ton-polokwane", component: LocationPage },
  { path: "/8-ton-port-elizabeth", component: LocationPage },
  { path: "/8-ton-port-shepstone", component: LocationPage },
  { path: "/8-ton-potchefstroom", component: LocationPage },
  { path: "/8-ton-pretoria", component: LocationPage },
  { path: "/8-ton-queenstown", component: LocationPage },
  { path: "/8-ton-randburg", component: LocationPage },
  { path: "/8-ton-roodepoort", component: LocationPage },
  { path: "/8-ton-rustenburg", component: LocationPage },
  { path: "/8-ton-saldanha", component: LocationPage },
  { path: "/8-ton-sandton", component: LocationPage },
  { path: "/8-ton-soweto", component: LocationPage },
  { path: "/8-ton-springs", component: LocationPage },
  { path: "/8-ton-standerton", component: LocationPage },
  { path: "/8-ton-thohoyandou", component: LocationPage },
  { path: "/8-ton-tzaneen", component: LocationPage },
  { path: "/8-ton-uitenhage", component: LocationPage },
  { path: "/8-ton-ulundi", component: LocationPage },
  { path: "/8-ton-umtata", component: LocationPage },
  { path: "/8-ton-upington", component: LocationPage },
  { path: "/8-ton-vanderbijlpark", component: LocationPage },
  { path: "/8-ton-vereeniging", component: LocationPage },
  { path: "/8-ton-volksrust", component: LocationPage },
  { path: "/8-ton-vredenburg", component: LocationPage },
  { path: "/8-ton-vryburg", component: LocationPage },
  { path: "/8-ton-vryheid", component: LocationPage },
  { path: "/8-ton-welkom", component: LocationPage },
  { path: "/8-ton-worcester", component: LocationPage },
  { path: "/bakkie-hire--alberton", component: LocationPage },
  { path: "/bakkie-hire--aliwal-north", component: LocationPage },
  { path: "/bakkie-hire--beaufort-west", component: LocationPage },
  { path: "/bakkie-hire--benoni", component: LocationPage },
  { path: "/bakkie-hire--bethal", component: LocationPage },
  { path: "/bakkie-hire--bhisho", component: LocationPage },
  { path: "/bakkie-hire--bloemfontein", component: LocationPage },
  { path: "/bakkie-hire--brakpan", component: LocationPage },
  { path: "/bakkie-hire--brits", component: LocationPage },
  { path: "/bakkie-hire--cape-town", component: LocationPage },
  { path: "/bakkie-hire--carletonville", component: LocationPage },
  { path: "/bakkie-hire--centurion", component: LocationPage },
  { path: "/bakkie-hire--cradock", component: LocationPage },
  { path: "/bakkie-hire--de-aar", component: LocationPage },
  { path: "/bakkie-hire--durban", component: LocationPage },
  { path: "/bakkie-hire--east-london", component: LocationPage },
  { path: "/bakkie-hire--ferndale", component: LocationPage },
  { path: "/bakkie-hire--george", component: LocationPage },
  { path: "/bakkie-hire--graaff-reinet", component: LocationPage },
  { path: "/bakkie-hire--grahamstown", component: LocationPage },
  { path: "/bakkie-hire--hermanus", component: LocationPage },
  { path: "/bakkie-hire--jeffreys-bay", component: LocationPage },
  { path: "/bakkie-hire--johannesburg", component: LocationPage },
  { path: "/bakkie-hire--kimberley", component: LocationPage },
  { path: "/bakkie-hire--klerksdorp", component: LocationPage },
  { path: "/bakkie-hire--knysna", component: LocationPage },
  { path: "/bakkie-hire--kroonstad", component: LocationPage },
  { path: "/bakkie-hire--kuilsrivier", component: LocationPage },
  { path: "/bakkie-hire--ladysmith", component: LocationPage },
  { path: "/bakkie-hire--lebowakgomo", component: LocationPage },
  { path: "/bakkie-hire--lichtenburg", component: LocationPage },
  { path: "/bakkie-hire--mahikeng", component: LocationPage },
  { path: "/bakkie-hire--malmesbury", component: LocationPage },
  { path: "/bakkie-hire--middelburg", component: LocationPage },
  { path: "/bakkie-hire--midrand", component: LocationPage },
  { path: "/bakkie-hire--mmabatho", component: LocationPage },
  { path: "/bakkie-hire--mossel-bay", component: LocationPage },
  { path: "/bakkie-hire--musina", component: LocationPage },
  { path: "/bakkie-hire--nelspruit", component: LocationPage },
  { path: "/bakkie-hire--oudtshoorn", component: LocationPage },
  { path: "/bakkie-hire--paarl", component: LocationPage },
  { path: "/bakkie-hire--pietermaritzburg", component: LocationPage },
  { path: "/bakkie-hire--polokwane", component: LocationPage },
  { path: "/bakkie-hire--port-elizabeth", component: LocationPage },
  { path: "/bakkie-hire--port-shepstone", component: LocationPage },
  { path: "/bakkie-hire--potchefstroom", component: LocationPage },
  { path: "/bakkie-hire--pretoria", component: LocationPage },
  { path: "/bakkie-hire--queenstown", component: LocationPage },
  { path: "/bakkie-hire--randburg", component: LocationPage },
  { path: "/bakkie-hire--roodepoort", component: LocationPage },
  { path: "/bakkie-hire--rustenburg", component: LocationPage },
  { path: "/bakkie-hire--saldanha", component: LocationPage },
  { path: "/bakkie-hire--sandton", component: LocationPage },
  { path: "/bakkie-hire--soweto", component: LocationPage },
  { path: "/bakkie-hire--springs", component: LocationPage },
  { path: "/bakkie-hire--standerton", component: LocationPage },
  { path: "/bakkie-hire--thohoyandou", component: LocationPage },
  { path: "/bakkie-hire--tzaneen", component: LocationPage },
  { path: "/bakkie-hire--uitenhage", component: LocationPage },
  { path: "/bakkie-hire--ulundi", component: LocationPage },
  { path: "/bakkie-hire--umtata", component: LocationPage },
  { path: "/bakkie-hire--upington", component: LocationPage },
  { path: "/bakkie-hire--vanderbijlpark", component: LocationPage },
  { path: "/bakkie-hire--vereeniging", component: LocationPage },
  { path: "/bakkie-hire--volksrust", component: LocationPage },
  { path: "/bakkie-hire--vredenburg", component: LocationPage },
  { path: "/bakkie-hire--vryburg", component: LocationPage },
  { path: "/bakkie-hire--vryheid", component: LocationPage },
  { path: "/bakkie-hire--welkom", component: LocationPage },
  { path: "/bakkie-hire--worcester", component: LocationPage },
  { path: "/business-delivery-alberton", component: LocationPage },
  { path: "/business-delivery-aliwal-north", component: LocationPage },
  { path: "/business-delivery-beaufort-west", component: LocationPage },
  { path: "/business-delivery-benoni", component: LocationPage },
  { path: "/business-delivery-bethal", component: LocationPage },
  { path: "/business-delivery-bhisho", component: LocationPage },
  { path: "/business-delivery-bloemfontein", component: LocationPage },
  { path: "/business-delivery-brakpan", component: LocationPage },
  { path: "/business-delivery-brits", component: LocationPage },
  { path: "/business-delivery-cape-town", component: LocationPage },
  { path: "/business-delivery-carletonville", component: LocationPage },
  { path: "/business-delivery-centurion", component: LocationPage },
  { path: "/business-delivery-cradock", component: LocationPage },
  { path: "/business-delivery-de-aar", component: LocationPage },
  { path: "/business-delivery-durban", component: LocationPage },
  { path: "/business-delivery-east-london", component: LocationPage },
  { path: "/business-delivery-ferndale", component: LocationPage },
  { path: "/business-delivery-george", component: LocationPage },
  { path: "/business-delivery-graaff-reinet", component: LocationPage },
  { path: "/business-delivery-grahamstown", component: LocationPage },
  { path: "/business-delivery-hermanus", component: LocationPage },
  { path: "/business-delivery-jeffreys-bay", component: LocationPage },
  { path: "/business-delivery-johannesburg", component: LocationPage },
  { path: "/business-delivery-kimberley", component: LocationPage },
  { path: "/business-delivery-klerksdorp", component: LocationPage },
  { path: "/business-delivery-knysna", component: LocationPage },
  { path: "/business-delivery-kroonstad", component: LocationPage },
  { path: "/business-delivery-kuilsrivier", component: LocationPage },
  { path: "/business-delivery-ladysmith", component: LocationPage },
  { path: "/business-delivery-lebowakgomo", component: LocationPage },
  { path: "/business-delivery-lichtenburg", component: LocationPage },
  { path: "/business-delivery-mahikeng", component: LocationPage },
  { path: "/business-delivery-malmesbury", component: LocationPage },
  { path: "/business-delivery-middelburg", component: LocationPage },
  { path: "/business-delivery-midrand", component: LocationPage },
  { path: "/business-delivery-mmabatho", component: LocationPage },
  { path: "/business-delivery-mossel-bay", component: LocationPage },
  { path: "/business-delivery-musina", component: LocationPage },
  { path: "/business-delivery-nelspruit", component: LocationPage },
  { path: "/business-delivery-oudtshoorn", component: LocationPage },
  { path: "/business-delivery-paarl", component: LocationPage },
  { path: "/business-delivery-pietermaritzburg", component: LocationPage },
  { path: "/business-delivery-polokwane", component: LocationPage },
  { path: "/business-delivery-port-elizabeth", component: LocationPage },
  { path: "/business-delivery-port-shepstone", component: LocationPage },
  { path: "/business-delivery-potchefstroom", component: LocationPage },
  { path: "/business-delivery-pretoria", component: LocationPage },
  { path: "/business-delivery-queenstown", component: LocationPage },
  { path: "/business-delivery-randburg", component: LocationPage },
  { path: "/business-delivery-roodepoort", component: LocationPage },
  { path: "/business-delivery-rustenburg", component: LocationPage },
  { path: "/business-delivery-saldanha", component: LocationPage },
  { path: "/business-delivery-sandton", component: LocationPage },
  { path: "/business-delivery-soweto", component: LocationPage },
  { path: "/business-delivery-springs", component: LocationPage },
  { path: "/business-delivery-standerton", component: LocationPage },
  { path: "/business-delivery-thohoyandou", component: LocationPage },
  { path: "/business-delivery-tzaneen", component: LocationPage },
  { path: "/business-delivery-uitenhage", component: LocationPage },
  { path: "/business-delivery-ulundi", component: LocationPage },
  { path: "/business-delivery-umtata", component: LocationPage },
  { path: "/business-delivery-upington", component: LocationPage },
  { path: "/business-delivery-vanderbijlpark", component: LocationPage },
  { path: "/business-delivery-vereeniging", component: LocationPage },
  { path: "/business-delivery-volksrust", component: LocationPage },
  { path: "/business-delivery-vredenburg", component: LocationPage },
  { path: "/business-delivery-vryburg", component: LocationPage },
  { path: "/business-delivery-vryheid", component: LocationPage },
  { path: "/business-delivery-welkom", component: LocationPage },
  { path: "/business-delivery-worcester", component: LocationPage },
  { path: "/cheap-movers-near-me-alberton", component: LocationPage },
  { path: "/cheap-movers-near-me-aliwal-north", component: LocationPage },
  { path: "/cheap-movers-near-me-beaufort-west", component: LocationPage },
  { path: "/cheap-movers-near-me-benoni", component: LocationPage },
  { path: "/cheap-movers-near-me-bethal", component: LocationPage },
  { path: "/cheap-movers-near-me-bhisho", component: LocationPage },
  { path: "/cheap-movers-near-me-bloemfontein", component: LocationPage },
  { path: "/cheap-movers-near-me-brakpan", component: LocationPage },
  { path: "/cheap-movers-near-me-brits", component: LocationPage },
  { path: "/cheap-movers-near-me-cape-town", component: LocationPage },
  { path: "/cheap-movers-near-me-carletonville", component: LocationPage },
  { path: "/cheap-movers-near-me-centurion", component: LocationPage },
  { path: "/cheap-movers-near-me-cradock", component: LocationPage },
  { path: "/cheap-movers-near-me-de-aar", component: LocationPage },
  { path: "/cheap-movers-near-me-durban", component: LocationPage },
  { path: "/cheap-movers-near-me-east-london", component: LocationPage },
  { path: "/cheap-movers-near-me-ferndale", component: LocationPage },
  { path: "/cheap-movers-near-me-george", component: LocationPage },
  { path: "/cheap-movers-near-me-graaff-reinet", component: LocationPage },
  { path: "/cheap-movers-near-me-grahamstown", component: LocationPage },
  { path: "/cheap-movers-near-me-hermanus", component: LocationPage },
  { path: "/cheap-movers-near-me-jeffreys-bay", component: LocationPage },
  { path: "/cheap-movers-near-me-johannesburg", component: LocationPage },
  { path: "/cheap-movers-near-me-kimberley", component: LocationPage },
  { path: "/cheap-movers-near-me-klerksdorp", component: LocationPage },
  { path: "/cheap-movers-near-me-knysna", component: LocationPage },
  { path: "/cheap-movers-near-me-kroonstad", component: LocationPage },
  { path: "/cheap-movers-near-me-kuilsrivier", component: LocationPage },
  { path: "/cheap-movers-near-me-ladysmith", component: LocationPage },
  { path: "/cheap-movers-near-me-lebowakgomo", component: LocationPage },
  { path: "/cheap-movers-near-me-lichtenburg", component: LocationPage },
  { path: "/cheap-movers-near-me-mahikeng", component: LocationPage },
  { path: "/cheap-movers-near-me-malmesbury", component: LocationPage },
  { path: "/cheap-movers-near-me-middelburg", component: LocationPage },
  { path: "/cheap-movers-near-me-midrand", component: LocationPage },
  { path: "/cheap-movers-near-me-mmabatho", component: LocationPage },
  { path: "/cheap-movers-near-me-mossel-bay", component: LocationPage },
  { path: "/cheap-movers-near-me-musina", component: LocationPage },
  { path: "/cheap-movers-near-me-nelspruit", component: LocationPage },
  { path: "/cheap-movers-near-me-oudtshoorn", component: LocationPage },
  { path: "/cheap-movers-near-me-paarl", component: LocationPage },
  { path: "/cheap-movers-near-me-pietermaritzburg", component: LocationPage },
  { path: "/cheap-movers-near-me-polokwane", component: LocationPage },
  { path: "/cheap-movers-near-me-port-elizabeth", component: LocationPage },
  { path: "/cheap-movers-near-me-port-shepstone", component: LocationPage },
  { path: "/cheap-movers-near-me-potchefstroom", component: LocationPage },
  { path: "/cheap-movers-near-me-pretoria", component: LocationPage },
  { path: "/cheap-movers-near-me-queenstown", component: LocationPage },
  { path: "/cheap-movers-near-me-randburg", component: LocationPage },
  { path: "/cheap-movers-near-me-roodepoort", component: LocationPage },
  { path: "/cheap-movers-near-me-rustenburg", component: LocationPage },
  { path: "/cheap-movers-near-me-saldanha", component: LocationPage },
  { path: "/cheap-movers-near-me-sandton", component: LocationPage },
  { path: "/cheap-movers-near-me-soweto", component: LocationPage },
  { path: "/cheap-movers-near-me-springs", component: LocationPage },
  { path: "/cheap-movers-near-me-standerton", component: LocationPage },
  { path: "/cheap-movers-near-me-thohoyandou", component: LocationPage },
  { path: "/cheap-movers-near-me-tzaneen", component: LocationPage },
  { path: "/cheap-movers-near-me-uitenhage", component: LocationPage },
  { path: "/cheap-movers-near-me-ulundi", component: LocationPage },
  { path: "/cheap-movers-near-me-umtata", component: LocationPage },
  { path: "/cheap-movers-near-me-upington", component: LocationPage },
  { path: "/cheap-movers-near-me-vanderbijlpark", component: LocationPage },
  { path: "/cheap-movers-near-me-vereeniging", component: LocationPage },
  { path: "/cheap-movers-near-me-volksrust", component: LocationPage },
  { path: "/cheap-movers-near-me-vredenburg", component: LocationPage },
  { path: "/cheap-movers-near-me-vryburg", component: LocationPage },
  { path: "/cheap-movers-near-me-vryheid", component: LocationPage },
  { path: "/cheap-movers-near-me-welkom", component: LocationPage },
  { path: "/cheap-movers-near-me-worcester", component: LocationPage },
  { path: "/cheap-removals-alberton", component: LocationPage },
  { path: "/cheap-removals-aliwal-north", component: LocationPage },
  { path: "/cheap-removals-beaufort-west", component: LocationPage },
  { path: "/cheap-removals-benoni", component: LocationPage },
  { path: "/cheap-removals-bethal", component: LocationPage },
  { path: "/cheap-removals-bhisho", component: LocationPage },
  { path: "/cheap-removals-bloemfontein", component: LocationPage },
  { path: "/cheap-removals-brakpan", component: LocationPage },
  { path: "/cheap-removals-brits", component: LocationPage },
  { path: "/cheap-removals-cape-town", component: LocationPage },
  { path: "/cheap-removals-carletonville", component: LocationPage },
  { path: "/cheap-removals-centurion", component: LocationPage },
  { path: "/cheap-removals-cradock", component: LocationPage },
  { path: "/cheap-removals-de-aar", component: LocationPage },
  { path: "/cheap-removals-durban", component: LocationPage },
  { path: "/cheap-removals-east-london", component: LocationPage },
  { path: "/cheap-removals-ferndale", component: LocationPage },
  { path: "/cheap-removals-george", component: LocationPage },
  { path: "/cheap-removals-graaff-reinet", component: LocationPage },
  { path: "/cheap-removals-grahamstown", component: LocationPage },
  { path: "/cheap-removals-hermanus", component: LocationPage },
  { path: "/cheap-removals-jeffreys-bay", component: LocationPage },
  { path: "/cheap-removals-johannesburg", component: LocationPage },
  { path: "/cheap-removals-kimberley", component: LocationPage },
  { path: "/cheap-removals-klerksdorp", component: LocationPage },
  { path: "/cheap-removals-knysna", component: LocationPage },
  { path: "/cheap-removals-kroonstad", component: LocationPage },
  { path: "/cheap-removals-kuilsrivier", component: LocationPage },
  { path: "/cheap-removals-ladysmith", component: LocationPage },
  { path: "/cheap-removals-lebowakgomo", component: LocationPage },
  { path: "/cheap-removals-lichtenburg", component: LocationPage },
  { path: "/cheap-removals-mahikeng", component: LocationPage },
  { path: "/cheap-removals-malmesbury", component: LocationPage },
  { path: "/cheap-removals-middelburg", component: LocationPage },
  { path: "/cheap-removals-midrand", component: LocationPage },
  { path: "/cheap-removals-mmabatho", component: LocationPage },
  { path: "/cheap-removals-mossel-bay", component: LocationPage },
  { path: "/cheap-removals-musina", component: LocationPage },
  { path: "/cheap-removals-nelspruit", component: LocationPage },
  { path: "/cheap-removals-oudtshoorn", component: LocationPage },
  { path: "/cheap-removals-paarl", component: LocationPage },
  { path: "/cheap-removals-pietermaritzburg", component: LocationPage },
  { path: "/cheap-removals-polokwane", component: LocationPage },
  { path: "/cheap-removals-port-elizabeth", component: LocationPage },
  { path: "/cheap-removals-port-shepstone", component: LocationPage },
  { path: "/cheap-removals-potchefstroom", component: LocationPage },
  { path: "/cheap-removals-pretoria", component: LocationPage },
  { path: "/cheap-removals-queenstown", component: LocationPage },
  { path: "/cheap-removals-randburg", component: LocationPage },
  { path: "/cheap-removals-roodepoort", component: LocationPage },
  { path: "/cheap-removals-rustenburg", component: LocationPage },
  { path: "/cheap-removals-saldanha", component: LocationPage },
  { path: "/cheap-removals-sandton", component: LocationPage },
  { path: "/cheap-removals-soweto", component: LocationPage },
  { path: "/cheap-removals-springs", component: LocationPage },
  { path: "/cheap-removals-standerton", component: LocationPage },
  { path: "/cheap-removals-thohoyandou", component: LocationPage },
  { path: "/cheap-removals-tzaneen", component: LocationPage },
  { path: "/cheap-removals-uitenhage", component: LocationPage },
  { path: "/cheap-removals-ulundi", component: LocationPage },
  { path: "/cheap-removals-umtata", component: LocationPage },
  { path: "/cheap-removals-upington", component: LocationPage },
  { path: "/cheap-removals-vanderbijlpark", component: LocationPage },
  { path: "/cheap-removals-vereeniging", component: LocationPage },
  { path: "/cheap-removals-volksrust", component: LocationPage },
  { path: "/cheap-removals-vredenburg", component: LocationPage },
  { path: "/cheap-removals-vryburg", component: LocationPage },
  { path: "/cheap-removals-vryheid", component: LocationPage },
  { path: "/cheap-removals-welkom", component: LocationPage },
  { path: "/cheap-removals-worcester", component: LocationPage },
  { path: "/cheap-transportation-alberton", component: LocationPage },
  { path: "/cheap-transportation-aliwal-north", component: LocationPage },
  { path: "/cheap-transportation-beaufort-west", component: LocationPage },
  { path: "/cheap-transportation-benoni", component: LocationPage },
  { path: "/cheap-transportation-bethal", component: LocationPage },
  { path: "/cheap-transportation-bhisho", component: LocationPage },
  { path: "/cheap-transportation-bloemfontein", component: LocationPage },
  { path: "/cheap-transportation-brakpan", component: LocationPage },
  { path: "/cheap-transportation-brits", component: LocationPage },
  { path: "/cheap-transportation-cape-town", component: LocationPage },
  { path: "/cheap-transportation-carletonville", component: LocationPage },
  { path: "/cheap-transportation-centurion", component: LocationPage },
  { path: "/cheap-transportation-cradock", component: LocationPage },
  { path: "/cheap-transportation-de-aar", component: LocationPage },
  { path: "/cheap-transportation-durban", component: LocationPage },
  { path: "/cheap-transportation-east-london", component: LocationPage },
  { path: "/cheap-transportation-ferndale", component: LocationPage },
  { path: "/cheap-transportation-george", component: LocationPage },
  { path: "/cheap-transportation-graaff-reinet", component: LocationPage },
  { path: "/cheap-transportation-grahamstown", component: LocationPage },
  { path: "/cheap-transportation-hermanus", component: LocationPage },
  { path: "/cheap-transportation-jeffreys-bay", component: LocationPage },
  { path: "/cheap-transportation-johannesburg", component: LocationPage },
  { path: "/cheap-transportation-kimberley", component: LocationPage },
  { path: "/cheap-transportation-klerksdorp", component: LocationPage },
  { path: "/cheap-transportation-knysna", component: LocationPage },
  { path: "/cheap-transportation-kroonstad", component: LocationPage },
  { path: "/cheap-transportation-kuilsrivier", component: LocationPage },
  { path: "/cheap-transportation-ladysmith", component: LocationPage },
  { path: "/cheap-transportation-lebowakgomo", component: LocationPage },
  { path: "/cheap-transportation-lichtenburg", component: LocationPage },
  { path: "/cheap-transportation-mahikeng", component: LocationPage },
  { path: "/cheap-transportation-malmesbury", component: LocationPage },
  { path: "/cheap-transportation-middelburg", component: LocationPage },
  { path: "/cheap-transportation-midrand", component: LocationPage },
  { path: "/cheap-transportation-mmabatho", component: LocationPage },
  { path: "/cheap-transportation-mossel-bay", component: LocationPage },
  { path: "/cheap-transportation-musina", component: LocationPage },
  { path: "/cheap-transportation-nelspruit", component: LocationPage },
  { path: "/cheap-transportation-oudtshoorn", component: LocationPage },
  { path: "/cheap-transportation-paarl", component: LocationPage },
  { path: "/cheap-transportation-pietermaritzburg", component: LocationPage },
  { path: "/cheap-transportation-polokwane", component: LocationPage },
  { path: "/cheap-transportation-port-elizabeth", component: LocationPage },
  { path: "/cheap-transportation-port-shepstone", component: LocationPage },
  { path: "/cheap-transportation-potchefstroom", component: LocationPage },
  { path: "/cheap-transportation-pretoria", component: LocationPage },
  { path: "/cheap-transportation-queenstown", component: LocationPage },
  { path: "/cheap-transportation-randburg", component: LocationPage },
  { path: "/cheap-transportation-roodepoort", component: LocationPage },
  { path: "/cheap-transportation-rustenburg", component: LocationPage },
  { path: "/cheap-transportation-saldanha", component: LocationPage },
  { path: "/cheap-transportation-sandton", component: LocationPage },
  { path: "/cheap-transportation-soweto", component: LocationPage },
  { path: "/cheap-transportation-springs", component: LocationPage },
  { path: "/cheap-transportation-standerton", component: LocationPage },
  { path: "/cheap-transportation-thohoyandou", component: LocationPage },
  { path: "/cheap-transportation-tzaneen", component: LocationPage },
  { path: "/cheap-transportation-uitenhage", component: LocationPage },
  { path: "/cheap-transportation-ulundi", component: LocationPage },
  { path: "/cheap-transportation-umtata", component: LocationPage },
  { path: "/cheap-transportation-upington", component: LocationPage },
  { path: "/cheap-transportation-vanderbijlpark", component: LocationPage },
  { path: "/cheap-transportation-vereeniging", component: LocationPage },
  { path: "/cheap-transportation-volksrust", component: LocationPage },
  { path: "/cheap-transportation-vredenburg", component: LocationPage },
  { path: "/cheap-transportation-vryburg", component: LocationPage },
  { path: "/cheap-transportation-vryheid", component: LocationPage },
  { path: "/cheap-transportation-welkom", component: LocationPage },
  { path: "/cheap-transportation-worcester", component: LocationPage },
  { path: "/courier--alberton", component: LocationPage },
  { path: "/courier--aliwal-north", component: LocationPage },
  { path: "/courier--beaufort-west", component: LocationPage },
  { path: "/courier--benoni", component: LocationPage },
  { path: "/courier--bethal", component: LocationPage },
  { path: "/courier--bhisho", component: LocationPage },
  { path: "/courier--bloemfontein", component: LocationPage },
  { path: "/courier--brakpan", component: LocationPage },
  { path: "/courier--brits", component: LocationPage },
  { path: "/courier--cape-town", component: LocationPage },
  { path: "/courier--carletonville", component: LocationPage },
  { path: "/courier--centurion", component: LocationPage },
  { path: "/courier--cradock", component: LocationPage },
  { path: "/courier--de-aar", component: LocationPage },
  { path: "/courier--durban", component: LocationPage },
  { path: "/courier--east-london", component: LocationPage },
  { path: "/courier--ferndale", component: LocationPage },
  { path: "/courier--george", component: LocationPage },
  { path: "/courier--graaff-reinet", component: LocationPage },
  { path: "/courier--grahamstown", component: LocationPage },
  { path: "/courier--hermanus", component: LocationPage },
  { path: "/courier--jeffreys-bay", component: LocationPage },
  { path: "/courier--johannesburg", component: LocationPage },
  { path: "/courier--kimberley", component: LocationPage },
  { path: "/courier--klerksdorp", component: LocationPage },
  { path: "/courier--knysna", component: LocationPage },
  { path: "/courier--kroonstad", component: LocationPage },
  { path: "/courier--kuilsrivier", component: LocationPage },
  { path: "/courier--ladysmith", component: LocationPage },
  { path: "/courier--lebowakgomo", component: LocationPage },
  { path: "/courier--lichtenburg", component: LocationPage },
  { path: "/courier--mahikeng", component: LocationPage },
  { path: "/courier--malmesbury", component: LocationPage },
  { path: "/courier--middelburg", component: LocationPage },
  { path: "/courier--midrand", component: LocationPage },
  { path: "/courier--mmabatho", component: LocationPage },
  { path: "/courier--mossel-bay", component: LocationPage },
  { path: "/courier--musina", component: LocationPage },
  { path: "/courier--nelspruit", component: LocationPage },
  { path: "/courier--oudtshoorn", component: LocationPage },
  { path: "/courier--paarl", component: LocationPage },
  { path: "/courier--pietermaritzburg", component: LocationPage },
  { path: "/courier--polokwane", component: LocationPage },
  { path: "/courier--port-elizabeth", component: LocationPage },
  { path: "/courier--port-shepstone", component: LocationPage },
  { path: "/courier--potchefstroom", component: LocationPage },
  { path: "/courier--pretoria", component: LocationPage },
  { path: "/courier--queenstown", component: LocationPage },
  { path: "/courier--randburg", component: LocationPage },
  { path: "/courier--roodepoort", component: LocationPage },
  { path: "/courier--rustenburg", component: LocationPage },
  { path: "/courier--saldanha", component: LocationPage },
  { path: "/courier--sandton", component: LocationPage },
  { path: "/courier--soweto", component: LocationPage },
  { path: "/courier--springs", component: LocationPage },
  { path: "/courier--standerton", component: LocationPage },
  { path: "/courier--thohoyandou", component: LocationPage },
  { path: "/courier--tzaneen", component: LocationPage },
  { path: "/courier--uitenhage", component: LocationPage },
  { path: "/courier--ulundi", component: LocationPage },
  { path: "/courier--umtata", component: LocationPage },
  { path: "/courier--upington", component: LocationPage },
  { path: "/courier--vanderbijlpark", component: LocationPage },
  { path: "/courier--vereeniging", component: LocationPage },
  { path: "/courier--volksrust", component: LocationPage },
  { path: "/courier--vredenburg", component: LocationPage },
  { path: "/courier--vryburg", component: LocationPage },
  { path: "/courier--vryheid", component: LocationPage },
  { path: "/courier--welkom", component: LocationPage },
  { path: "/courier--worcester", component: LocationPage },
  { path: "/delivery-alberton", component: LocationPage },
  { path: "/delivery-aliwal-north", component: LocationPage },
  { path: "/delivery-beaufort-west", component: LocationPage },
  { path: "/delivery-benoni", component: LocationPage },
  { path: "/delivery-bethal", component: LocationPage },
  { path: "/delivery-bhisho", component: LocationPage },
  { path: "/delivery-bloemfontein", component: LocationPage },
  { path: "/delivery-brakpan", component: LocationPage },
  { path: "/delivery-brits", component: LocationPage },
  { path: "/delivery-cape-town", component: LocationPage },
  { path: "/delivery-carletonville", component: LocationPage },
  { path: "/delivery-centurion", component: LocationPage },
  { path: "/delivery-cradock", component: LocationPage },
  { path: "/delivery-de-aar", component: LocationPage },
  { path: "/delivery-durban", component: LocationPage },
  { path: "/delivery-east-london", component: LocationPage },
  { path: "/delivery-ferndale", component: LocationPage },
  { path: "/delivery-george", component: LocationPage },
  { path: "/delivery-graaff-reinet", component: LocationPage },
  { path: "/delivery-grahamstown", component: LocationPage },
  { path: "/delivery-hermanus", component: LocationPage },
  { path: "/delivery-jeffreys-bay", component: LocationPage },
  { path: "/delivery-johannesburg", component: LocationPage },
  { path: "/delivery-kimberley", component: LocationPage },
  { path: "/delivery-klerksdorp", component: LocationPage },
  { path: "/delivery-knysna", component: LocationPage },
  { path: "/delivery-kroonstad", component: LocationPage },
  { path: "/delivery-kuilsrivier", component: LocationPage },
  { path: "/delivery-ladysmith", component: LocationPage },
  { path: "/delivery-lebowakgomo", component: LocationPage },
  { path: "/delivery-lichtenburg", component: LocationPage },
  { path: "/delivery-mahikeng", component: LocationPage },
  { path: "/delivery-malmesbury", component: LocationPage },
  { path: "/delivery-middelburg", component: LocationPage },
  { path: "/delivery-midrand", component: LocationPage },
  { path: "/delivery-mmabatho", component: LocationPage },
  { path: "/delivery-mossel-bay", component: LocationPage },
  { path: "/delivery-musina", component: LocationPage },
  { path: "/delivery-nelspruit", component: LocationPage },
  { path: "/delivery-oudtshoorn", component: LocationPage },
  { path: "/delivery-paarl", component: LocationPage },
  { path: "/delivery-pietermaritzburg", component: LocationPage },
  { path: "/delivery-polokwane", component: LocationPage },
  { path: "/delivery-port-elizabeth", component: LocationPage },
  { path: "/delivery-port-shepstone", component: LocationPage },
  { path: "/delivery-potchefstroom", component: LocationPage },
  { path: "/delivery-pretoria", component: LocationPage },
  { path: "/delivery-queenstown", component: LocationPage },
  { path: "/delivery-randburg", component: LocationPage },
  { path: "/delivery-roodepoort", component: LocationPage },
  { path: "/delivery-rustenburg", component: LocationPage },
  { path: "/delivery-saldanha", component: LocationPage },
  { path: "/delivery-sandton", component: LocationPage },
  { path: "/delivery-soweto", component: LocationPage },
  { path: "/delivery-springs", component: LocationPage },
  { path: "/delivery-standerton", component: LocationPage },
  { path: "/delivery-thohoyandou", component: LocationPage },
  { path: "/delivery-tzaneen", component: LocationPage },
  { path: "/delivery-uitenhage", component: LocationPage },
  { path: "/delivery-ulundi", component: LocationPage },
  { path: "/delivery-umtata", component: LocationPage },
  { path: "/delivery-upington", component: LocationPage },
  { path: "/delivery-vanderbijlpark", component: LocationPage },
  { path: "/delivery-vereeniging", component: LocationPage },
  { path: "/delivery-volksrust", component: LocationPage },
  { path: "/delivery-vredenburg", component: LocationPage },
  { path: "/delivery-vryburg", component: LocationPage },
  { path: "/delivery-vryheid", component: LocationPage },
  { path: "/delivery-welkom", component: LocationPage },
  { path: "/delivery-worcester", component: LocationPage },
  { path: "/door-to-door-delivery-alberton", component: LocationPage },
  { path: "/door-to-door-delivery-aliwal-north", component: LocationPage },
  { path: "/door-to-door-delivery-beaufort-west", component: LocationPage },
  { path: "/door-to-door-delivery-benoni", component: LocationPage },
  { path: "/door-to-door-delivery-bethal", component: LocationPage },
  { path: "/door-to-door-delivery-bhisho", component: LocationPage },
  { path: "/door-to-door-delivery-bloemfontein", component: LocationPage },
  { path: "/door-to-door-delivery-brakpan", component: LocationPage },
  { path: "/door-to-door-delivery-brits", component: LocationPage },
  { path: "/door-to-door-delivery-cape-town", component: LocationPage },
  { path: "/door-to-door-delivery-carletonville", component: LocationPage },
  { path: "/door-to-door-delivery-centurion", component: LocationPage },
  { path: "/door-to-door-delivery-cradock", component: LocationPage },
  { path: "/door-to-door-delivery-de-aar", component: LocationPage },
  { path: "/door-to-door-delivery-durban", component: LocationPage },
  { path: "/door-to-door-delivery-east-london", component: LocationPage },
  { path: "/door-to-door-delivery-ferndale", component: LocationPage },
  { path: "/door-to-door-delivery-george", component: LocationPage },
  { path: "/door-to-door-delivery-graaff-reinet", component: LocationPage },
  { path: "/door-to-door-delivery-grahamstown", component: LocationPage },
  { path: "/door-to-door-delivery-hermanus", component: LocationPage },
  { path: "/door-to-door-delivery-jeffreys-bay", component: LocationPage },
  { path: "/door-to-door-delivery-johannesburg", component: LocationPage },
  { path: "/door-to-door-delivery-kimberley", component: LocationPage },
  { path: "/door-to-door-delivery-klerksdorp", component: LocationPage },
  { path: "/door-to-door-delivery-knysna", component: LocationPage },
  { path: "/door-to-door-delivery-kroonstad", component: LocationPage },
  { path: "/door-to-door-delivery-kuilsrivier", component: LocationPage },
  { path: "/door-to-door-delivery-ladysmith", component: LocationPage },
  { path: "/door-to-door-delivery-lebowakgomo", component: LocationPage },
  { path: "/door-to-door-delivery-lichtenburg", component: LocationPage },
  { path: "/door-to-door-delivery-mahikeng", component: LocationPage },
  { path: "/door-to-door-delivery-malmesbury", component: LocationPage },
  { path: "/door-to-door-delivery-middelburg", component: LocationPage },
  { path: "/door-to-door-delivery-midrand", component: LocationPage },
  { path: "/door-to-door-delivery-mmabatho", component: LocationPage },
  { path: "/door-to-door-delivery-mossel-bay", component: LocationPage },
  { path: "/door-to-door-delivery-musina", component: LocationPage },
  { path: "/door-to-door-delivery-nelspruit", component: LocationPage },
  { path: "/door-to-door-delivery-oudtshoorn", component: LocationPage },
  { path: "/door-to-door-delivery-paarl", component: LocationPage },
  { path: "/door-to-door-delivery-pietermaritzburg", component: LocationPage },
  { path: "/door-to-door-delivery-polokwane", component: LocationPage },
  { path: "/door-to-door-delivery-port-elizabeth", component: LocationPage },
  { path: "/door-to-door-delivery-port-shepstone", component: LocationPage },
  { path: "/door-to-door-delivery-potchefstroom", component: LocationPage },
  { path: "/door-to-door-delivery-pretoria", component: LocationPage },
  { path: "/door-to-door-delivery-queenstown", component: LocationPage },
  { path: "/door-to-door-delivery-randburg", component: LocationPage },
  { path: "/door-to-door-delivery-roodepoort", component: LocationPage },
  { path: "/door-to-door-delivery-rustenburg", component: LocationPage },
  { path: "/door-to-door-delivery-saldanha", component: LocationPage },
  { path: "/door-to-door-delivery-sandton", component: LocationPage },
  { path: "/door-to-door-delivery-soweto", component: LocationPage },
  { path: "/door-to-door-delivery-springs", component: LocationPage },
  { path: "/door-to-door-delivery-standerton", component: LocationPage },
  { path: "/door-to-door-delivery-thohoyandou", component: LocationPage },
  { path: "/door-to-door-delivery-tzaneen", component: LocationPage },
  { path: "/door-to-door-delivery-uitenhage", component: LocationPage },
  { path: "/door-to-door-delivery-ulundi", component: LocationPage },
  { path: "/door-to-door-delivery-umtata", component: LocationPage },
  { path: "/door-to-door-delivery-upington", component: LocationPage },
  { path: "/door-to-door-delivery-vanderbijlpark", component: LocationPage },
  { path: "/door-to-door-delivery-vereeniging", component: LocationPage },
  { path: "/door-to-door-delivery-volksrust", component: LocationPage },
  { path: "/door-to-door-delivery-vredenburg", component: LocationPage },
  { path: "/door-to-door-delivery-vryburg", component: LocationPage },
  { path: "/door-to-door-delivery-vryheid", component: LocationPage },
  { path: "/door-to-door-delivery-welkom", component: LocationPage },
  { path: "/door-to-door-delivery-worcester", component: LocationPage },
  { path: "/equipment-transport-alberton", component: LocationPage },
  { path: "/equipment-transport-aliwal-north", component: LocationPage },
  { path: "/equipment-transport-beaufort-west", component: LocationPage },
  { path: "/equipment-transport-benoni", component: LocationPage },
  { path: "/equipment-transport-bethal", component: LocationPage },
  { path: "/equipment-transport-bhisho", component: LocationPage },
  { path: "/equipment-transport-bloemfontein", component: LocationPage },
  { path: "/equipment-transport-brakpan", component: LocationPage },
  { path: "/equipment-transport-brits", component: LocationPage },
  { path: "/equipment-transport-cape-town", component: LocationPage },
  { path: "/equipment-transport-carletonville", component: LocationPage },
  { path: "/equipment-transport-centurion", component: LocationPage },
  { path: "/equipment-transport-cradock", component: LocationPage },
  { path: "/equipment-transport-de-aar", component: LocationPage },
  { path: "/equipment-transport-durban", component: LocationPage },
  { path: "/equipment-transport-east-london", component: LocationPage },
  { path: "/equipment-transport-ferndale", component: LocationPage },
  { path: "/equipment-transport-george", component: LocationPage },
  { path: "/equipment-transport-graaff-reinet", component: LocationPage },
  { path: "/equipment-transport-grahamstown", component: LocationPage },
  { path: "/equipment-transport-hermanus", component: LocationPage },
  { path: "/equipment-transport-jeffreys-bay", component: LocationPage },
  { path: "/equipment-transport-johannesburg", component: LocationPage },
  { path: "/equipment-transport-kimberley", component: LocationPage },
  { path: "/equipment-transport-klerksdorp", component: LocationPage },
  { path: "/equipment-transport-knysna", component: LocationPage },
  { path: "/equipment-transport-kroonstad", component: LocationPage },
  { path: "/equipment-transport-kuilsrivier", component: LocationPage },
  { path: "/equipment-transport-ladysmith", component: LocationPage },
  { path: "/equipment-transport-lebowakgomo", component: LocationPage },
  { path: "/equipment-transport-lichtenburg", component: LocationPage },
  { path: "/equipment-transport-mahikeng", component: LocationPage },
  { path: "/equipment-transport-malmesbury", component: LocationPage },
  { path: "/equipment-transport-middelburg", component: LocationPage },
  { path: "/equipment-transport-midrand", component: LocationPage },
  { path: "/equipment-transport-mmabatho", component: LocationPage },
  { path: "/equipment-transport-mossel-bay", component: LocationPage },
  { path: "/equipment-transport-musina", component: LocationPage },
  { path: "/equipment-transport-nelspruit", component: LocationPage },
  { path: "/equipment-transport-oudtshoorn", component: LocationPage },
  { path: "/equipment-transport-paarl", component: LocationPage },
  { path: "/equipment-transport-pietermaritzburg", component: LocationPage },
  { path: "/equipment-transport-polokwane", component: LocationPage },
  { path: "/equipment-transport-port-elizabeth", component: LocationPage },
  { path: "/equipment-transport-port-shepstone", component: LocationPage },
  { path: "/equipment-transport-potchefstroom", component: LocationPage },
  { path: "/equipment-transport-pretoria", component: LocationPage },
  { path: "/equipment-transport-queenstown", component: LocationPage },
  { path: "/equipment-transport-randburg", component: LocationPage },
  { path: "/equipment-transport-roodepoort", component: LocationPage },
  { path: "/equipment-transport-rustenburg", component: LocationPage },
  { path: "/equipment-transport-saldanha", component: LocationPage },
  { path: "/equipment-transport-sandton", component: LocationPage },
  { path: "/equipment-transport-soweto", component: LocationPage },
  { path: "/equipment-transport-springs", component: LocationPage },
  { path: "/equipment-transport-standerton", component: LocationPage },
  { path: "/equipment-transport-thohoyandou", component: LocationPage },
  { path: "/equipment-transport-tzaneen", component: LocationPage },
  { path: "/equipment-transport-uitenhage", component: LocationPage },
  { path: "/equipment-transport-ulundi", component: LocationPage },
  { path: "/equipment-transport-umtata", component: LocationPage },
  { path: "/equipment-transport-upington", component: LocationPage },
  { path: "/equipment-transport-vanderbijlpark", component: LocationPage },
  { path: "/equipment-transport-vereeniging", component: LocationPage },
  { path: "/equipment-transport-volksrust", component: LocationPage },
  { path: "/equipment-transport-vredenburg", component: LocationPage },
  { path: "/equipment-transport-vryburg", component: LocationPage },
  { path: "/equipment-transport-vryheid", component: LocationPage },
  { path: "/equipment-transport-welkom", component: LocationPage },
  { path: "/equipment-transport-worcester", component: LocationPage },
  { path: "/express-delivery-alberton", component: LocationPage },
  { path: "/express-delivery-aliwal-north", component: LocationPage },
  { path: "/express-delivery-beaufort-west", component: LocationPage },
  { path: "/express-delivery-benoni", component: LocationPage },
  { path: "/express-delivery-bethal", component: LocationPage },
  { path: "/express-delivery-bhisho", component: LocationPage },
  { path: "/express-delivery-bloemfontein", component: LocationPage },
  { path: "/express-delivery-brakpan", component: LocationPage },
  { path: "/express-delivery-brits", component: LocationPage },
  { path: "/express-delivery-cape-town", component: LocationPage },
  { path: "/express-delivery-carletonville", component: LocationPage },
  { path: "/express-delivery-centurion", component: LocationPage },
  { path: "/express-delivery-cradock", component: LocationPage },
  { path: "/express-delivery-de-aar", component: LocationPage },
  { path: "/express-delivery-durban", component: LocationPage },
  { path: "/express-delivery-east-london", component: LocationPage },
  { path: "/express-delivery-ferndale", component: LocationPage },
  { path: "/express-delivery-george", component: LocationPage },
  { path: "/express-delivery-graaff-reinet", component: LocationPage },
  { path: "/express-delivery-grahamstown", component: LocationPage },
  { path: "/express-delivery-hermanus", component: LocationPage },
  { path: "/express-delivery-jeffreys-bay", component: LocationPage },
  { path: "/express-delivery-johannesburg", component: LocationPage },
  { path: "/express-delivery-kimberley", component: LocationPage },
  { path: "/express-delivery-klerksdorp", component: LocationPage },
  { path: "/express-delivery-knysna", component: LocationPage },
  { path: "/express-delivery-kroonstad", component: LocationPage },
  { path: "/express-delivery-kuilsrivier", component: LocationPage },
  { path: "/express-delivery-ladysmith", component: LocationPage },
  { path: "/express-delivery-lebowakgomo", component: LocationPage },
  { path: "/express-delivery-lichtenburg", component: LocationPage },
  { path: "/express-delivery-mahikeng", component: LocationPage },
  { path: "/express-delivery-malmesbury", component: LocationPage },
  { path: "/express-delivery-middelburg", component: LocationPage },
  { path: "/express-delivery-midrand", component: LocationPage },
  { path: "/express-delivery-mmabatho", component: LocationPage },
  { path: "/express-delivery-mossel-bay", component: LocationPage },
  { path: "/express-delivery-musina", component: LocationPage },
  { path: "/express-delivery-nelspruit", component: LocationPage },
  { path: "/express-delivery-oudtshoorn", component: LocationPage },
  { path: "/express-delivery-paarl", component: LocationPage },
  { path: "/express-delivery-pietermaritzburg", component: LocationPage },
  { path: "/express-delivery-polokwane", component: LocationPage },
  { path: "/express-delivery-port-elizabeth", component: LocationPage },
  { path: "/express-delivery-port-shepstone", component: LocationPage },
  { path: "/express-delivery-potchefstroom", component: LocationPage },
  { path: "/express-delivery-pretoria", component: LocationPage },
  { path: "/express-delivery-queenstown", component: LocationPage },
  { path: "/express-delivery-randburg", component: LocationPage },
  { path: "/express-delivery-roodepoort", component: LocationPage },
  { path: "/express-delivery-rustenburg", component: LocationPage },
  { path: "/express-delivery-saldanha", component: LocationPage },
  { path: "/express-delivery-sandton", component: LocationPage },
  { path: "/express-delivery-soweto", component: LocationPage },
  { path: "/express-delivery-springs", component: LocationPage },
  { path: "/express-delivery-standerton", component: LocationPage },
  { path: "/express-delivery-thohoyandou", component: LocationPage },
  { path: "/express-delivery-tzaneen", component: LocationPage },
  { path: "/express-delivery-uitenhage", component: LocationPage },
  { path: "/express-delivery-ulundi", component: LocationPage },
  { path: "/express-delivery-umtata", component: LocationPage },
  { path: "/express-delivery-upington", component: LocationPage },
  { path: "/express-delivery-vanderbijlpark", component: LocationPage },
  { path: "/express-delivery-vereeniging", component: LocationPage },
  { path: "/express-delivery-volksrust", component: LocationPage },
  { path: "/express-delivery-vredenburg", component: LocationPage },
  { path: "/express-delivery-vryburg", component: LocationPage },
  { path: "/express-delivery-vryheid", component: LocationPage },
  { path: "/express-delivery-welkom", component: LocationPage },
  { path: "/express-delivery-worcester", component: LocationPage },
  { path: "/flexible-delivery-options-alberton", component: LocationPage },
  { path: "/flexible-delivery-options-aliwal-north", component: LocationPage },
  { path: "/flexible-delivery-options-beaufort-west", component: LocationPage },
  { path: "/flexible-delivery-options-benoni", component: LocationPage },
  { path: "/flexible-delivery-options-bethal", component: LocationPage },
  { path: "/flexible-delivery-options-bhisho", component: LocationPage },
  { path: "/flexible-delivery-options-bloemfontein", component: LocationPage },
  { path: "/flexible-delivery-options-brakpan", component: LocationPage },
  { path: "/flexible-delivery-options-brits", component: LocationPage },
  { path: "/flexible-delivery-options-cape-town", component: LocationPage },
  { path: "/flexible-delivery-options-carletonville", component: LocationPage },
  { path: "/flexible-delivery-options-centurion", component: LocationPage },
  { path: "/flexible-delivery-options-cradock", component: LocationPage },
  { path: "/flexible-delivery-options-de-aar", component: LocationPage },
  { path: "/flexible-delivery-options-durban", component: LocationPage },
  { path: "/flexible-delivery-options-east-london", component: LocationPage },
  { path: "/flexible-delivery-options-ferndale", component: LocationPage },
  { path: "/flexible-delivery-options-george", component: LocationPage },
  { path: "/flexible-delivery-options-graaff-reinet", component: LocationPage },
  { path: "/flexible-delivery-options-grahamstown", component: LocationPage },
  { path: "/flexible-delivery-options-hermanus", component: LocationPage },
  { path: "/flexible-delivery-options-jeffreys-bay", component: LocationPage },
  { path: "/flexible-delivery-options-johannesburg", component: LocationPage },
  { path: "/flexible-delivery-options-kimberley", component: LocationPage },
  { path: "/flexible-delivery-options-klerksdorp", component: LocationPage },
  { path: "/flexible-delivery-options-knysna", component: LocationPage },
  { path: "/flexible-delivery-options-kroonstad", component: LocationPage },
  { path: "/flexible-delivery-options-kuilsrivier", component: LocationPage },
  { path: "/flexible-delivery-options-ladysmith", component: LocationPage },
  { path: "/flexible-delivery-options-lebowakgomo", component: LocationPage },
  { path: "/flexible-delivery-options-lichtenburg", component: LocationPage },
  { path: "/flexible-delivery-options-mahikeng", component: LocationPage },
  { path: "/flexible-delivery-options-malmesbury", component: LocationPage },
  { path: "/flexible-delivery-options-middelburg", component: LocationPage },
  { path: "/flexible-delivery-options-midrand", component: LocationPage },
  { path: "/flexible-delivery-options-mmabatho", component: LocationPage },
  { path: "/flexible-delivery-options-mossel-bay", component: LocationPage },
  { path: "/flexible-delivery-options-musina", component: LocationPage },
  { path: "/flexible-delivery-options-nelspruit", component: LocationPage },
  { path: "/flexible-delivery-options-oudtshoorn", component: LocationPage },
  { path: "/flexible-delivery-options-paarl", component: LocationPage },
  { path: "/flexible-delivery-options-pietermaritzburg", component: LocationPage },
  { path: "/flexible-delivery-options-polokwane", component: LocationPage },
  { path: "/flexible-delivery-options-port-elizabeth", component: LocationPage },
  { path: "/flexible-delivery-options-port-shepstone", component: LocationPage },
  { path: "/flexible-delivery-options-potchefstroom", component: LocationPage },
  { path: "/flexible-delivery-options-pretoria", component: LocationPage },
  { path: "/flexible-delivery-options-queenstown", component: LocationPage },
  { path: "/flexible-delivery-options-randburg", component: LocationPage },
  { path: "/flexible-delivery-options-roodepoort", component: LocationPage },
  { path: "/flexible-delivery-options-rustenburg", component: LocationPage },
  { path: "/flexible-delivery-options-saldanha", component: LocationPage },
  { path: "/flexible-delivery-options-sandton", component: LocationPage },
  { path: "/flexible-delivery-options-soweto", component: LocationPage },
  { path: "/flexible-delivery-options-springs", component: LocationPage },
  { path: "/flexible-delivery-options-standerton", component: LocationPage },
  { path: "/flexible-delivery-options-thohoyandou", component: LocationPage },
  { path: "/flexible-delivery-options-tzaneen", component: LocationPage },
  { path: "/flexible-delivery-options-uitenhage", component: LocationPage },
  { path: "/flexible-delivery-options-ulundi", component: LocationPage },
  { path: "/flexible-delivery-options-umtata", component: LocationPage },
  { path: "/flexible-delivery-options-upington", component: LocationPage },
  { path: "/flexible-delivery-options-vanderbijlpark", component: LocationPage },
  { path: "/flexible-delivery-options-vereeniging", component: LocationPage },
  { path: "/flexible-delivery-options-volksrust", component: LocationPage },
  { path: "/flexible-delivery-options-vredenburg", component: LocationPage },
  { path: "/flexible-delivery-options-vryburg", component: LocationPage },
  { path: "/flexible-delivery-options-vryheid", component: LocationPage },
  { path: "/flexible-delivery-options-welkom", component: LocationPage },
  { path: "/flexible-delivery-options-worcester", component: LocationPage },
  { path: "/fragile-delivery-alberton", component: LocationPage },
  { path: "/fragile-delivery-aliwal-north", component: LocationPage },
  { path: "/fragile-delivery-beaufort-west", component: LocationPage },
  { path: "/fragile-delivery-benoni", component: LocationPage },
  { path: "/fragile-delivery-bethal", component: LocationPage },
  { path: "/fragile-delivery-bhisho", component: LocationPage },
  { path: "/fragile-delivery-bloemfontein", component: LocationPage },
  { path: "/fragile-delivery-brakpan", component: LocationPage },
  { path: "/fragile-delivery-brits", component: LocationPage },
  { path: "/fragile-delivery-cape-town", component: LocationPage },
  { path: "/fragile-delivery-carletonville", component: LocationPage },
  { path: "/fragile-delivery-centurion", component: LocationPage },
  { path: "/fragile-delivery-cradock", component: LocationPage },
  { path: "/fragile-delivery-de-aar", component: LocationPage },
  { path: "/fragile-delivery-durban", component: LocationPage },
  { path: "/fragile-delivery-east-london", component: LocationPage },
  { path: "/fragile-delivery-ferndale", component: LocationPage },
  { path: "/fragile-delivery-george", component: LocationPage },
  { path: "/fragile-delivery-graaff-reinet", component: LocationPage },
  { path: "/fragile-delivery-grahamstown", component: LocationPage },
  { path: "/fragile-delivery-hermanus", component: LocationPage },
  { path: "/fragile-delivery-jeffreys-bay", component: LocationPage },
  { path: "/fragile-delivery-johannesburg", component: LocationPage },
  { path: "/fragile-delivery-kimberley", component: LocationPage },
  { path: "/fragile-delivery-klerksdorp", component: LocationPage },
  { path: "/fragile-delivery-knysna", component: LocationPage },
  { path: "/fragile-delivery-kroonstad", component: LocationPage },
  { path: "/fragile-delivery-kuilsrivier", component: LocationPage },
  { path: "/fragile-delivery-ladysmith", component: LocationPage },
  { path: "/fragile-delivery-lebowakgomo", component: LocationPage },
  { path: "/fragile-delivery-lichtenburg", component: LocationPage },
  { path: "/fragile-delivery-mahikeng", component: LocationPage },
  { path: "/fragile-delivery-malmesbury", component: LocationPage },
  { path: "/fragile-delivery-middelburg", component: LocationPage },
  { path: "/fragile-delivery-midrand", component: LocationPage },
  { path: "/fragile-delivery-mmabatho", component: LocationPage },
  { path: "/fragile-delivery-mossel-bay", component: LocationPage },
  { path: "/fragile-delivery-musina", component: LocationPage },
  { path: "/fragile-delivery-nelspruit", component: LocationPage },
  { path: "/fragile-delivery-oudtshoorn", component: LocationPage },
  { path: "/fragile-delivery-paarl", component: LocationPage },
  { path: "/fragile-delivery-pietermaritzburg", component: LocationPage },
  { path: "/fragile-delivery-polokwane", component: LocationPage },
  { path: "/fragile-delivery-port-elizabeth", component: LocationPage },
  { path: "/fragile-delivery-port-shepstone", component: LocationPage },
  { path: "/fragile-delivery-potchefstroom", component: LocationPage },
  { path: "/fragile-delivery-pretoria", component: LocationPage },
  { path: "/fragile-delivery-queenstown", component: LocationPage },
  { path: "/fragile-delivery-randburg", component: LocationPage },
  { path: "/fragile-delivery-roodepoort", component: LocationPage },
  { path: "/fragile-delivery-rustenburg", component: LocationPage },
  { path: "/fragile-delivery-saldanha", component: LocationPage },
  { path: "/fragile-delivery-sandton", component: LocationPage },
  { path: "/fragile-delivery-soweto", component: LocationPage },
  { path: "/fragile-delivery-springs", component: LocationPage },
  { path: "/fragile-delivery-standerton", component: LocationPage },
  { path: "/fragile-delivery-thohoyandou", component: LocationPage },
  { path: "/fragile-delivery-tzaneen", component: LocationPage },
  { path: "/fragile-delivery-uitenhage", component: LocationPage },
  { path: "/fragile-delivery-ulundi", component: LocationPage },
  { path: "/fragile-delivery-umtata", component: LocationPage },
  { path: "/fragile-delivery-upington", component: LocationPage },
  { path: "/fragile-delivery-vanderbijlpark", component: LocationPage },
  { path: "/fragile-delivery-vereeniging", component: LocationPage },
  { path: "/fragile-delivery-volksrust", component: LocationPage },
  { path: "/fragile-delivery-vredenburg", component: LocationPage },
  { path: "/fragile-delivery-vryburg", component: LocationPage },
  { path: "/fragile-delivery-vryheid", component: LocationPage },
  { path: "/fragile-delivery-welkom", component: LocationPage },
  { path: "/fragile-delivery-worcester", component: LocationPage },
  { path: "/freight-delivery-alberton", component: LocationPage },
  { path: "/freight-delivery-aliwal-north", component: LocationPage },
  { path: "/freight-delivery-beaufort-west", component: LocationPage },
  { path: "/freight-delivery-benoni", component: LocationPage },
  { path: "/freight-delivery-bethal", component: LocationPage },
  { path: "/freight-delivery-bhisho", component: LocationPage },
  { path: "/freight-delivery-bloemfontein", component: LocationPage },
  { path: "/freight-delivery-brakpan", component: LocationPage },
  { path: "/freight-delivery-brits", component: LocationPage },
  { path: "/freight-delivery-cape-town", component: LocationPage },
  { path: "/freight-delivery-carletonville", component: LocationPage },
  { path: "/freight-delivery-centurion", component: LocationPage },
  { path: "/freight-delivery-cradock", component: LocationPage },
  { path: "/freight-delivery-de-aar", component: LocationPage },
  { path: "/freight-delivery-durban", component: LocationPage },
  { path: "/freight-delivery-east-london", component: LocationPage },
  { path: "/freight-delivery-ferndale", component: LocationPage },
  { path: "/freight-delivery-george", component: LocationPage },
  { path: "/freight-delivery-graaff-reinet", component: LocationPage },
  { path: "/freight-delivery-grahamstown", component: LocationPage },
  { path: "/freight-delivery-hermanus", component: LocationPage },
  { path: "/freight-delivery-jeffreys-bay", component: LocationPage },
  { path: "/freight-delivery-johannesburg", component: LocationPage },
  { path: "/freight-delivery-kimberley", component: LocationPage },
  { path: "/freight-delivery-klerksdorp", component: LocationPage },
  { path: "/freight-delivery-knysna", component: LocationPage },
  { path: "/freight-delivery-kroonstad", component: LocationPage },
  { path: "/freight-delivery-kuilsrivier", component: LocationPage },
  { path: "/freight-delivery-ladysmith", component: LocationPage },
  { path: "/freight-delivery-lebowakgomo", component: LocationPage },
  { path: "/freight-delivery-lichtenburg", component: LocationPage },
  { path: "/freight-delivery-mahikeng", component: LocationPage },
  { path: "/freight-delivery-malmesbury", component: LocationPage },
  { path: "/freight-delivery-middelburg", component: LocationPage },
  { path: "/freight-delivery-midrand", component: LocationPage },
  { path: "/freight-delivery-mmabatho", component: LocationPage },
  { path: "/freight-delivery-mossel-bay", component: LocationPage },
  { path: "/freight-delivery-musina", component: LocationPage },
  { path: "/freight-delivery-nelspruit", component: LocationPage },
  { path: "/freight-delivery-oudtshoorn", component: LocationPage },
  { path: "/freight-delivery-paarl", component: LocationPage },
  { path: "/freight-delivery-pietermaritzburg", component: LocationPage },
  { path: "/freight-delivery-polokwane", component: LocationPage },
  { path: "/freight-delivery-port-elizabeth", component: LocationPage },
  { path: "/freight-delivery-port-shepstone", component: LocationPage },
  { path: "/freight-delivery-potchefstroom", component: LocationPage },
  { path: "/freight-delivery-pretoria", component: LocationPage },
  { path: "/freight-delivery-queenstown", component: LocationPage },
  { path: "/freight-delivery-randburg", component: LocationPage },
  { path: "/freight-delivery-roodepoort", component: LocationPage },
  { path: "/freight-delivery-rustenburg", component: LocationPage },
  { path: "/freight-delivery-saldanha", component: LocationPage },
  { path: "/freight-delivery-sandton", component: LocationPage },
  { path: "/freight-delivery-soweto", component: LocationPage },
  { path: "/freight-delivery-springs", component: LocationPage },
  { path: "/freight-delivery-standerton", component: LocationPage },
  { path: "/freight-delivery-thohoyandou", component: LocationPage },
  { path: "/freight-delivery-tzaneen", component: LocationPage },
  { path: "/freight-delivery-uitenhage", component: LocationPage },
  { path: "/freight-delivery-ulundi", component: LocationPage },
  { path: "/freight-delivery-umtata", component: LocationPage },
  { path: "/freight-delivery-upington", component: LocationPage },
  { path: "/freight-delivery-vanderbijlpark", component: LocationPage },
  { path: "/freight-delivery-vereeniging", component: LocationPage },
  { path: "/freight-delivery-volksrust", component: LocationPage },
  { path: "/freight-delivery-vredenburg", component: LocationPage },
  { path: "/freight-delivery-vryburg", component: LocationPage },
  { path: "/freight-delivery-vryheid", component: LocationPage },
  { path: "/freight-delivery-welkom", component: LocationPage },
  { path: "/freight-delivery-worcester", component: LocationPage },
  { path: "/furniture-delivery-alberton", component: LocationPage },
  { path: "/furniture-delivery-aliwal-north", component: LocationPage },
  { path: "/furniture-delivery-beaufort-west", component: LocationPage },
  { path: "/furniture-delivery-benoni", component: LocationPage },
  { path: "/furniture-delivery-bethal", component: LocationPage },
  { path: "/furniture-delivery-bhisho", component: LocationPage },
  { path: "/furniture-delivery-bloemfontein", component: LocationPage },
  { path: "/furniture-delivery-brakpan", component: LocationPage },
  { path: "/furniture-delivery-brits", component: LocationPage },
  { path: "/furniture-delivery-cape-town", component: LocationPage },
  { path: "/furniture-delivery-carletonville", component: LocationPage },
  { path: "/furniture-delivery-centurion", component: LocationPage },
  { path: "/furniture-delivery-cradock", component: LocationPage },
  { path: "/furniture-delivery-de-aar", component: LocationPage },
  { path: "/furniture-delivery-durban", component: LocationPage },
  { path: "/furniture-delivery-east-london", component: LocationPage },
  { path: "/furniture-delivery-ferndale", component: LocationPage },
  { path: "/furniture-delivery-george", component: LocationPage },
  { path: "/furniture-delivery-graaff-reinet", component: LocationPage },
  { path: "/furniture-delivery-grahamstown", component: LocationPage },
  { path: "/furniture-delivery-hermanus", component: LocationPage },
  { path: "/furniture-delivery-jeffreys-bay", component: LocationPage },
  { path: "/furniture-delivery-johannesburg", component: LocationPage },
  { path: "/furniture-delivery-kimberley", component: LocationPage },
  { path: "/furniture-delivery-klerksdorp", component: LocationPage },
  { path: "/furniture-delivery-knysna", component: LocationPage },
  { path: "/furniture-delivery-kroonstad", component: LocationPage },
  { path: "/furniture-delivery-kuilsrivier", component: LocationPage },
  { path: "/furniture-delivery-ladysmith", component: LocationPage },
  { path: "/furniture-delivery-lebowakgomo", component: LocationPage },
  { path: "/furniture-delivery-lichtenburg", component: LocationPage },
  { path: "/furniture-delivery-mahikeng", component: LocationPage },
  { path: "/furniture-delivery-malmesbury", component: LocationPage },
  { path: "/furniture-delivery-middelburg", component: LocationPage },
  { path: "/furniture-delivery-midrand", component: LocationPage },
  { path: "/furniture-delivery-mmabatho", component: LocationPage },
  { path: "/furniture-delivery-mossel-bay", component: LocationPage },
  { path: "/furniture-delivery-musina", component: LocationPage },
  { path: "/furniture-delivery-nelspruit", component: LocationPage },
  { path: "/furniture-delivery-oudtshoorn", component: LocationPage },
  { path: "/furniture-delivery-paarl", component: LocationPage },
  { path: "/furniture-delivery-pietermaritzburg", component: LocationPage },
  { path: "/furniture-delivery-polokwane", component: LocationPage },
  { path: "/furniture-delivery-port-elizabeth", component: LocationPage },
  { path: "/furniture-delivery-port-shepstone", component: LocationPage },
  { path: "/furniture-delivery-potchefstroom", component: LocationPage },
  { path: "/furniture-delivery-pretoria", component: LocationPage },
  { path: "/furniture-delivery-queenstown", component: LocationPage },
  { path: "/furniture-delivery-randburg", component: LocationPage },
  { path: "/furniture-delivery-roodepoort", component: LocationPage },
  { path: "/furniture-delivery-rustenburg", component: LocationPage },
  { path: "/furniture-delivery-saldanha", component: LocationPage },
  { path: "/furniture-delivery-sandton", component: LocationPage },
  { path: "/furniture-delivery-soweto", component: LocationPage },
  { path: "/furniture-delivery-springs", component: LocationPage },
  { path: "/furniture-delivery-standerton", component: LocationPage },
  { path: "/furniture-delivery-thohoyandou", component: LocationPage },
  { path: "/furniture-delivery-tzaneen", component: LocationPage },
  { path: "/furniture-delivery-uitenhage", component: LocationPage },
  { path: "/furniture-delivery-ulundi", component: LocationPage },
  { path: "/furniture-delivery-umtata", component: LocationPage },
  { path: "/furniture-delivery-upington", component: LocationPage },
  { path: "/furniture-delivery-vanderbijlpark", component: LocationPage },
  { path: "/furniture-delivery-vereeniging", component: LocationPage },
  { path: "/furniture-delivery-volksrust", component: LocationPage },
  { path: "/furniture-delivery-vredenburg", component: LocationPage },
  { path: "/furniture-delivery-vryburg", component: LocationPage },
  { path: "/furniture-delivery-vryheid", component: LocationPage },
  { path: "/furniture-delivery-welkom", component: LocationPage },
  { path: "/furniture-delivery-worcester", component: LocationPage },
  { path: "/furniture-movers-alberton", component: LocationPage },
  { path: "/furniture-movers-aliwal-north", component: LocationPage },
  { path: "/furniture-movers-beaufort-west", component: LocationPage },
  { path: "/furniture-movers-benoni", component: LocationPage },
  { path: "/furniture-movers-bethal", component: LocationPage },
  { path: "/furniture-movers-bhisho", component: LocationPage },
  { path: "/furniture-movers-bloemfontein", component: LocationPage },
  { path: "/furniture-movers-brakpan", component: LocationPage },
  { path: "/furniture-movers-brits", component: LocationPage },
  { path: "/furniture-movers-cape-town", component: LocationPage },
  { path: "/furniture-movers-carletonville", component: LocationPage },
  { path: "/furniture-movers-centurion", component: LocationPage },
  { path: "/furniture-movers-cradock", component: LocationPage },
  { path: "/furniture-movers-de-aar", component: LocationPage },
  { path: "/furniture-movers-durban", component: LocationPage },
  { path: "/furniture-movers-east-london", component: LocationPage },
  { path: "/furniture-movers-ferndale", component: LocationPage },
  { path: "/furniture-movers-george", component: LocationPage },
  { path: "/furniture-movers-graaff-reinet", component: LocationPage },
  { path: "/furniture-movers-grahamstown", component: LocationPage },
  { path: "/furniture-movers-hermanus", component: LocationPage },
  { path: "/furniture-movers-jeffreys-bay", component: LocationPage },
  { path: "/furniture-movers-johannesburg", component: LocationPage },
  { path: "/furniture-movers-kimberley", component: LocationPage },
  { path: "/furniture-movers-klerksdorp", component: LocationPage },
  { path: "/furniture-movers-knysna", component: LocationPage },
  { path: "/furniture-movers-kroonstad", component: LocationPage },
  { path: "/furniture-movers-kuilsrivier", component: LocationPage },
  { path: "/furniture-movers-ladysmith", component: LocationPage },
  { path: "/furniture-movers-lebowakgomo", component: LocationPage },
  { path: "/furniture-movers-lichtenburg", component: LocationPage },
  { path: "/furniture-movers-mahikeng", component: LocationPage },
  { path: "/furniture-movers-malmesbury", component: LocationPage },
  { path: "/furniture-movers-middelburg", component: LocationPage },
  { path: "/furniture-movers-midrand", component: LocationPage },
  { path: "/furniture-movers-mmabatho", component: LocationPage },
  { path: "/furniture-movers-mossel-bay", component: LocationPage },
  { path: "/furniture-movers-musina", component: LocationPage },
  { path: "/furniture-movers-nelspruit", component: LocationPage },
  { path: "/furniture-movers-oudtshoorn", component: LocationPage },
  { path: "/furniture-movers-paarl", component: LocationPage },
  { path: "/furniture-movers-pietermaritzburg", component: LocationPage },
  { path: "/furniture-movers-polokwane", component: LocationPage },
  { path: "/furniture-movers-port-elizabeth", component: LocationPage },
  { path: "/furniture-movers-port-shepstone", component: LocationPage },
  { path: "/furniture-movers-potchefstroom", component: LocationPage },
  { path: "/furniture-movers-pretoria", component: LocationPage },
  { path: "/furniture-movers-queenstown", component: LocationPage },
  { path: "/furniture-movers-randburg", component: LocationPage },
  { path: "/furniture-movers-roodepoort", component: LocationPage },
  { path: "/furniture-movers-rustenburg", component: LocationPage },
  { path: "/furniture-movers-saldanha", component: LocationPage },
  { path: "/furniture-movers-sandton", component: LocationPage },
  { path: "/furniture-movers-soweto", component: LocationPage },
  { path: "/furniture-movers-springs", component: LocationPage },
  { path: "/furniture-movers-standerton", component: LocationPage },
  { path: "/furniture-movers-thohoyandou", component: LocationPage },
  { path: "/furniture-movers-tzaneen", component: LocationPage },
  { path: "/furniture-movers-uitenhage", component: LocationPage },
  { path: "/furniture-movers-ulundi", component: LocationPage },
  { path: "/furniture-movers-umtata", component: LocationPage },
  { path: "/furniture-movers-upington", component: LocationPage },
  { path: "/furniture-movers-vanderbijlpark", component: LocationPage },
  { path: "/furniture-movers-vereeniging", component: LocationPage },
  { path: "/furniture-movers-volksrust", component: LocationPage },
  { path: "/furniture-movers-vredenburg", component: LocationPage },
  { path: "/furniture-movers-vryburg", component: LocationPage },
  { path: "/furniture-movers-vryheid", component: LocationPage },
  { path: "/furniture-movers-welkom", component: LocationPage },
  { path: "/furniture-movers-worcester", component: LocationPage },
  { path: "/goods-transport-alberton", component: LocationPage },
  { path: "/goods-transport-aliwal-north", component: LocationPage },
  { path: "/goods-transport-beaufort-west", component: LocationPage },
  { path: "/goods-transport-benoni", component: LocationPage },
  { path: "/goods-transport-bethal", component: LocationPage },
  { path: "/goods-transport-bhisho", component: LocationPage },
  { path: "/goods-transport-bloemfontein", component: LocationPage },
  { path: "/goods-transport-brakpan", component: LocationPage },
  { path: "/goods-transport-brits", component: LocationPage },
  { path: "/goods-transport-cape-town", component: LocationPage },
  { path: "/goods-transport-carletonville", component: LocationPage },
  { path: "/goods-transport-centurion", component: LocationPage },
  { path: "/goods-transport-cradock", component: LocationPage },
  { path: "/goods-transport-de-aar", component: LocationPage },
  { path: "/goods-transport-durban", component: LocationPage },
  { path: "/goods-transport-east-london", component: LocationPage },
  { path: "/goods-transport-ferndale", component: LocationPage },
  { path: "/goods-transport-george", component: LocationPage },
  { path: "/goods-transport-graaff-reinet", component: LocationPage },
  { path: "/goods-transport-grahamstown", component: LocationPage },
  { path: "/goods-transport-hermanus", component: LocationPage },
  { path: "/goods-transport-jeffreys-bay", component: LocationPage },
  { path: "/goods-transport-johannesburg", component: LocationPage },
  { path: "/goods-transport-kimberley", component: LocationPage },
  { path: "/goods-transport-klerksdorp", component: LocationPage },
  { path: "/goods-transport-knysna", component: LocationPage },
  { path: "/goods-transport-kroonstad", component: LocationPage },
  { path: "/goods-transport-kuilsrivier", component: LocationPage },
  { path: "/goods-transport-ladysmith", component: LocationPage },
  { path: "/goods-transport-lebowakgomo", component: LocationPage },
  { path: "/goods-transport-lichtenburg", component: LocationPage },
  { path: "/goods-transport-mahikeng", component: LocationPage },
  { path: "/goods-transport-malmesbury", component: LocationPage },
  { path: "/goods-transport-middelburg", component: LocationPage },
  { path: "/goods-transport-midrand", component: LocationPage },
  { path: "/goods-transport-mmabatho", component: LocationPage },
  { path: "/goods-transport-mossel-bay", component: LocationPage },
  { path: "/goods-transport-musina", component: LocationPage },
  { path: "/goods-transport-nelspruit", component: LocationPage },
  { path: "/goods-transport-oudtshoorn", component: LocationPage },
  { path: "/goods-transport-paarl", component: LocationPage },
  { path: "/goods-transport-pietermaritzburg", component: LocationPage },
  { path: "/goods-transport-polokwane", component: LocationPage },
  { path: "/goods-transport-port-elizabeth", component: LocationPage },
  { path: "/goods-transport-port-shepstone", component: LocationPage },
  { path: "/goods-transport-potchefstroom", component: LocationPage },
  { path: "/goods-transport-pretoria", component: LocationPage },
  { path: "/goods-transport-queenstown", component: LocationPage },
  { path: "/goods-transport-randburg", component: LocationPage },
  { path: "/goods-transport-roodepoort", component: LocationPage },
  { path: "/goods-transport-rustenburg", component: LocationPage },
  { path: "/goods-transport-saldanha", component: LocationPage },
  { path: "/goods-transport-sandton", component: LocationPage },
  { path: "/goods-transport-soweto", component: LocationPage },
  { path: "/goods-transport-springs", component: LocationPage },
  { path: "/goods-transport-standerton", component: LocationPage },
  { path: "/goods-transport-thohoyandou", component: LocationPage },
  { path: "/goods-transport-tzaneen", component: LocationPage },
  { path: "/goods-transport-uitenhage", component: LocationPage },
  { path: "/goods-transport-ulundi", component: LocationPage },
  { path: "/goods-transport-umtata", component: LocationPage },
  { path: "/goods-transport-upington", component: LocationPage },
  { path: "/goods-transport-vanderbijlpark", component: LocationPage },
  { path: "/goods-transport-vereeniging", component: LocationPage },
  { path: "/goods-transport-volksrust", component: LocationPage },
  { path: "/goods-transport-vredenburg", component: LocationPage },
  { path: "/goods-transport-vryburg", component: LocationPage },
  { path: "/goods-transport-vryheid", component: LocationPage },
  { path: "/goods-transport-welkom", component: LocationPage },
  { path: "/goods-transport-worcester", component: LocationPage },
  { path: "/h100-alberton", component: LocationPage },
  { path: "/h100-aliwal-north", component: LocationPage },
  { path: "/h100-beaufort-west", component: LocationPage },
  { path: "/h100-benoni", component: LocationPage },
  { path: "/h100-bethal", component: LocationPage },
  { path: "/h100-bhisho", component: LocationPage },
  { path: "/h100-bloemfontein", component: LocationPage },
  { path: "/h100-brakpan", component: LocationPage },
  { path: "/h100-brits", component: LocationPage },
  { path: "/h100-cape-town", component: LocationPage },
  { path: "/h100-carletonville", component: LocationPage },
  { path: "/h100-centurion", component: LocationPage },
  { path: "/h100-cradock", component: LocationPage },
  { path: "/h100-de-aar", component: LocationPage },
  { path: "/h100-durban", component: LocationPage },
  { path: "/h100-east-london", component: LocationPage },
  { path: "/h100-ferndale", component: LocationPage },
  { path: "/h100-george", component: LocationPage },
  { path: "/h100-graaff-reinet", component: LocationPage },
  { path: "/h100-grahamstown", component: LocationPage },
  { path: "/h100-hermanus", component: LocationPage },
  { path: "/h100-jeffreys-bay", component: LocationPage },
  { path: "/h100-johannesburg", component: LocationPage },
  { path: "/h100-kimberley", component: LocationPage },
  { path: "/h100-klerksdorp", component: LocationPage },
  { path: "/h100-knysna", component: LocationPage },
  { path: "/h100-kroonstad", component: LocationPage },
  { path: "/h100-kuilsrivier", component: LocationPage },
  { path: "/h100-ladysmith", component: LocationPage },
  { path: "/h100-lebowakgomo", component: LocationPage },
  { path: "/h100-lichtenburg", component: LocationPage },
  { path: "/h100-mahikeng", component: LocationPage },
  { path: "/h100-malmesbury", component: LocationPage },
  { path: "/h100-middelburg", component: LocationPage },
  { path: "/h100-midrand", component: LocationPage },
  { path: "/h100-mmabatho", component: LocationPage },
  { path: "/h100-mossel-bay", component: LocationPage },
  { path: "/h100-musina", component: LocationPage },
  { path: "/h100-nelspruit", component: LocationPage },
  { path: "/h100-oudtshoorn", component: LocationPage },
  { path: "/h100-paarl", component: LocationPage },
  { path: "/h100-pietermaritzburg", component: LocationPage },
  { path: "/h100-polokwane", component: LocationPage },
  { path: "/h100-port-elizabeth", component: LocationPage },
  { path: "/h100-port-shepstone", component: LocationPage },
  { path: "/h100-potchefstroom", component: LocationPage },
  { path: "/h100-pretoria", component: LocationPage },
  { path: "/h100-queenstown", component: LocationPage },
  { path: "/h100-randburg", component: LocationPage },
  { path: "/h100-roodepoort", component: LocationPage },
  { path: "/h100-rustenburg", component: LocationPage },
  { path: "/h100-saldanha", component: LocationPage },
  { path: "/h100-sandton", component: LocationPage },
  { path: "/h100-soweto", component: LocationPage },
  { path: "/h100-springs", component: LocationPage },
  { path: "/h100-standerton", component: LocationPage },
  { path: "/h100-thohoyandou", component: LocationPage },
  { path: "/h100-tzaneen", component: LocationPage },
  { path: "/h100-uitenhage", component: LocationPage },
  { path: "/h100-ulundi", component: LocationPage },
  { path: "/h100-umtata", component: LocationPage },
  { path: "/h100-upington", component: LocationPage },
  { path: "/h100-vanderbijlpark", component: LocationPage },
  { path: "/h100-vereeniging", component: LocationPage },
  { path: "/h100-volksrust", component: LocationPage },
  { path: "/h100-vredenburg", component: LocationPage },
  { path: "/h100-vryburg", component: LocationPage },
  { path: "/h100-vryheid", component: LocationPage },
  { path: "/h100-welkom", component: LocationPage },
  { path: "/h100-worcester", component: LocationPage },
  { path: "/heavy-equipment-delivery-alberton", component: LocationPage },
  { path: "/heavy-equipment-delivery-aliwal-north", component: LocationPage },
  { path: "/heavy-equipment-delivery-beaufort-west", component: LocationPage },
  { path: "/heavy-equipment-delivery-benoni", component: LocationPage },
  { path: "/heavy-equipment-delivery-bethal", component: LocationPage },
  { path: "/heavy-equipment-delivery-bhisho", component: LocationPage },
  { path: "/heavy-equipment-delivery-bloemfontein", component: LocationPage },
  { path: "/heavy-equipment-delivery-brakpan", component: LocationPage },
  { path: "/heavy-equipment-delivery-brits", component: LocationPage },
  { path: "/heavy-equipment-delivery-cape-town", component: LocationPage },
  { path: "/heavy-equipment-delivery-carletonville", component: LocationPage },
  { path: "/heavy-equipment-delivery-centurion", component: LocationPage },
  { path: "/heavy-equipment-delivery-cradock", component: LocationPage },
  { path: "/heavy-equipment-delivery-de-aar", component: LocationPage },
  { path: "/heavy-equipment-delivery-durban", component: LocationPage },
  { path: "/heavy-equipment-delivery-east-london", component: LocationPage },
  { path: "/heavy-equipment-delivery-ferndale", component: LocationPage },
  { path: "/heavy-equipment-delivery-george", component: LocationPage },
  { path: "/heavy-equipment-delivery-graaff-reinet", component: LocationPage },
  { path: "/heavy-equipment-delivery-grahamstown", component: LocationPage },
  { path: "/heavy-equipment-delivery-hermanus", component: LocationPage },
  { path: "/heavy-equipment-delivery-jeffreys-bay", component: LocationPage },
  { path: "/heavy-equipment-delivery-johannesburg", component: LocationPage },
  { path: "/heavy-equipment-delivery-kimberley", component: LocationPage },
  { path: "/heavy-equipment-delivery-klerksdorp", component: LocationPage },
  { path: "/heavy-equipment-delivery-knysna", component: LocationPage },
  { path: "/heavy-equipment-delivery-kroonstad", component: LocationPage },
  { path: "/heavy-equipment-delivery-kuilsrivier", component: LocationPage },
  { path: "/heavy-equipment-delivery-ladysmith", component: LocationPage },
  { path: "/heavy-equipment-delivery-lebowakgomo", component: LocationPage },
  { path: "/heavy-equipment-delivery-lichtenburg", component: LocationPage },
  { path: "/heavy-equipment-delivery-mahikeng", component: LocationPage },
  { path: "/heavy-equipment-delivery-malmesbury", component: LocationPage },
  { path: "/heavy-equipment-delivery-middelburg", component: LocationPage },
  { path: "/heavy-equipment-delivery-midrand", component: LocationPage },
  { path: "/heavy-equipment-delivery-mmabatho", component: LocationPage },
  { path: "/heavy-equipment-delivery-mossel-bay", component: LocationPage },
  { path: "/heavy-equipment-delivery-musina", component: LocationPage },
  { path: "/heavy-equipment-delivery-nelspruit", component: LocationPage },
  { path: "/heavy-equipment-delivery-oudtshoorn", component: LocationPage },
  { path: "/heavy-equipment-delivery-paarl", component: LocationPage },
  { path: "/heavy-equipment-delivery-pietermaritzburg", component: LocationPage },
  { path: "/heavy-equipment-delivery-polokwane", component: LocationPage },
  { path: "/heavy-equipment-delivery-port-elizabeth", component: LocationPage },
  { path: "/heavy-equipment-delivery-port-shepstone", component: LocationPage },
  { path: "/heavy-equipment-delivery-potchefstroom", component: LocationPage },
  { path: "/heavy-equipment-delivery-pretoria", component: LocationPage },
  { path: "/heavy-equipment-delivery-queenstown", component: LocationPage },
  { path: "/heavy-equipment-delivery-randburg", component: LocationPage },
  { path: "/heavy-equipment-delivery-roodepoort", component: LocationPage },
  { path: "/heavy-equipment-delivery-rustenburg", component: LocationPage },
  { path: "/heavy-equipment-delivery-saldanha", component: LocationPage },
  { path: "/heavy-equipment-delivery-sandton", component: LocationPage },
  { path: "/heavy-equipment-delivery-soweto", component: LocationPage },
  { path: "/heavy-equipment-delivery-springs", component: LocationPage },
  { path: "/heavy-equipment-delivery-standerton", component: LocationPage },
  { path: "/heavy-equipment-delivery-thohoyandou", component: LocationPage },
  { path: "/heavy-equipment-delivery-tzaneen", component: LocationPage },
  { path: "/heavy-equipment-delivery-uitenhage", component: LocationPage },
  { path: "/heavy-equipment-delivery-ulundi", component: LocationPage },
  { path: "/heavy-equipment-delivery-umtata", component: LocationPage },
  { path: "/heavy-equipment-delivery-upington", component: LocationPage },
  { path: "/heavy-equipment-delivery-vanderbijlpark", component: LocationPage },
  { path: "/heavy-equipment-delivery-vereeniging", component: LocationPage },
  { path: "/heavy-equipment-delivery-volksrust", component: LocationPage },
  { path: "/heavy-equipment-delivery-vredenburg", component: LocationPage },
  { path: "/heavy-equipment-delivery-vryburg", component: LocationPage },
  { path: "/heavy-equipment-delivery-vryheid", component: LocationPage },
  { path: "/heavy-equipment-delivery-welkom", component: LocationPage },
  { path: "/heavy-equipment-delivery-worcester", component: LocationPage },
  { path: "/local-delivery-alberton", component: LocationPage },
  { path: "/local-delivery-aliwal-north", component: LocationPage },
  { path: "/local-delivery-beaufort-west", component: LocationPage },
  { path: "/local-delivery-benoni", component: LocationPage },
  { path: "/local-delivery-bethal", component: LocationPage },
  { path: "/local-delivery-bhisho", component: LocationPage },
  { path: "/local-delivery-bloemfontein", component: LocationPage },
  { path: "/local-delivery-brakpan", component: LocationPage },
  { path: "/local-delivery-brits", component: LocationPage },
  { path: "/local-delivery-cape-town", component: LocationPage },
  { path: "/local-delivery-carletonville", component: LocationPage },
  { path: "/local-delivery-centurion", component: LocationPage },
  { path: "/local-delivery-cradock", component: LocationPage },
  { path: "/local-delivery-de-aar", component: LocationPage },
  { path: "/local-delivery-durban", component: LocationPage },
  { path: "/local-delivery-east-london", component: LocationPage },
  { path: "/local-delivery-ferndale", component: LocationPage },
  { path: "/local-delivery-george", component: LocationPage },
  { path: "/local-delivery-graaff-reinet", component: LocationPage },
  { path: "/local-delivery-grahamstown", component: LocationPage },
  { path: "/local-delivery-hermanus", component: LocationPage },
  { path: "/local-delivery-jeffreys-bay", component: LocationPage },
  { path: "/local-delivery-johannesburg", component: LocationPage },
  { path: "/local-delivery-kimberley", component: LocationPage },
  { path: "/local-delivery-klerksdorp", component: LocationPage },
  { path: "/local-delivery-knysna", component: LocationPage },
  { path: "/local-delivery-kroonstad", component: LocationPage },
  { path: "/local-delivery-kuilsrivier", component: LocationPage },
  { path: "/local-delivery-ladysmith", component: LocationPage },
  { path: "/local-delivery-lebowakgomo", component: LocationPage },
  { path: "/local-delivery-lichtenburg", component: LocationPage },
  { path: "/local-delivery-mahikeng", component: LocationPage },
  { path: "/local-delivery-malmesbury", component: LocationPage },
  { path: "/local-delivery-middelburg", component: LocationPage },
  { path: "/local-delivery-midrand", component: LocationPage },
  { path: "/local-delivery-mmabatho", component: LocationPage },
  { path: "/local-delivery-mossel-bay", component: LocationPage },
  { path: "/local-delivery-musina", component: LocationPage },
  { path: "/local-delivery-nelspruit", component: LocationPage },
  { path: "/local-delivery-oudtshoorn", component: LocationPage },
  { path: "/local-delivery-paarl", component: LocationPage },
  { path: "/local-delivery-pietermaritzburg", component: LocationPage },
  { path: "/local-delivery-polokwane", component: LocationPage },
  { path: "/local-delivery-port-elizabeth", component: LocationPage },
  { path: "/local-delivery-port-shepstone", component: LocationPage },
  { path: "/local-delivery-potchefstroom", component: LocationPage },
  { path: "/local-delivery-pretoria", component: LocationPage },
  { path: "/local-delivery-queenstown", component: LocationPage },
  { path: "/local-delivery-randburg", component: LocationPage },
  { path: "/local-delivery-roodepoort", component: LocationPage },
  { path: "/local-delivery-rustenburg", component: LocationPage },
  { path: "/local-delivery-saldanha", component: LocationPage },
  { path: "/local-delivery-sandton", component: LocationPage },
  { path: "/local-delivery-soweto", component: LocationPage },
  { path: "/local-delivery-springs", component: LocationPage },
  { path: "/local-delivery-standerton", component: LocationPage },
  { path: "/local-delivery-thohoyandou", component: LocationPage },
  { path: "/local-delivery-tzaneen", component: LocationPage },
  { path: "/local-delivery-uitenhage", component: LocationPage },
  { path: "/local-delivery-ulundi", component: LocationPage },
  { path: "/local-delivery-umtata", component: LocationPage },
  { path: "/local-delivery-upington", component: LocationPage },
  { path: "/local-delivery-vanderbijlpark", component: LocationPage },
  { path: "/local-delivery-vereeniging", component: LocationPage },
  { path: "/local-delivery-volksrust", component: LocationPage },
  { path: "/local-delivery-vredenburg", component: LocationPage },
  { path: "/local-delivery-vryburg", component: LocationPage },
  { path: "/local-delivery-vryheid", component: LocationPage },
  { path: "/local-delivery-welkom", component: LocationPage },
  { path: "/local-delivery-worcester", component: LocationPage },
  { path: "/local-movers-near-me-alberton", component: LocationPage },
  { path: "/local-movers-near-me-aliwal-north", component: LocationPage },
  { path: "/local-movers-near-me-beaufort-west", component: LocationPage },
  { path: "/local-movers-near-me-benoni", component: LocationPage },
  { path: "/local-movers-near-me-bethal", component: LocationPage },
  { path: "/local-movers-near-me-bhisho", component: LocationPage },
  { path: "/local-movers-near-me-bloemfontein", component: LocationPage },
  { path: "/local-movers-near-me-brakpan", component: LocationPage },
  { path: "/local-movers-near-me-brits", component: LocationPage },
  { path: "/local-movers-near-me-cape-town", component: LocationPage },
  { path: "/local-movers-near-me-carletonville", component: LocationPage },
  { path: "/local-movers-near-me-centurion", component: LocationPage },
  { path: "/local-movers-near-me-cradock", component: LocationPage },
  { path: "/local-movers-near-me-de-aar", component: LocationPage },
  { path: "/local-movers-near-me-durban", component: LocationPage },
  { path: "/local-movers-near-me-east-london", component: LocationPage },
  { path: "/local-movers-near-me-ferndale", component: LocationPage },
  { path: "/local-movers-near-me-george", component: LocationPage },
  { path: "/local-movers-near-me-graaff-reinet", component: LocationPage },
  { path: "/local-movers-near-me-grahamstown", component: LocationPage },
  { path: "/local-movers-near-me-hermanus", component: LocationPage },
  { path: "/local-movers-near-me-jeffreys-bay", component: LocationPage },
  { path: "/local-movers-near-me-johannesburg", component: LocationPage },
  { path: "/local-movers-near-me-kimberley", component: LocationPage },
  { path: "/local-movers-near-me-klerksdorp", component: LocationPage },
  { path: "/local-movers-near-me-knysna", component: LocationPage },
  { path: "/local-movers-near-me-kroonstad", component: LocationPage },
  { path: "/local-movers-near-me-kuilsrivier", component: LocationPage },
  { path: "/local-movers-near-me-ladysmith", component: LocationPage },
  { path: "/local-movers-near-me-lebowakgomo", component: LocationPage },
  { path: "/local-movers-near-me-lichtenburg", component: LocationPage },
  { path: "/local-movers-near-me-mahikeng", component: LocationPage },
  { path: "/local-movers-near-me-malmesbury", component: LocationPage },
  { path: "/local-movers-near-me-middelburg", component: LocationPage },
  { path: "/local-movers-near-me-midrand", component: LocationPage },
  { path: "/local-movers-near-me-mmabatho", component: LocationPage },
  { path: "/local-movers-near-me-mossel-bay", component: LocationPage },
  { path: "/local-movers-near-me-musina", component: LocationPage },
  { path: "/local-movers-near-me-nelspruit", component: LocationPage },
  { path: "/local-movers-near-me-oudtshoorn", component: LocationPage },
  { path: "/local-movers-near-me-paarl", component: LocationPage },
  { path: "/local-movers-near-me-pietermaritzburg", component: LocationPage },
  { path: "/local-movers-near-me-polokwane", component: LocationPage },
  { path: "/local-movers-near-me-port-elizabeth", component: LocationPage },
  { path: "/local-movers-near-me-port-shepstone", component: LocationPage },
  { path: "/local-movers-near-me-potchefstroom", component: LocationPage },
  { path: "/local-movers-near-me-pretoria", component: LocationPage },
  { path: "/local-movers-near-me-queenstown", component: LocationPage },
  { path: "/local-movers-near-me-randburg", component: LocationPage },
  { path: "/local-movers-near-me-roodepoort", component: LocationPage },
  { path: "/local-movers-near-me-rustenburg", component: LocationPage },
  { path: "/local-movers-near-me-saldanha", component: LocationPage },
  { path: "/local-movers-near-me-sandton", component: LocationPage },
  { path: "/local-movers-near-me-soweto", component: LocationPage },
  { path: "/local-movers-near-me-springs", component: LocationPage },
  { path: "/local-movers-near-me-standerton", component: LocationPage },
  { path: "/local-movers-near-me-thohoyandou", component: LocationPage },
  { path: "/local-movers-near-me-tzaneen", component: LocationPage },
  { path: "/local-movers-near-me-uitenhage", component: LocationPage },
  { path: "/local-movers-near-me-ulundi", component: LocationPage },
  { path: "/local-movers-near-me-umtata", component: LocationPage },
  { path: "/local-movers-near-me-upington", component: LocationPage },
  { path: "/local-movers-near-me-vanderbijlpark", component: LocationPage },
  { path: "/local-movers-near-me-vereeniging", component: LocationPage },
  { path: "/local-movers-near-me-volksrust", component: LocationPage },
  { path: "/local-movers-near-me-vredenburg", component: LocationPage },
  { path: "/local-movers-near-me-vryburg", component: LocationPage },
  { path: "/local-movers-near-me-vryheid", component: LocationPage },
  { path: "/local-movers-near-me-welkom", component: LocationPage },
  { path: "/local-movers-near-me-worcester", component: LocationPage },
  { path: "/long-distance-alberton", component: LocationPage },
  { path: "/long-distance-aliwal-north", component: LocationPage },
  { path: "/long-distance-beaufort-west", component: LocationPage },
  { path: "/long-distance-benoni", component: LocationPage },
  { path: "/long-distance-bethal", component: LocationPage },
  { path: "/long-distance-bhisho", component: LocationPage },
  { path: "/long-distance-bloemfontein", component: LocationPage },
  { path: "/long-distance-brakpan", component: LocationPage },
  { path: "/long-distance-brits", component: LocationPage },
  { path: "/long-distance-cape-town", component: LocationPage },
  { path: "/long-distance-carletonville", component: LocationPage },
  { path: "/long-distance-centurion", component: LocationPage },
  { path: "/long-distance-cradock", component: LocationPage },
  { path: "/long-distance-de-aar", component: LocationPage },
  { path: "/long-distance-durban", component: LocationPage },
  { path: "/long-distance-east-london", component: LocationPage },
  { path: "/long-distance-ferndale", component: LocationPage },
  { path: "/long-distance-george", component: LocationPage },
  { path: "/long-distance-graaff-reinet", component: LocationPage },
  { path: "/long-distance-grahamstown", component: LocationPage },
  { path: "/long-distance-hermanus", component: LocationPage },
  { path: "/long-distance-jeffreys-bay", component: LocationPage },
  { path: "/long-distance-johannesburg", component: LocationPage },
  { path: "/long-distance-kimberley", component: LocationPage },
  { path: "/long-distance-klerksdorp", component: LocationPage },
  { path: "/long-distance-knysna", component: LocationPage },
  { path: "/long-distance-kroonstad", component: LocationPage },
  { path: "/long-distance-kuilsrivier", component: LocationPage },
  { path: "/long-distance-ladysmith", component: LocationPage },
  { path: "/long-distance-lebowakgomo", component: LocationPage },
  { path: "/long-distance-lichtenburg", component: LocationPage },
  { path: "/long-distance-mahikeng", component: LocationPage },
  { path: "/long-distance-malmesbury", component: LocationPage },
  { path: "/long-distance-middelburg", component: LocationPage },
  { path: "/long-distance-midrand", component: LocationPage },
  { path: "/long-distance-mmabatho", component: LocationPage },
  { path: "/long-distance-mossel-bay", component: LocationPage },
  { path: "/long-distance-musina", component: LocationPage },
  { path: "/long-distance-nelspruit", component: LocationPage },
  { path: "/long-distance-oudtshoorn", component: LocationPage },
  { path: "/long-distance-paarl", component: LocationPage },
  { path: "/long-distance-pietermaritzburg", component: LocationPage },
  { path: "/long-distance-polokwane", component: LocationPage },
  { path: "/long-distance-port-elizabeth", component: LocationPage },
  { path: "/long-distance-port-shepstone", component: LocationPage },
  { path: "/long-distance-potchefstroom", component: LocationPage },
  { path: "/long-distance-pretoria", component: LocationPage },
  { path: "/long-distance-queenstown", component: LocationPage },
  { path: "/long-distance-randburg", component: LocationPage },
  { path: "/long-distance-roodepoort", component: LocationPage },
  { path: "/long-distance-rustenburg", component: LocationPage },
  { path: "/long-distance-saldanha", component: LocationPage },
  { path: "/long-distance-sandton", component: LocationPage },
  { path: "/long-distance-soweto", component: LocationPage },
  { path: "/long-distance-springs", component: LocationPage },
  { path: "/long-distance-standerton", component: LocationPage },
  { path: "/long-distance-thohoyandou", component: LocationPage },
  { path: "/long-distance-tzaneen", component: LocationPage },
  { path: "/long-distance-uitenhage", component: LocationPage },
  { path: "/long-distance-ulundi", component: LocationPage },
  { path: "/long-distance-umtata", component: LocationPage },
  { path: "/long-distance-upington", component: LocationPage },
  { path: "/long-distance-vanderbijlpark", component: LocationPage },
  { path: "/long-distance-vereeniging", component: LocationPage },
  { path: "/long-distance-volksrust", component: LocationPage },
  { path: "/long-distance-vredenburg", component: LocationPage },
  { path: "/long-distance-vryburg", component: LocationPage },
  { path: "/long-distance-vryheid", component: LocationPage },
  { path: "/long-distance-welkom", component: LocationPage },
  { path: "/long-distance-worcester", component: LocationPage },
  { path: "/mighty-ex8-alberton", component: LocationPage },
  { path: "/mighty-ex8-aliwal-north", component: LocationPage },
  { path: "/mighty-ex8-beaufort-west", component: LocationPage },
  { path: "/mighty-ex8-benoni", component: LocationPage },
  { path: "/mighty-ex8-bethal", component: LocationPage },
  { path: "/mighty-ex8-bhisho", component: LocationPage },
  { path: "/mighty-ex8-bloemfontein", component: LocationPage },
  { path: "/mighty-ex8-brakpan", component: LocationPage },
  { path: "/mighty-ex8-brits", component: LocationPage },
  { path: "/mighty-ex8-cape-town", component: LocationPage },
  { path: "/mighty-ex8-carletonville", component: LocationPage },
  { path: "/mighty-ex8-centurion", component: LocationPage },
  { path: "/mighty-ex8-cradock", component: LocationPage },
  { path: "/mighty-ex8-de-aar", component: LocationPage },
  { path: "/mighty-ex8-durban", component: LocationPage },
  { path: "/mighty-ex8-east-london", component: LocationPage },
  { path: "/mighty-ex8-ferndale", component: LocationPage },
  { path: "/mighty-ex8-george", component: LocationPage },
  { path: "/mighty-ex8-graaff-reinet", component: LocationPage },
  { path: "/mighty-ex8-grahamstown", component: LocationPage },
  { path: "/mighty-ex8-hermanus", component: LocationPage },
  { path: "/mighty-ex8-jeffreys-bay", component: LocationPage },
  { path: "/mighty-ex8-johannesburg", component: LocationPage },
  { path: "/mighty-ex8-kimberley", component: LocationPage },
  { path: "/mighty-ex8-klerksdorp", component: LocationPage },
  { path: "/mighty-ex8-knysna", component: LocationPage },
  { path: "/mighty-ex8-kroonstad", component: LocationPage },
  { path: "/mighty-ex8-kuilsrivier", component: LocationPage },
  { path: "/mighty-ex8-ladysmith", component: LocationPage },
  { path: "/mighty-ex8-lebowakgomo", component: LocationPage },
  { path: "/mighty-ex8-lichtenburg", component: LocationPage },
  { path: "/mighty-ex8-mahikeng", component: LocationPage },
  { path: "/mighty-ex8-malmesbury", component: LocationPage },
  { path: "/mighty-ex8-middelburg", component: LocationPage },
  { path: "/mighty-ex8-midrand", component: LocationPage },
  { path: "/mighty-ex8-mmabatho", component: LocationPage },
  { path: "/mighty-ex8-mossel-bay", component: LocationPage },
  { path: "/mighty-ex8-musina", component: LocationPage },
  { path: "/mighty-ex8-nelspruit", component: LocationPage },
  { path: "/mighty-ex8-oudtshoorn", component: LocationPage },
  { path: "/mighty-ex8-paarl", component: LocationPage },
  { path: "/mighty-ex8-pietermaritzburg", component: LocationPage },
  { path: "/mighty-ex8-polokwane", component: LocationPage },
  { path: "/mighty-ex8-port-elizabeth", component: LocationPage },
  { path: "/mighty-ex8-port-shepstone", component: LocationPage },
  { path: "/mighty-ex8-potchefstroom", component: LocationPage },
  { path: "/mighty-ex8-pretoria", component: LocationPage },
  { path: "/mighty-ex8-queenstown", component: LocationPage },
  { path: "/mighty-ex8-randburg", component: LocationPage },
  { path: "/mighty-ex8-roodepoort", component: LocationPage },
  { path: "/mighty-ex8-rustenburg", component: LocationPage },
  { path: "/mighty-ex8-saldanha", component: LocationPage },
  { path: "/mighty-ex8-sandton", component: LocationPage },
  { path: "/mighty-ex8-soweto", component: LocationPage },
  { path: "/mighty-ex8-springs", component: LocationPage },
  { path: "/mighty-ex8-standerton", component: LocationPage },
  { path: "/mighty-ex8-thohoyandou", component: LocationPage },
  { path: "/mighty-ex8-tzaneen", component: LocationPage },
  { path: "/mighty-ex8-uitenhage", component: LocationPage },
  { path: "/mighty-ex8-ulundi", component: LocationPage },
  { path: "/mighty-ex8-umtata", component: LocationPage },
  { path: "/mighty-ex8-upington", component: LocationPage },
  { path: "/mighty-ex8-vanderbijlpark", component: LocationPage },
  { path: "/mighty-ex8-vereeniging", component: LocationPage },
  { path: "/mighty-ex8-volksrust", component: LocationPage },
  { path: "/mighty-ex8-vredenburg", component: LocationPage },
  { path: "/mighty-ex8-vryburg", component: LocationPage },
  { path: "/mighty-ex8-vryheid", component: LocationPage },
  { path: "/mighty-ex8-welkom", component: LocationPage },
  { path: "/mighty-ex8-worcester", component: LocationPage },
  { path: "/moving-alberton", component: LocationPage },
  { path: "/moving-aliwal-north", component: LocationPage },
  { path: "/moving-beaufort-west", component: LocationPage },
  { path: "/moving-benoni", component: LocationPage },
  { path: "/moving-bethal", component: LocationPage },
  { path: "/moving-bhisho", component: LocationPage },
  { path: "/moving-bloemfontein", component: LocationPage },
  { path: "/moving-brakpan", component: LocationPage },
  { path: "/moving-brits", component: LocationPage },
  { path: "/moving-cape-town", component: LocationPage },
  { path: "/moving-carletonville", component: LocationPage },
  { path: "/moving-centurion", component: LocationPage },
  { path: "/moving-cradock", component: LocationPage },
  { path: "/moving-de-aar", component: LocationPage },
  { path: "/moving-durban", component: LocationPage },
  { path: "/moving-east-london", component: LocationPage },
  { path: "/moving-ferndale", component: LocationPage },
  { path: "/moving-george", component: LocationPage },
  { path: "/moving-graaff-reinet", component: LocationPage },
  { path: "/moving-grahamstown", component: LocationPage },
  { path: "/moving-hermanus", component: LocationPage },
  { path: "/moving-jeffreys-bay", component: LocationPage },
  { path: "/moving-johannesburg", component: LocationPage },
  { path: "/moving-kimberley", component: LocationPage },
  { path: "/moving-klerksdorp", component: LocationPage },
  { path: "/moving-knysna", component: LocationPage },
  { path: "/moving-kroonstad", component: LocationPage },
  { path: "/moving-kuilsrivier", component: LocationPage },
  { path: "/moving-ladysmith", component: LocationPage },
  { path: "/moving-lebowakgomo", component: LocationPage },
  { path: "/moving-lichtenburg", component: LocationPage },
  { path: "/moving-mahikeng", component: LocationPage },
  { path: "/moving-malmesbury", component: LocationPage },
  { path: "/moving-middelburg", component: LocationPage },
  { path: "/moving-midrand", component: LocationPage },
  { path: "/moving-mmabatho", component: LocationPage },
  { path: "/moving-mossel-bay", component: LocationPage },
  { path: "/moving-musina", component: LocationPage },
  { path: "/moving-nelspruit", component: LocationPage },
  { path: "/moving-oudtshoorn", component: LocationPage },
  { path: "/moving-out-alberton", component: LocationPage },
  { path: "/moving-out-aliwal-north", component: LocationPage },
  { path: "/moving-out-beaufort-west", component: LocationPage },
  { path: "/moving-out-benoni", component: LocationPage },
  { path: "/moving-out-bethal", component: LocationPage },
  { path: "/moving-out-bhisho", component: LocationPage },
  { path: "/moving-out-bloemfontein", component: LocationPage },
  { path: "/moving-out-brakpan", component: LocationPage },
  { path: "/moving-out-brits", component: LocationPage },
  { path: "/moving-out-cape-town", component: LocationPage },
  { path: "/moving-out-carletonville", component: LocationPage },
  { path: "/moving-out-centurion", component: LocationPage },
  { path: "/moving-out-cradock", component: LocationPage },
  { path: "/moving-out-de-aar", component: LocationPage },
  { path: "/moving-out-durban", component: LocationPage },
  { path: "/moving-out-east-london", component: LocationPage },
  { path: "/moving-out-ferndale", component: LocationPage },
  { path: "/moving-out-george", component: LocationPage },
  { path: "/moving-out-graaff-reinet", component: LocationPage },
  { path: "/moving-out-grahamstown", component: LocationPage },
  { path: "/moving-out-hermanus", component: LocationPage },
  { path: "/moving-out-jeffreys-bay", component: LocationPage },
  { path: "/moving-out-johannesburg", component: LocationPage },
  { path: "/moving-out-kimberley", component: LocationPage },
  { path: "/moving-out-klerksdorp", component: LocationPage },
  { path: "/moving-out-knysna", component: LocationPage },
  { path: "/moving-out-kroonstad", component: LocationPage },
  { path: "/moving-out-kuilsrivier", component: LocationPage },
  { path: "/moving-out-ladysmith", component: LocationPage },
  { path: "/moving-out-lebowakgomo", component: LocationPage },
  { path: "/moving-out-lichtenburg", component: LocationPage },
  { path: "/moving-out-mahikeng", component: LocationPage },
  { path: "/moving-out-malmesbury", component: LocationPage },
  { path: "/moving-out-middelburg", component: LocationPage },
  { path: "/moving-out-midrand", component: LocationPage },
  { path: "/moving-out-mmabatho", component: LocationPage },
  { path: "/moving-out-mossel-bay", component: LocationPage },
  { path: "/moving-out-musina", component: LocationPage },
  { path: "/moving-out-nelspruit", component: LocationPage },
  { path: "/moving-out-oudtshoorn", component: LocationPage },
  { path: "/moving-out-paarl", component: LocationPage },
  { path: "/moving-out-pietermaritzburg", component: LocationPage },
  { path: "/moving-out-polokwane", component: LocationPage },
  { path: "/moving-out-port-elizabeth", component: LocationPage },
  { path: "/moving-out-port-shepstone", component: LocationPage },
  { path: "/moving-out-potchefstroom", component: LocationPage },
  { path: "/moving-out-pretoria", component: LocationPage },
  { path: "/moving-out-queenstown", component: LocationPage },
  { path: "/moving-out-randburg", component: LocationPage },
  { path: "/moving-out-roodepoort", component: LocationPage },
  { path: "/moving-out-rustenburg", component: LocationPage },
  { path: "/moving-out-saldanha", component: LocationPage },
  { path: "/moving-out-sandton", component: LocationPage },
  { path: "/moving-out-soweto", component: LocationPage },
  { path: "/moving-out-springs", component: LocationPage },
  { path: "/moving-out-standerton", component: LocationPage },
  { path: "/moving-out-thohoyandou", component: LocationPage },
  { path: "/moving-out-tzaneen", component: LocationPage },
  { path: "/moving-out-uitenhage", component: LocationPage },
  { path: "/moving-out-ulundi", component: LocationPage },
  { path: "/moving-out-umtata", component: LocationPage },
  { path: "/moving-out-upington", component: LocationPage },
  { path: "/moving-out-vanderbijlpark", component: LocationPage },
  { path: "/moving-out-vereeniging", component: LocationPage },
  { path: "/moving-out-volksrust", component: LocationPage },
  { path: "/moving-out-vredenburg", component: LocationPage },
  { path: "/moving-out-vryburg", component: LocationPage },
  { path: "/moving-out-vryheid", component: LocationPage },
  { path: "/moving-out-welkom", component: LocationPage },
  { path: "/moving-out-worcester", component: LocationPage },
  { path: "/moving-paarl", component: LocationPage },
  { path: "/moving-pietermaritzburg", component: LocationPage },
  { path: "/moving-polokwane", component: LocationPage },
  { path: "/moving-port-elizabeth", component: LocationPage },
  { path: "/moving-port-shepstone", component: LocationPage },
  { path: "/moving-potchefstroom", component: LocationPage },
  { path: "/moving-pretoria", component: LocationPage },
  { path: "/moving-queenstown", component: LocationPage },
  { path: "/moving-randburg", component: LocationPage },
  { path: "/moving-roodepoort", component: LocationPage },
  { path: "/moving-rustenburg", component: LocationPage },
  { path: "/moving-saldanha", component: LocationPage },
  { path: "/moving-sandton", component: LocationPage },
  { path: "/moving-soweto", component: LocationPage },
  { path: "/moving-springs", component: LocationPage },
  { path: "/moving-standerton", component: LocationPage },
  { path: "/moving-thohoyandou", component: LocationPage },
  { path: "/moving-tzaneen", component: LocationPage },
  { path: "/moving-uitenhage", component: LocationPage },
  { path: "/moving-ulundi", component: LocationPage },
  { path: "/moving-umtata", component: LocationPage },
  { path: "/moving-upington", component: LocationPage },
  { path: "/moving-vanderbijlpark", component: LocationPage },
  { path: "/moving-vereeniging", component: LocationPage },
  { path: "/moving-volksrust", component: LocationPage },
  { path: "/moving-vredenburg", component: LocationPage },
  { path: "/moving-vryburg", component: LocationPage },
  { path: "/moving-vryheid", component: LocationPage },
  { path: "/moving-welkom", component: LocationPage },
  { path: "/moving-worcester", component: LocationPage },
  { path: "/need-delivery-alberton", component: LocationPage },
  { path: "/need-delivery-aliwal-north", component: LocationPage },
  { path: "/need-delivery-beaufort-west", component: LocationPage },
  { path: "/need-delivery-benoni", component: LocationPage },
  { path: "/need-delivery-bethal", component: LocationPage },
  { path: "/need-delivery-bhisho", component: LocationPage },
  { path: "/need-delivery-bloemfontein", component: LocationPage },
  { path: "/need-delivery-brakpan", component: LocationPage },
  { path: "/need-delivery-brits", component: LocationPage },
  { path: "/need-delivery-cape-town", component: LocationPage },
  { path: "/need-delivery-carletonville", component: LocationPage },
  { path: "/need-delivery-centurion", component: LocationPage },
  { path: "/need-delivery-cradock", component: LocationPage },
  { path: "/need-delivery-de-aar", component: LocationPage },
  { path: "/need-delivery-durban", component: LocationPage },
  { path: "/need-delivery-east-london", component: LocationPage },
  { path: "/need-delivery-ferndale", component: LocationPage },
  { path: "/need-delivery-george", component: LocationPage },
  { path: "/need-delivery-graaff-reinet", component: LocationPage },
  { path: "/need-delivery-grahamstown", component: LocationPage },
  { path: "/need-delivery-hermanus", component: LocationPage },
  { path: "/need-delivery-jeffreys-bay", component: LocationPage },
  { path: "/need-delivery-johannesburg", component: LocationPage },
  { path: "/need-delivery-kimberley", component: LocationPage },
  { path: "/need-delivery-klerksdorp", component: LocationPage },
  { path: "/need-delivery-knysna", component: LocationPage },
  { path: "/need-delivery-kroonstad", component: LocationPage },
  { path: "/need-delivery-kuilsrivier", component: LocationPage },
  { path: "/need-delivery-ladysmith", component: LocationPage },
  { path: "/need-delivery-lebowakgomo", component: LocationPage },
  { path: "/need-delivery-lichtenburg", component: LocationPage },
  { path: "/need-delivery-mahikeng", component: LocationPage },
  { path: "/need-delivery-malmesbury", component: LocationPage },
  { path: "/need-delivery-middelburg", component: LocationPage },
  { path: "/need-delivery-midrand", component: LocationPage },
  { path: "/need-delivery-mmabatho", component: LocationPage },
  { path: "/need-delivery-mossel-bay", component: LocationPage },
  { path: "/need-delivery-musina", component: LocationPage },
  { path: "/need-delivery-nelspruit", component: LocationPage },
  { path: "/need-delivery-oudtshoorn", component: LocationPage },
  { path: "/need-delivery-paarl", component: LocationPage },
  { path: "/need-delivery-pietermaritzburg", component: LocationPage },
  { path: "/need-delivery-polokwane", component: LocationPage },
  { path: "/need-delivery-port-elizabeth", component: LocationPage },
  { path: "/need-delivery-port-shepstone", component: LocationPage },
  { path: "/need-delivery-potchefstroom", component: LocationPage },
  { path: "/need-delivery-pretoria", component: LocationPage },
  { path: "/need-delivery-queenstown", component: LocationPage },
  { path: "/need-delivery-randburg", component: LocationPage },
  { path: "/need-delivery-roodepoort", component: LocationPage },
  { path: "/need-delivery-rustenburg", component: LocationPage },
  { path: "/need-delivery-saldanha", component: LocationPage },
  { path: "/need-delivery-sandton", component: LocationPage },
  { path: "/need-delivery-soweto", component: LocationPage },
  { path: "/need-delivery-springs", component: LocationPage },
  { path: "/need-delivery-standerton", component: LocationPage },
  { path: "/need-delivery-thohoyandou", component: LocationPage },
  { path: "/need-delivery-tzaneen", component: LocationPage },
  { path: "/need-delivery-uitenhage", component: LocationPage },
  { path: "/need-delivery-ulundi", component: LocationPage },
  { path: "/need-delivery-umtata", component: LocationPage },
  { path: "/need-delivery-upington", component: LocationPage },
  { path: "/need-delivery-vanderbijlpark", component: LocationPage },
  { path: "/need-delivery-vereeniging", component: LocationPage },
  { path: "/need-delivery-volksrust", component: LocationPage },
  { path: "/need-delivery-vredenburg", component: LocationPage },
  { path: "/need-delivery-vryburg", component: LocationPage },
  { path: "/need-delivery-vryheid", component: LocationPage },
  { path: "/need-delivery-welkom", component: LocationPage },
  { path: "/need-delivery-worcester", component: LocationPage },
  { path: "/one-way-truck-alberton", component: LocationPage },
  { path: "/one-way-truck-aliwal-north", component: LocationPage },
  { path: "/one-way-truck-beaufort-west", component: LocationPage },
  { path: "/one-way-truck-benoni", component: LocationPage },
  { path: "/one-way-truck-bethal", component: LocationPage },
  { path: "/one-way-truck-bhisho", component: LocationPage },
  { path: "/one-way-truck-bloemfontein", component: LocationPage },
  { path: "/one-way-truck-brakpan", component: LocationPage },
  { path: "/one-way-truck-brits", component: LocationPage },
  { path: "/one-way-truck-cape-town", component: LocationPage },
  { path: "/one-way-truck-carletonville", component: LocationPage },
  { path: "/one-way-truck-centurion", component: LocationPage },
  { path: "/one-way-truck-cradock", component: LocationPage },
  { path: "/one-way-truck-de-aar", component: LocationPage },
  { path: "/one-way-truck-durban", component: LocationPage },
  { path: "/one-way-truck-east-london", component: LocationPage },
  { path: "/one-way-truck-ferndale", component: LocationPage },
  { path: "/one-way-truck-george", component: LocationPage },
  { path: "/one-way-truck-graaff-reinet", component: LocationPage },
  { path: "/one-way-truck-grahamstown", component: LocationPage },
  { path: "/one-way-truck-hermanus", component: LocationPage },
  { path: "/one-way-truck-jeffreys-bay", component: LocationPage },
  { path: "/one-way-truck-johannesburg", component: LocationPage },
  { path: "/one-way-truck-kimberley", component: LocationPage },
  { path: "/one-way-truck-klerksdorp", component: LocationPage },
  { path: "/one-way-truck-knysna", component: LocationPage },
  { path: "/one-way-truck-kroonstad", component: LocationPage },
  { path: "/one-way-truck-kuilsrivier", component: LocationPage },
  { path: "/one-way-truck-ladysmith", component: LocationPage },
  { path: "/one-way-truck-lebowakgomo", component: LocationPage },
  { path: "/one-way-truck-lichtenburg", component: LocationPage },
  { path: "/one-way-truck-mahikeng", component: LocationPage },
  { path: "/one-way-truck-malmesbury", component: LocationPage },
  { path: "/one-way-truck-middelburg", component: LocationPage },
  { path: "/one-way-truck-midrand", component: LocationPage },
  { path: "/one-way-truck-mmabatho", component: LocationPage },
  { path: "/one-way-truck-mossel-bay", component: LocationPage },
  { path: "/one-way-truck-musina", component: LocationPage },
  { path: "/one-way-truck-nelspruit", component: LocationPage },
  { path: "/one-way-truck-oudtshoorn", component: LocationPage },
  { path: "/one-way-truck-paarl", component: LocationPage },
  { path: "/one-way-truck-pietermaritzburg", component: LocationPage },
  { path: "/one-way-truck-polokwane", component: LocationPage },
  { path: "/one-way-truck-port-elizabeth", component: LocationPage },
  { path: "/one-way-truck-port-shepstone", component: LocationPage },
  { path: "/one-way-truck-potchefstroom", component: LocationPage },
  { path: "/one-way-truck-pretoria", component: LocationPage },
  { path: "/one-way-truck-queenstown", component: LocationPage },
  { path: "/one-way-truck-randburg", component: LocationPage },
  { path: "/one-way-truck-roodepoort", component: LocationPage },
  { path: "/one-way-truck-rustenburg", component: LocationPage },
  { path: "/one-way-truck-saldanha", component: LocationPage },
  { path: "/one-way-truck-sandton", component: LocationPage },
  { path: "/one-way-truck-soweto", component: LocationPage },
  { path: "/one-way-truck-springs", component: LocationPage },
  { path: "/one-way-truck-standerton", component: LocationPage },
  { path: "/one-way-truck-thohoyandou", component: LocationPage },
  { path: "/one-way-truck-tzaneen", component: LocationPage },
  { path: "/one-way-truck-uitenhage", component: LocationPage },
  { path: "/one-way-truck-ulundi", component: LocationPage },
  { path: "/one-way-truck-umtata", component: LocationPage },
  { path: "/one-way-truck-upington", component: LocationPage },
  { path: "/one-way-truck-vanderbijlpark", component: LocationPage },
  { path: "/one-way-truck-vereeniging", component: LocationPage },
  { path: "/one-way-truck-volksrust", component: LocationPage },
  { path: "/one-way-truck-vredenburg", component: LocationPage },
  { path: "/one-way-truck-vryburg", component: LocationPage },
  { path: "/one-way-truck-vryheid", component: LocationPage },
  { path: "/one-way-truck-welkom", component: LocationPage },
  { path: "/one-way-truck-worcester", component: LocationPage },
  { path: "/online-order-delivery-alberton", component: LocationPage },
  { path: "/online-order-delivery-aliwal-north", component: LocationPage },
  { path: "/online-order-delivery-beaufort-west", component: LocationPage },
  { path: "/online-order-delivery-benoni", component: LocationPage },
  { path: "/online-order-delivery-bethal", component: LocationPage },
  { path: "/online-order-delivery-bhisho", component: LocationPage },
  { path: "/online-order-delivery-bloemfontein", component: LocationPage },
  { path: "/online-order-delivery-brakpan", component: LocationPage },
  { path: "/online-order-delivery-brits", component: LocationPage },
  { path: "/online-order-delivery-cape-town", component: LocationPage },
  { path: "/online-order-delivery-carletonville", component: LocationPage },
  { path: "/online-order-delivery-centurion", component: LocationPage },
  { path: "/online-order-delivery-cradock", component: LocationPage },
  { path: "/online-order-delivery-de-aar", component: LocationPage },
  { path: "/online-order-delivery-durban", component: LocationPage },
  { path: "/online-order-delivery-east-london", component: LocationPage },
  { path: "/online-order-delivery-ferndale", component: LocationPage },
  { path: "/online-order-delivery-george", component: LocationPage },
  { path: "/online-order-delivery-graaff-reinet", component: LocationPage },
  { path: "/online-order-delivery-grahamstown", component: LocationPage },
  { path: "/online-order-delivery-hermanus", component: LocationPage },
  { path: "/online-order-delivery-jeffreys-bay", component: LocationPage },
  { path: "/online-order-delivery-johannesburg", component: LocationPage },
  { path: "/online-order-delivery-kimberley", component: LocationPage },
  { path: "/online-order-delivery-klerksdorp", component: LocationPage },
  { path: "/online-order-delivery-knysna", component: LocationPage },
  { path: "/online-order-delivery-kroonstad", component: LocationPage },
  { path: "/online-order-delivery-kuilsrivier", component: LocationPage },
  { path: "/online-order-delivery-ladysmith", component: LocationPage },
  { path: "/online-order-delivery-lebowakgomo", component: LocationPage },
  { path: "/online-order-delivery-lichtenburg", component: LocationPage },
  { path: "/online-order-delivery-mahikeng", component: LocationPage },
  { path: "/online-order-delivery-malmesbury", component: LocationPage },
  { path: "/online-order-delivery-middelburg", component: LocationPage },
  { path: "/online-order-delivery-midrand", component: LocationPage },
  { path: "/online-order-delivery-mmabatho", component: LocationPage },
  { path: "/online-order-delivery-mossel-bay", component: LocationPage },
  { path: "/online-order-delivery-musina", component: LocationPage },
  { path: "/online-order-delivery-nelspruit", component: LocationPage },
  { path: "/online-order-delivery-oudtshoorn", component: LocationPage },
  { path: "/online-order-delivery-paarl", component: LocationPage },
  { path: "/online-order-delivery-pietermaritzburg", component: LocationPage },
  { path: "/online-order-delivery-polokwane", component: LocationPage },
  { path: "/online-order-delivery-port-elizabeth", component: LocationPage },
  { path: "/online-order-delivery-port-shepstone", component: LocationPage },
  { path: "/online-order-delivery-potchefstroom", component: LocationPage },
  { path: "/online-order-delivery-pretoria", component: LocationPage },
  { path: "/online-order-delivery-queenstown", component: LocationPage },
  { path: "/online-order-delivery-randburg", component: LocationPage },
  { path: "/online-order-delivery-roodepoort", component: LocationPage },
  { path: "/online-order-delivery-rustenburg", component: LocationPage },
  { path: "/online-order-delivery-saldanha", component: LocationPage },
  { path: "/online-order-delivery-sandton", component: LocationPage },
  { path: "/online-order-delivery-soweto", component: LocationPage },
  { path: "/online-order-delivery-springs", component: LocationPage },
  { path: "/online-order-delivery-standerton", component: LocationPage },
  { path: "/online-order-delivery-thohoyandou", component: LocationPage },
  { path: "/online-order-delivery-tzaneen", component: LocationPage },
  { path: "/online-order-delivery-uitenhage", component: LocationPage },
  { path: "/online-order-delivery-ulundi", component: LocationPage },
  { path: "/online-order-delivery-umtata", component: LocationPage },
  { path: "/online-order-delivery-upington", component: LocationPage },
  { path: "/online-order-delivery-vanderbijlpark", component: LocationPage },
  { path: "/online-order-delivery-vereeniging", component: LocationPage },
  { path: "/online-order-delivery-volksrust", component: LocationPage },
  { path: "/online-order-delivery-vredenburg", component: LocationPage },
  { path: "/online-order-delivery-vryburg", component: LocationPage },
  { path: "/online-order-delivery-vryheid", component: LocationPage },
  { path: "/online-order-delivery-welkom", component: LocationPage },
  { path: "/online-order-delivery-worcester", component: LocationPage },
  { path: "/overnight-delivery-alberton", component: LocationPage },
  { path: "/overnight-delivery-aliwal-north", component: LocationPage },
  { path: "/overnight-delivery-beaufort-west", component: LocationPage },
  { path: "/overnight-delivery-benoni", component: LocationPage },
  { path: "/overnight-delivery-bethal", component: LocationPage },
  { path: "/overnight-delivery-bhisho", component: LocationPage },
  { path: "/overnight-delivery-bloemfontein", component: LocationPage },
  { path: "/overnight-delivery-brakpan", component: LocationPage },
  { path: "/overnight-delivery-brits", component: LocationPage },
  { path: "/overnight-delivery-cape-town", component: LocationPage },
  { path: "/overnight-delivery-carletonville", component: LocationPage },
  { path: "/overnight-delivery-centurion", component: LocationPage },
  { path: "/overnight-delivery-cradock", component: LocationPage },
  { path: "/overnight-delivery-de-aar", component: LocationPage },
  { path: "/overnight-delivery-durban", component: LocationPage },
  { path: "/overnight-delivery-east-london", component: LocationPage },
  { path: "/overnight-delivery-ferndale", component: LocationPage },
  { path: "/overnight-delivery-george", component: LocationPage },
  { path: "/overnight-delivery-graaff-reinet", component: LocationPage },
  { path: "/overnight-delivery-grahamstown", component: LocationPage },
  { path: "/overnight-delivery-hermanus", component: LocationPage },
  { path: "/overnight-delivery-jeffreys-bay", component: LocationPage },
  { path: "/overnight-delivery-johannesburg", component: LocationPage },
  { path: "/overnight-delivery-kimberley", component: LocationPage },
  { path: "/overnight-delivery-klerksdorp", component: LocationPage },
  { path: "/overnight-delivery-knysna", component: LocationPage },
  { path: "/overnight-delivery-kroonstad", component: LocationPage },
  { path: "/overnight-delivery-kuilsrivier", component: LocationPage },
  { path: "/overnight-delivery-ladysmith", component: LocationPage },
  { path: "/overnight-delivery-lebowakgomo", component: LocationPage },
  { path: "/overnight-delivery-lichtenburg", component: LocationPage },
  { path: "/overnight-delivery-mahikeng", component: LocationPage },
  { path: "/overnight-delivery-malmesbury", component: LocationPage },
  { path: "/overnight-delivery-middelburg", component: LocationPage },
  { path: "/overnight-delivery-midrand", component: LocationPage },
  { path: "/overnight-delivery-mmabatho", component: LocationPage },
  { path: "/overnight-delivery-mossel-bay", component: LocationPage },
  { path: "/overnight-delivery-musina", component: LocationPage },
  { path: "/overnight-delivery-nelspruit", component: LocationPage },
  { path: "/overnight-delivery-oudtshoorn", component: LocationPage },
  { path: "/overnight-delivery-paarl", component: LocationPage },
  { path: "/overnight-delivery-pietermaritzburg", component: LocationPage },
  { path: "/overnight-delivery-polokwane", component: LocationPage },
  { path: "/overnight-delivery-port-elizabeth", component: LocationPage },
  { path: "/overnight-delivery-port-shepstone", component: LocationPage },
  { path: "/overnight-delivery-potchefstroom", component: LocationPage },
  { path: "/overnight-delivery-pretoria", component: LocationPage },
  { path: "/overnight-delivery-queenstown", component: LocationPage },
  { path: "/overnight-delivery-randburg", component: LocationPage },
  { path: "/overnight-delivery-roodepoort", component: LocationPage },
  { path: "/overnight-delivery-rustenburg", component: LocationPage },
  { path: "/overnight-delivery-saldanha", component: LocationPage },
  { path: "/overnight-delivery-sandton", component: LocationPage },
  { path: "/overnight-delivery-soweto", component: LocationPage },
  { path: "/overnight-delivery-springs", component: LocationPage },
  { path: "/overnight-delivery-standerton", component: LocationPage },
  { path: "/overnight-delivery-thohoyandou", component: LocationPage },
  { path: "/overnight-delivery-tzaneen", component: LocationPage },
  { path: "/overnight-delivery-uitenhage", component: LocationPage },
  { path: "/overnight-delivery-ulundi", component: LocationPage },
  { path: "/overnight-delivery-umtata", component: LocationPage },
  { path: "/overnight-delivery-upington", component: LocationPage },
  { path: "/overnight-delivery-vanderbijlpark", component: LocationPage },
  { path: "/overnight-delivery-vereeniging", component: LocationPage },
  { path: "/overnight-delivery-volksrust", component: LocationPage },
  { path: "/overnight-delivery-vredenburg", component: LocationPage },
  { path: "/overnight-delivery-vryburg", component: LocationPage },
  { path: "/overnight-delivery-vryheid", component: LocationPage },
  { path: "/overnight-delivery-welkom", component: LocationPage },
  { path: "/overnight-delivery-worcester", component: LocationPage },
  { path: "/package-delivery-alberton", component: LocationPage },
  { path: "/package-delivery-aliwal-north", component: LocationPage },
  { path: "/package-delivery-beaufort-west", component: LocationPage },
  { path: "/package-delivery-benoni", component: LocationPage },
  { path: "/package-delivery-bethal", component: LocationPage },
  { path: "/package-delivery-bhisho", component: LocationPage },
  { path: "/package-delivery-bloemfontein", component: LocationPage },
  { path: "/package-delivery-brakpan", component: LocationPage },
  { path: "/package-delivery-brits", component: LocationPage },
  { path: "/package-delivery-cape-town", component: LocationPage },
  { path: "/package-delivery-carletonville", component: LocationPage },
  { path: "/package-delivery-centurion", component: LocationPage },
  { path: "/package-delivery-cradock", component: LocationPage },
  { path: "/package-delivery-de-aar", component: LocationPage },
  { path: "/package-delivery-durban", component: LocationPage },
  { path: "/package-delivery-east-london", component: LocationPage },
  { path: "/package-delivery-ferndale", component: LocationPage },
  { path: "/package-delivery-george", component: LocationPage },
  { path: "/package-delivery-graaff-reinet", component: LocationPage },
  { path: "/package-delivery-grahamstown", component: LocationPage },
  { path: "/package-delivery-hermanus", component: LocationPage },
  { path: "/package-delivery-jeffreys-bay", component: LocationPage },
  { path: "/package-delivery-johannesburg", component: LocationPage },
  { path: "/package-delivery-kimberley", component: LocationPage },
  { path: "/package-delivery-klerksdorp", component: LocationPage },
  { path: "/package-delivery-knysna", component: LocationPage },
  { path: "/package-delivery-kroonstad", component: LocationPage },
  { path: "/package-delivery-kuilsrivier", component: LocationPage },
  { path: "/package-delivery-ladysmith", component: LocationPage },
  { path: "/package-delivery-lebowakgomo", component: LocationPage },
  { path: "/package-delivery-lichtenburg", component: LocationPage },
  { path: "/package-delivery-mahikeng", component: LocationPage },
  { path: "/package-delivery-malmesbury", component: LocationPage },
  { path: "/package-delivery-middelburg", component: LocationPage },
  { path: "/package-delivery-midrand", component: LocationPage },
  { path: "/package-delivery-mmabatho", component: LocationPage },
  { path: "/package-delivery-mossel-bay", component: LocationPage },
  { path: "/package-delivery-musina", component: LocationPage },
  { path: "/package-delivery-nelspruit", component: LocationPage },
  { path: "/package-delivery-oudtshoorn", component: LocationPage },
  { path: "/package-delivery-paarl", component: LocationPage },
  { path: "/package-delivery-pietermaritzburg", component: LocationPage },
  { path: "/package-delivery-polokwane", component: LocationPage },
  { path: "/package-delivery-port-elizabeth", component: LocationPage },
  { path: "/package-delivery-port-shepstone", component: LocationPage },
  { path: "/package-delivery-potchefstroom", component: LocationPage },
  { path: "/package-delivery-pretoria", component: LocationPage },
  { path: "/package-delivery-queenstown", component: LocationPage },
  { path: "/package-delivery-randburg", component: LocationPage },
  { path: "/package-delivery-roodepoort", component: LocationPage },
  { path: "/package-delivery-rustenburg", component: LocationPage },
  { path: "/package-delivery-saldanha", component: LocationPage },
  { path: "/package-delivery-sandton", component: LocationPage },
  { path: "/package-delivery-soweto", component: LocationPage },
  { path: "/package-delivery-springs", component: LocationPage },
  { path: "/package-delivery-standerton", component: LocationPage },
  { path: "/package-delivery-thohoyandou", component: LocationPage },
  { path: "/package-delivery-tzaneen", component: LocationPage },
  { path: "/package-delivery-uitenhage", component: LocationPage },
  { path: "/package-delivery-ulundi", component: LocationPage },
  { path: "/package-delivery-umtata", component: LocationPage },
  { path: "/package-delivery-upington", component: LocationPage },
  { path: "/package-delivery-vanderbijlpark", component: LocationPage },
  { path: "/package-delivery-vereeniging", component: LocationPage },
  { path: "/package-delivery-volksrust", component: LocationPage },
  { path: "/package-delivery-vredenburg", component: LocationPage },
  { path: "/package-delivery-vryburg", component: LocationPage },
  { path: "/package-delivery-vryheid", component: LocationPage },
  { path: "/package-delivery-welkom", component: LocationPage },
  { path: "/package-delivery-worcester", component: LocationPage },
  { path: "/quick-truck-hire-alberton", component: LocationPage },
  { path: "/quick-truck-hire-aliwal-north", component: LocationPage },
  { path: "/quick-truck-hire-beaufort-west", component: LocationPage },
  { path: "/quick-truck-hire-benoni", component: LocationPage },
  { path: "/quick-truck-hire-bethal", component: LocationPage },
  { path: "/quick-truck-hire-bhisho", component: LocationPage },
  { path: "/quick-truck-hire-bloemfontein", component: LocationPage },
  { path: "/quick-truck-hire-brakpan", component: LocationPage },
  { path: "/quick-truck-hire-brits", component: LocationPage },
  { path: "/quick-truck-hire-cape-town", component: LocationPage },
  { path: "/quick-truck-hire-carletonville", component: LocationPage },
  { path: "/quick-truck-hire-centurion", component: LocationPage },
  { path: "/quick-truck-hire-cradock", component: LocationPage },
  { path: "/quick-truck-hire-de-aar", component: LocationPage },
  { path: "/quick-truck-hire-durban", component: LocationPage },
  { path: "/quick-truck-hire-east-london", component: LocationPage },
  { path: "/quick-truck-hire-ferndale", component: LocationPage },
  { path: "/quick-truck-hire-george", component: LocationPage },
  { path: "/quick-truck-hire-graaff-reinet", component: LocationPage },
  { path: "/quick-truck-hire-grahamstown", component: LocationPage },
  { path: "/quick-truck-hire-hermanus", component: LocationPage },
  { path: "/quick-truck-hire-jeffreys-bay", component: LocationPage },
  { path: "/quick-truck-hire-johannesburg", component: LocationPage },
  { path: "/quick-truck-hire-kimberley", component: LocationPage },
  { path: "/quick-truck-hire-klerksdorp", component: LocationPage },
  { path: "/quick-truck-hire-knysna", component: LocationPage },
  { path: "/quick-truck-hire-kroonstad", component: LocationPage },
  { path: "/quick-truck-hire-kuilsrivier", component: LocationPage },
  { path: "/quick-truck-hire-ladysmith", component: LocationPage },
  { path: "/quick-truck-hire-lebowakgomo", component: LocationPage },
  { path: "/quick-truck-hire-lichtenburg", component: LocationPage },
  { path: "/quick-truck-hire-mahikeng", component: LocationPage },
  { path: "/quick-truck-hire-malmesbury", component: LocationPage },
  { path: "/quick-truck-hire-middelburg", component: LocationPage },
  { path: "/quick-truck-hire-midrand", component: LocationPage },
  { path: "/quick-truck-hire-mmabatho", component: LocationPage },
  { path: "/quick-truck-hire-mossel-bay", component: LocationPage },
  { path: "/quick-truck-hire-musina", component: LocationPage },
  { path: "/quick-truck-hire-nelspruit", component: LocationPage },
  { path: "/quick-truck-hire-oudtshoorn", component: LocationPage },
  { path: "/quick-truck-hire-paarl", component: LocationPage },
  { path: "/quick-truck-hire-pietermaritzburg", component: LocationPage },
  { path: "/quick-truck-hire-polokwane", component: LocationPage },
  { path: "/quick-truck-hire-port-elizabeth", component: LocationPage },
  { path: "/quick-truck-hire-port-shepstone", component: LocationPage },
  { path: "/quick-truck-hire-potchefstroom", component: LocationPage },
  { path: "/quick-truck-hire-pretoria", component: LocationPage },
  { path: "/quick-truck-hire-queenstown", component: LocationPage },
  { path: "/quick-truck-hire-randburg", component: LocationPage },
  { path: "/quick-truck-hire-roodepoort", component: LocationPage },
  { path: "/quick-truck-hire-rustenburg", component: LocationPage },
  { path: "/quick-truck-hire-saldanha", component: LocationPage },
  { path: "/quick-truck-hire-sandton", component: LocationPage },
  { path: "/quick-truck-hire-soweto", component: LocationPage },
  { path: "/quick-truck-hire-springs", component: LocationPage },
  { path: "/quick-truck-hire-standerton", component: LocationPage },
  { path: "/quick-truck-hire-thohoyandou", component: LocationPage },
  { path: "/quick-truck-hire-tzaneen", component: LocationPage },
  { path: "/quick-truck-hire-uitenhage", component: LocationPage },
  { path: "/quick-truck-hire-ulundi", component: LocationPage },
  { path: "/quick-truck-hire-umtata", component: LocationPage },
  { path: "/quick-truck-hire-upington", component: LocationPage },
  { path: "/quick-truck-hire-vanderbijlpark", component: LocationPage },
  { path: "/quick-truck-hire-vereeniging", component: LocationPage },
  { path: "/quick-truck-hire-volksrust", component: LocationPage },
  { path: "/quick-truck-hire-vredenburg", component: LocationPage },
  { path: "/quick-truck-hire-vryburg", component: LocationPage },
  { path: "/quick-truck-hire-vryheid", component: LocationPage },
  { path: "/quick-truck-hire-welkom", component: LocationPage },
  { path: "/quick-truck-hire-worcester", component: LocationPage },
  { path: "/quick-truck-rental-alberton", component: LocationPage },
  { path: "/quick-truck-rental-aliwal-north", component: LocationPage },
  { path: "/quick-truck-rental-beaufort-west", component: LocationPage },
  { path: "/quick-truck-rental-benoni", component: LocationPage },
  { path: "/quick-truck-rental-bethal", component: LocationPage },
  { path: "/quick-truck-rental-bhisho", component: LocationPage },
  { path: "/quick-truck-rental-bloemfontein", component: LocationPage },
  { path: "/quick-truck-rental-brakpan", component: LocationPage },
  { path: "/quick-truck-rental-brits", component: LocationPage },
  { path: "/quick-truck-rental-cape-town", component: LocationPage },
  { path: "/quick-truck-rental-carletonville", component: LocationPage },
  { path: "/quick-truck-rental-centurion", component: LocationPage },
  { path: "/quick-truck-rental-cradock", component: LocationPage },
  { path: "/quick-truck-rental-de-aar", component: LocationPage },
  { path: "/quick-truck-rental-durban", component: LocationPage },
  { path: "/quick-truck-rental-east-london", component: LocationPage },
  { path: "/quick-truck-rental-ferndale", component: LocationPage },
  { path: "/quick-truck-rental-george", component: LocationPage },
  { path: "/quick-truck-rental-graaff-reinet", component: LocationPage },
  { path: "/quick-truck-rental-grahamstown", component: LocationPage },
  { path: "/quick-truck-rental-hermanus", component: LocationPage },
  { path: "/quick-truck-rental-jeffreys-bay", component: LocationPage },
  { path: "/quick-truck-rental-johannesburg", component: LocationPage },
  { path: "/quick-truck-rental-kimberley", component: LocationPage },
  { path: "/quick-truck-rental-klerksdorp", component: LocationPage },
  { path: "/quick-truck-rental-knysna", component: LocationPage },
  { path: "/quick-truck-rental-kroonstad", component: LocationPage },
  { path: "/quick-truck-rental-kuilsrivier", component: LocationPage },
  { path: "/quick-truck-rental-ladysmith", component: LocationPage },
  { path: "/quick-truck-rental-lebowakgomo", component: LocationPage },
  { path: "/quick-truck-rental-lichtenburg", component: LocationPage },
  { path: "/quick-truck-rental-mahikeng", component: LocationPage },
  { path: "/quick-truck-rental-malmesbury", component: LocationPage },
  { path: "/quick-truck-rental-middelburg", component: LocationPage },
  { path: "/quick-truck-rental-midrand", component: LocationPage },
  { path: "/quick-truck-rental-mmabatho", component: LocationPage },
  { path: "/quick-truck-rental-mossel-bay", component: LocationPage },
  { path: "/quick-truck-rental-musina", component: LocationPage },
  { path: "/quick-truck-rental-nelspruit", component: LocationPage },
  { path: "/quick-truck-rental-oudtshoorn", component: LocationPage },
  { path: "/quick-truck-rental-paarl", component: LocationPage },
  { path: "/quick-truck-rental-pietermaritzburg", component: LocationPage },
  { path: "/quick-truck-rental-polokwane", component: LocationPage },
  { path: "/quick-truck-rental-port-elizabeth", component: LocationPage },
  { path: "/quick-truck-rental-port-shepstone", component: LocationPage },
  { path: "/quick-truck-rental-potchefstroom", component: LocationPage },
  { path: "/quick-truck-rental-pretoria", component: LocationPage },
  { path: "/quick-truck-rental-queenstown", component: LocationPage },
  { path: "/quick-truck-rental-randburg", component: LocationPage },
  { path: "/quick-truck-rental-roodepoort", component: LocationPage },
  { path: "/quick-truck-rental-rustenburg", component: LocationPage },
  { path: "/quick-truck-rental-saldanha", component: LocationPage },
  { path: "/quick-truck-rental-sandton", component: LocationPage },
  { path: "/quick-truck-rental-soweto", component: LocationPage },
  { path: "/quick-truck-rental-springs", component: LocationPage },
  { path: "/quick-truck-rental-standerton", component: LocationPage },
  { path: "/quick-truck-rental-thohoyandou", component: LocationPage },
  { path: "/quick-truck-rental-tzaneen", component: LocationPage },
  { path: "/quick-truck-rental-uitenhage", component: LocationPage },
  { path: "/quick-truck-rental-ulundi", component: LocationPage },
  { path: "/quick-truck-rental-umtata", component: LocationPage },
  { path: "/quick-truck-rental-upington", component: LocationPage },
  { path: "/quick-truck-rental-vanderbijlpark", component: LocationPage },
  { path: "/quick-truck-rental-vereeniging", component: LocationPage },
  { path: "/quick-truck-rental-volksrust", component: LocationPage },
  { path: "/quick-truck-rental-vredenburg", component: LocationPage },
  { path: "/quick-truck-rental-vryburg", component: LocationPage },
  { path: "/quick-truck-rental-vryheid", component: LocationPage },
  { path: "/quick-truck-rental-welkom", component: LocationPage },
  { path: "/quick-truck-rental-worcester", component: LocationPage },
  { path: "/regular-deliveries-johannesburg", component: LocationPage },
  { path: "/removals-alberton", component: LocationPage },
  { path: "/removals-aliwal-north", component: LocationPage },
  { path: "/removals-beaufort-west", component: LocationPage },
  { path: "/removals-benoni", component: LocationPage },
  { path: "/removals-bethal", component: LocationPage },
  { path: "/removals-bhisho", component: LocationPage },
  { path: "/removals-bloemfontein", component: LocationPage },
  { path: "/removals-brakpan", component: LocationPage },
  { path: "/removals-brits", component: LocationPage },
  { path: "/removals-cape-town", component: LocationPage },
  { path: "/removals-carletonville", component: LocationPage },
  { path: "/removals-centurion", component: LocationPage },
  { path: "/removals-cradock", component: LocationPage },
  { path: "/removals-de-aar", component: LocationPage },
  { path: "/removals-durban", component: LocationPage },
  { path: "/removals-east-london", component: LocationPage },
  { path: "/removals-ferndale", component: LocationPage },
  { path: "/removals-george", component: LocationPage },
  { path: "/removals-graaff-reinet", component: LocationPage },
  { path: "/removals-grahamstown", component: LocationPage },
  { path: "/removals-hermanus", component: LocationPage },
  { path: "/removals-jeffreys-bay", component: LocationPage },
  { path: "/removals-johannesburg", component: LocationPage },
  { path: "/removals-kimberley", component: LocationPage },
  { path: "/removals-klerksdorp", component: LocationPage },
  { path: "/removals-knysna", component: LocationPage },
  { path: "/removals-kroonstad", component: LocationPage },
  { path: "/removals-kuilsrivier", component: LocationPage },
  { path: "/removals-ladysmith", component: LocationPage },
  { path: "/removals-lebowakgomo", component: LocationPage },
  { path: "/removals-lichtenburg", component: LocationPage },
  { path: "/removals-mahikeng", component: LocationPage },
  { path: "/removals-malmesbury", component: LocationPage },
  { path: "/removals-middelburg", component: LocationPage },
  { path: "/removals-midrand", component: LocationPage },
  { path: "/removals-mmabatho", component: LocationPage },
  { path: "/removals-mossel-bay", component: LocationPage },
  { path: "/removals-musina", component: LocationPage },
  { path: "/removals-nelspruit", component: LocationPage },
  { path: "/removals-oudtshoorn", component: LocationPage },
  { path: "/removals-paarl", component: LocationPage },
  { path: "/removals-pietermaritzburg", component: LocationPage },
  { path: "/removals-polokwane", component: LocationPage },
  { path: "/removals-port-elizabeth", component: LocationPage },
  { path: "/removals-port-shepstone", component: LocationPage },
  { path: "/removals-potchefstroom", component: LocationPage },
  { path: "/removals-pretoria", component: LocationPage },
  { path: "/removals-queenstown", component: LocationPage },
  { path: "/removals-randburg", component: LocationPage },
  { path: "/removals-roodepoort", component: LocationPage },
  { path: "/removals-rustenburg", component: LocationPage },
  { path: "/removals-saldanha", component: LocationPage },
  { path: "/removals-sandton", component: LocationPage },
  { path: "/removals-soweto", component: LocationPage },
  { path: "/removals-springs", component: LocationPage },
  { path: "/removals-standerton", component: LocationPage },
  { path: "/removals-thohoyandou", component: LocationPage },
  { path: "/removals-tzaneen", component: LocationPage },
  { path: "/removals-uitenhage", component: LocationPage },
  { path: "/removals-ulundi", component: LocationPage },
  { path: "/removals-umtata", component: LocationPage },
  { path: "/removals-upington", component: LocationPage },
  { path: "/removals-vanderbijlpark", component: LocationPage },
  { path: "/removals-vereeniging", component: LocationPage },
  { path: "/removals-volksrust", component: LocationPage },
  { path: "/removals-vredenburg", component: LocationPage },
  { path: "/removals-vryburg", component: LocationPage },
  { path: "/removals-vryheid", component: LocationPage },
  { path: "/removals-welkom", component: LocationPage },
  { path: "/removals-worcester", component: LocationPage },
  { path: "/rent-a-bakkie-alberton", component: LocationPage },
  { path: "/rent-a-bakkie-aliwal-north", component: LocationPage },
  { path: "/rent-a-bakkie-beaufort-west", component: LocationPage },
  { path: "/rent-a-bakkie-benoni", component: LocationPage },
  { path: "/rent-a-bakkie-bethal", component: LocationPage },
  { path: "/rent-a-bakkie-bhisho", component: LocationPage },
  { path: "/rent-a-bakkie-bloemfontein", component: LocationPage },
  { path: "/rent-a-bakkie-brakpan", component: LocationPage },
  { path: "/rent-a-bakkie-brits", component: LocationPage },
  { path: "/rent-a-bakkie-cape-town", component: LocationPage },
  { path: "/rent-a-bakkie-carletonville", component: LocationPage },
  { path: "/rent-a-bakkie-centurion", component: LocationPage },
  { path: "/rent-a-bakkie-cradock", component: LocationPage },
  { path: "/rent-a-bakkie-de-aar", component: LocationPage },
  { path: "/rent-a-bakkie-durban", component: LocationPage },
  { path: "/rent-a-bakkie-east-london", component: LocationPage },
  { path: "/rent-a-bakkie-ferndale", component: LocationPage },
  { path: "/rent-a-bakkie-george", component: LocationPage },
  { path: "/rent-a-bakkie-graaff-reinet", component: LocationPage },
  { path: "/rent-a-bakkie-grahamstown", component: LocationPage },
  { path: "/rent-a-bakkie-hermanus", component: LocationPage },
  { path: "/rent-a-bakkie-jeffreys-bay", component: LocationPage },
  { path: "/rent-a-bakkie-johannesburg", component: LocationPage },
  { path: "/rent-a-bakkie-kimberley", component: LocationPage },
  { path: "/rent-a-bakkie-klerksdorp", component: LocationPage },
  { path: "/rent-a-bakkie-knysna", component: LocationPage },
  { path: "/rent-a-bakkie-kroonstad", component: LocationPage },
  { path: "/rent-a-bakkie-kuilsrivier", component: LocationPage },
  { path: "/rent-a-bakkie-ladysmith", component: LocationPage },
  { path: "/rent-a-bakkie-lebowakgomo", component: LocationPage },
  { path: "/rent-a-bakkie-lichtenburg", component: LocationPage },
  { path: "/rent-a-bakkie-mahikeng", component: LocationPage },
  { path: "/rent-a-bakkie-malmesbury", component: LocationPage },
  { path: "/rent-a-bakkie-middelburg", component: LocationPage },
  { path: "/rent-a-bakkie-midrand", component: LocationPage },
  { path: "/rent-a-bakkie-mmabatho", component: LocationPage },
  { path: "/rent-a-bakkie-mossel-bay", component: LocationPage },
  { path: "/rent-a-bakkie-musina", component: LocationPage },
  { path: "/rent-a-bakkie-nelspruit", component: LocationPage },
  { path: "/rent-a-bakkie-oudtshoorn", component: LocationPage },
  { path: "/rent-a-bakkie-paarl", component: LocationPage },
  { path: "/rent-a-bakkie-pietermaritzburg", component: LocationPage },
  { path: "/rent-a-bakkie-polokwane", component: LocationPage },
  { path: "/rent-a-bakkie-port-elizabeth", component: LocationPage },
  { path: "/rent-a-bakkie-port-shepstone", component: LocationPage },
  { path: "/rent-a-bakkie-potchefstroom", component: LocationPage },
  { path: "/rent-a-bakkie-pretoria", component: LocationPage },
  { path: "/rent-a-bakkie-queenstown", component: LocationPage },
  { path: "/rent-a-bakkie-randburg", component: LocationPage },
  { path: "/rent-a-bakkie-roodepoort", component: LocationPage },
  { path: "/rent-a-bakkie-rustenburg", component: LocationPage },
  { path: "/rent-a-bakkie-saldanha", component: LocationPage },
  { path: "/rent-a-bakkie-sandton", component: LocationPage },
  { path: "/rent-a-bakkie-soweto", component: LocationPage },
  { path: "/rent-a-bakkie-springs", component: LocationPage },
  { path: "/rent-a-bakkie-standerton", component: LocationPage },
  { path: "/rent-a-bakkie-thohoyandou", component: LocationPage },
  { path: "/rent-a-bakkie-tzaneen", component: LocationPage },
  { path: "/rent-a-bakkie-uitenhage", component: LocationPage },
  { path: "/rent-a-bakkie-ulundi", component: LocationPage },
  { path: "/rent-a-bakkie-umtata", component: LocationPage },
  { path: "/rent-a-bakkie-upington", component: LocationPage },
  { path: "/rent-a-bakkie-vanderbijlpark", component: LocationPage },
  { path: "/rent-a-bakkie-vereeniging", component: LocationPage },
  { path: "/rent-a-bakkie-volksrust", component: LocationPage },
  { path: "/rent-a-bakkie-vredenburg", component: LocationPage },
  { path: "/rent-a-bakkie-vryburg", component: LocationPage },
  { path: "/rent-a-bakkie-vryheid", component: LocationPage },
  { path: "/rent-a-bakkie-welkom", component: LocationPage },
  { path: "/rent-a-bakkie-worcester", component: LocationPage },
  { path: "/retail-delivery-alberton", component: LocationPage },
  { path: "/retail-delivery-aliwal-north", component: LocationPage },
  { path: "/retail-delivery-beaufort-west", component: LocationPage },
  { path: "/retail-delivery-benoni", component: LocationPage },
  { path: "/retail-delivery-bethal", component: LocationPage },
  { path: "/retail-delivery-bhisho", component: LocationPage },
  { path: "/retail-delivery-bloemfontein", component: LocationPage },
  { path: "/retail-delivery-brakpan", component: LocationPage },
  { path: "/retail-delivery-brits", component: LocationPage },
  { path: "/retail-delivery-cape-town", component: LocationPage },
  { path: "/retail-delivery-carletonville", component: LocationPage },
  { path: "/retail-delivery-centurion", component: LocationPage },
  { path: "/retail-delivery-cradock", component: LocationPage },
  { path: "/retail-delivery-de-aar", component: LocationPage },
  { path: "/retail-delivery-durban", component: LocationPage },
  { path: "/retail-delivery-east-london", component: LocationPage },
  { path: "/retail-delivery-ferndale", component: LocationPage },
  { path: "/retail-delivery-george", component: LocationPage },
  { path: "/retail-delivery-graaff-reinet", component: LocationPage },
  { path: "/retail-delivery-grahamstown", component: LocationPage },
  { path: "/retail-delivery-hermanus", component: LocationPage },
  { path: "/retail-delivery-jeffreys-bay", component: LocationPage },
  { path: "/retail-delivery-johannesburg", component: LocationPage },
  { path: "/retail-delivery-kimberley", component: LocationPage },
  { path: "/retail-delivery-klerksdorp", component: LocationPage },
  { path: "/retail-delivery-knysna", component: LocationPage },
  { path: "/retail-delivery-kroonstad", component: LocationPage },
  { path: "/retail-delivery-kuilsrivier", component: LocationPage },
  { path: "/retail-delivery-ladysmith", component: LocationPage },
  { path: "/retail-delivery-lebowakgomo", component: LocationPage },
  { path: "/retail-delivery-lichtenburg", component: LocationPage },
  { path: "/retail-delivery-mahikeng", component: LocationPage },
  { path: "/retail-delivery-malmesbury", component: LocationPage },
  { path: "/retail-delivery-middelburg", component: LocationPage },
  { path: "/retail-delivery-midrand", component: LocationPage },
  { path: "/retail-delivery-mmabatho", component: LocationPage },
  { path: "/retail-delivery-mossel-bay", component: LocationPage },
  { path: "/retail-delivery-musina", component: LocationPage },
  { path: "/retail-delivery-nelspruit", component: LocationPage },
  { path: "/retail-delivery-oudtshoorn", component: LocationPage },
  { path: "/retail-delivery-paarl", component: LocationPage },
  { path: "/retail-delivery-pietermaritzburg", component: LocationPage },
  { path: "/retail-delivery-polokwane", component: LocationPage },
  { path: "/retail-delivery-port-elizabeth", component: LocationPage },
  { path: "/retail-delivery-port-shepstone", component: LocationPage },
  { path: "/retail-delivery-potchefstroom", component: LocationPage },
  { path: "/retail-delivery-pretoria", component: LocationPage },
  { path: "/retail-delivery-queenstown", component: LocationPage },
  { path: "/retail-delivery-randburg", component: LocationPage },
  { path: "/retail-delivery-roodepoort", component: LocationPage },
  { path: "/retail-delivery-rustenburg", component: LocationPage },
  { path: "/retail-delivery-saldanha", component: LocationPage },
  { path: "/retail-delivery-sandton", component: LocationPage },
  { path: "/retail-delivery-soweto", component: LocationPage },
  { path: "/retail-delivery-springs", component: LocationPage },
  { path: "/retail-delivery-standerton", component: LocationPage },
  { path: "/retail-delivery-thohoyandou", component: LocationPage },
  { path: "/retail-delivery-tzaneen", component: LocationPage },
  { path: "/retail-delivery-uitenhage", component: LocationPage },
  { path: "/retail-delivery-ulundi", component: LocationPage },
  { path: "/retail-delivery-umtata", component: LocationPage },
  { path: "/retail-delivery-upington", component: LocationPage },
  { path: "/retail-delivery-vanderbijlpark", component: LocationPage },
  { path: "/retail-delivery-vereeniging", component: LocationPage },
  { path: "/retail-delivery-volksrust", component: LocationPage },
  { path: "/retail-delivery-vredenburg", component: LocationPage },
  { path: "/retail-delivery-vryburg", component: LocationPage },
  { path: "/retail-delivery-vryheid", component: LocationPage },
  { path: "/retail-delivery-welkom", component: LocationPage },
  { path: "/retail-delivery-worcester", component: LocationPage },
  { path: "/same-day-delivery-alberton", component: LocationPage },
  { path: "/same-day-delivery-aliwal-north", component: LocationPage },
  { path: "/same-day-delivery-beaufort-west", component: LocationPage },
  { path: "/same-day-delivery-benoni", component: LocationPage },
  { path: "/same-day-delivery-bethal", component: LocationPage },
  { path: "/same-day-delivery-bhisho", component: LocationPage },
  { path: "/same-day-delivery-bloemfontein", component: LocationPage },
  { path: "/same-day-delivery-brakpan", component: LocationPage },
  { path: "/same-day-delivery-brits", component: LocationPage },
  { path: "/same-day-delivery-cape-town", component: LocationPage },
  { path: "/same-day-delivery-carletonville", component: LocationPage },
  { path: "/same-day-delivery-centurion", component: LocationPage },
  { path: "/same-day-delivery-cradock", component: LocationPage },
  { path: "/same-day-delivery-de-aar", component: LocationPage },
  { path: "/same-day-delivery-durban", component: LocationPage },
  { path: "/same-day-delivery-east-london", component: LocationPage },
  { path: "/same-day-delivery-ferndale", component: LocationPage },
  { path: "/same-day-delivery-george", component: LocationPage },
  { path: "/same-day-delivery-graaff-reinet", component: LocationPage },
  { path: "/same-day-delivery-grahamstown", component: LocationPage },
  { path: "/same-day-delivery-hermanus", component: LocationPage },
  { path: "/same-day-delivery-jeffreys-bay", component: LocationPage },
  { path: "/same-day-delivery-johannesburg", component: LocationPage },
  { path: "/same-day-delivery-kimberley", component: LocationPage },
  { path: "/same-day-delivery-klerksdorp", component: LocationPage },
  { path: "/same-day-delivery-knysna", component: LocationPage },
  { path: "/same-day-delivery-kroonstad", component: LocationPage },
  { path: "/same-day-delivery-kuilsrivier", component: LocationPage },
  { path: "/same-day-delivery-ladysmith", component: LocationPage },
  { path: "/same-day-delivery-lebowakgomo", component: LocationPage },
  { path: "/same-day-delivery-lichtenburg", component: LocationPage },
  { path: "/same-day-delivery-mahikeng", component: LocationPage },
  { path: "/same-day-delivery-malmesbury", component: LocationPage },
  { path: "/same-day-delivery-middelburg", component: LocationPage },
  { path: "/same-day-delivery-midrand", component: LocationPage },
  { path: "/same-day-delivery-mmabatho", component: LocationPage },
  { path: "/same-day-delivery-mossel-bay", component: LocationPage },
  { path: "/same-day-delivery-musina", component: LocationPage },
  { path: "/same-day-delivery-nelspruit", component: LocationPage },
  { path: "/same-day-delivery-oudtshoorn", component: LocationPage },
  { path: "/same-day-delivery-paarl", component: LocationPage },
  { path: "/same-day-delivery-pietermaritzburg", component: LocationPage },
  { path: "/same-day-delivery-polokwane", component: LocationPage },
  { path: "/same-day-delivery-port-elizabeth", component: LocationPage },
  { path: "/same-day-delivery-port-shepstone", component: LocationPage },
  { path: "/same-day-delivery-potchefstroom", component: LocationPage },
  { path: "/same-day-delivery-pretoria", component: LocationPage },
  { path: "/same-day-delivery-queenstown", component: LocationPage },
  { path: "/same-day-delivery-randburg", component: LocationPage },
  { path: "/same-day-delivery-roodepoort", component: LocationPage },
  { path: "/same-day-delivery-rustenburg", component: LocationPage },
  { path: "/same-day-delivery-saldanha", component: LocationPage },
  { path: "/same-day-delivery-sandton", component: LocationPage },
  { path: "/same-day-delivery-soweto", component: LocationPage },
  { path: "/same-day-delivery-springs", component: LocationPage },
  { path: "/same-day-delivery-standerton", component: LocationPage },
  { path: "/same-day-delivery-thohoyandou", component: LocationPage },
  { path: "/same-day-delivery-tzaneen", component: LocationPage },
  { path: "/same-day-delivery-uitenhage", component: LocationPage },
  { path: "/same-day-delivery-ulundi", component: LocationPage },
  { path: "/same-day-delivery-umtata", component: LocationPage },
  { path: "/same-day-delivery-upington", component: LocationPage },
  { path: "/same-day-delivery-vanderbijlpark", component: LocationPage },
  { path: "/same-day-delivery-vereeniging", component: LocationPage },
  { path: "/same-day-delivery-volksrust", component: LocationPage },
  { path: "/same-day-delivery-vredenburg", component: LocationPage },
  { path: "/same-day-delivery-vryburg", component: LocationPage },
  { path: "/same-day-delivery-vryheid", component: LocationPage },
  { path: "/same-day-delivery-welkom", component: LocationPage },
  { path: "/same-day-delivery-worcester", component: LocationPage },
  { path: "/transportation-alberton", component: LocationPage },
  { path: "/transportation-aliwal-north", component: LocationPage },
  { path: "/transportation-beaufort-west", component: LocationPage },
  { path: "/transportation-benoni", component: LocationPage },
  { path: "/transportation-bethal", component: LocationPage },
  { path: "/transportation-bhisho", component: LocationPage },
  { path: "/transportation-bloemfontein", component: LocationPage },
  { path: "/transportation-brakpan", component: LocationPage },
  { path: "/transportation-brits", component: LocationPage },
  { path: "/transportation-cape-town", component: LocationPage },
  { path: "/transportation-carletonville", component: LocationPage },
  { path: "/transportation-centurion", component: LocationPage },
  { path: "/transportation-company-alberton", component: LocationPage },
  { path: "/transportation-company-aliwal-north", component: LocationPage },
  { path: "/transportation-company-beaufort-west", component: LocationPage },
  { path: "/transportation-company-benoni", component: LocationPage },
  { path: "/transportation-company-bethal", component: LocationPage },
  { path: "/transportation-company-bhisho", component: LocationPage },
  { path: "/transportation-company-bloemfontein", component: LocationPage },
  { path: "/transportation-company-brakpan", component: LocationPage },
  { path: "/transportation-company-brits", component: LocationPage },
  { path: "/transportation-company-cape-town", component: LocationPage },
  { path: "/transportation-company-carletonville", component: LocationPage },
  { path: "/transportation-company-centurion", component: LocationPage },
  { path: "/transportation-company-cradock", component: LocationPage },
  { path: "/transportation-company-de-aar", component: LocationPage },
  { path: "/transportation-company-durban", component: LocationPage },
  { path: "/transportation-company-east-london", component: LocationPage },
  { path: "/transportation-company-ferndale", component: LocationPage },
  { path: "/transportation-company-george", component: LocationPage },
  { path: "/transportation-company-graaff-reinet", component: LocationPage },
  { path: "/transportation-company-grahamstown", component: LocationPage },
  { path: "/transportation-company-hermanus", component: LocationPage },
  { path: "/transportation-company-jeffreys-bay", component: LocationPage },
  { path: "/transportation-company-johannesburg", component: LocationPage },
  { path: "/transportation-company-kimberley", component: LocationPage },
  { path: "/transportation-company-klerksdorp", component: LocationPage },
  { path: "/transportation-company-knysna", component: LocationPage },
  { path: "/transportation-company-kroonstad", component: LocationPage },
  { path: "/transportation-company-kuilsrivier", component: LocationPage },
  { path: "/transportation-company-ladysmith", component: LocationPage },
  { path: "/transportation-company-lebowakgomo", component: LocationPage },
  { path: "/transportation-company-lichtenburg", component: LocationPage },
  { path: "/transportation-company-mahikeng", component: LocationPage },
  { path: "/transportation-company-malmesbury", component: LocationPage },
  { path: "/transportation-company-middelburg", component: LocationPage },
  { path: "/transportation-company-midrand", component: LocationPage },
  { path: "/transportation-company-mmabatho", component: LocationPage },
  { path: "/transportation-company-mossel-bay", component: LocationPage },
  { path: "/transportation-company-musina", component: LocationPage },
  { path: "/transportation-company-nelspruit", component: LocationPage },
  { path: "/transportation-company-oudtshoorn", component: LocationPage },
  { path: "/transportation-company-paarl", component: LocationPage },
  { path: "/transportation-company-pietermaritzburg", component: LocationPage },
  { path: "/transportation-company-polokwane", component: LocationPage },
  { path: "/transportation-company-port-elizabeth", component: LocationPage },
  { path: "/transportation-company-port-shepstone", component: LocationPage },
  { path: "/transportation-company-potchefstroom", component: LocationPage },
  { path: "/transportation-company-pretoria", component: LocationPage },
  { path: "/transportation-company-queenstown", component: LocationPage },
  { path: "/transportation-company-randburg", component: LocationPage },
  { path: "/transportation-company-roodepoort", component: LocationPage },
  { path: "/transportation-company-rustenburg", component: LocationPage },
  { path: "/transportation-company-saldanha", component: LocationPage },
  { path: "/transportation-company-sandton", component: LocationPage },
  { path: "/transportation-company-soweto", component: LocationPage },
  { path: "/transportation-company-springs", component: LocationPage },
  { path: "/transportation-company-standerton", component: LocationPage },
  { path: "/transportation-company-thohoyandou", component: LocationPage },
  { path: "/transportation-company-tzaneen", component: LocationPage },
  { path: "/transportation-company-uitenhage", component: LocationPage },
  { path: "/transportation-company-ulundi", component: LocationPage },
  { path: "/transportation-company-umtata", component: LocationPage },
  { path: "/transportation-company-upington", component: LocationPage },
  { path: "/transportation-company-vanderbijlpark", component: LocationPage },
  { path: "/transportation-company-vereeniging", component: LocationPage },
  { path: "/transportation-company-volksrust", component: LocationPage },
  { path: "/transportation-company-vredenburg", component: LocationPage },
  { path: "/transportation-company-vryburg", component: LocationPage },
  { path: "/transportation-company-vryheid", component: LocationPage },
  { path: "/transportation-company-welkom", component: LocationPage },
  { path: "/transportation-company-worcester", component: LocationPage },
  { path: "/transportation-cradock", component: LocationPage },
  { path: "/transportation-de-aar", component: LocationPage },
  { path: "/transportation-durban", component: LocationPage },
  { path: "/transportation-east-london", component: LocationPage },
  { path: "/transportation-ferndale", component: LocationPage },
  { path: "/transportation-george", component: LocationPage },
  { path: "/transportation-graaff-reinet", component: LocationPage },
  { path: "/transportation-grahamstown", component: LocationPage },
  { path: "/transportation-hermanus", component: LocationPage },
  { path: "/transportation-jeffreys-bay", component: LocationPage },
  { path: "/transportation-johannesburg", component: LocationPage },
  { path: "/transportation-kimberley", component: LocationPage },
  { path: "/transportation-klerksdorp", component: LocationPage },
  { path: "/transportation-knysna", component: LocationPage },
  { path: "/transportation-kroonstad", component: LocationPage },
  { path: "/transportation-kuilsrivier", component: LocationPage },
  { path: "/transportation-ladysmith", component: LocationPage },
  { path: "/transportation-lebowakgomo", component: LocationPage },
  { path: "/transportation-lichtenburg", component: LocationPage },
  { path: "/transportation-mahikeng", component: LocationPage },
  { path: "/transportation-malmesbury", component: LocationPage },
  { path: "/transportation-middelburg", component: LocationPage },
  { path: "/transportation-midrand", component: LocationPage },
  { path: "/transportation-mmabatho", component: LocationPage },
  { path: "/transportation-mossel-bay", component: LocationPage },
  { path: "/transportation-musina", component: LocationPage },
  { path: "/transportation-nelspruit", component: LocationPage },
  { path: "/transportation-oudtshoorn", component: LocationPage },
  { path: "/transportation-paarl", component: LocationPage },
  { path: "/transportation-pietermaritzburg", component: LocationPage },
  { path: "/transportation-polokwane", component: LocationPage },
  { path: "/transportation-port-elizabeth", component: LocationPage },
  { path: "/transportation-port-shepstone", component: LocationPage },
  { path: "/transportation-potchefstroom", component: LocationPage },
  { path: "/transportation-pretoria", component: LocationPage },
  { path: "/transportation-queenstown", component: LocationPage },
  { path: "/transportation-randburg", component: LocationPage },
  { path: "/transportation-roodepoort", component: LocationPage },
  { path: "/transportation-rustenburg", component: LocationPage },
  { path: "/transportation-saldanha", component: LocationPage },
  { path: "/transportation-sandton", component: LocationPage },
  { path: "/transportation-soweto", component: LocationPage },
  { path: "/transportation-springs", component: LocationPage },
  { path: "/transportation-standerton", component: LocationPage },
  { path: "/transportation-thohoyandou", component: LocationPage },
  { path: "/transportation-tzaneen", component: LocationPage },
  { path: "/transportation-uitenhage", component: LocationPage },
  { path: "/transportation-ulundi", component: LocationPage },
  { path: "/transportation-umtata", component: LocationPage },
  { path: "/transportation-upington", component: LocationPage },
  { path: "/transportation-vanderbijlpark", component: LocationPage },
  { path: "/transportation-vereeniging", component: LocationPage },
  { path: "/transportation-volksrust", component: LocationPage },
  { path: "/transportation-vredenburg", component: LocationPage },
  { path: "/transportation-vryburg", component: LocationPage },
  { path: "/transportation-vryheid", component: LocationPage },
  { path: "/transportation-welkom", component: LocationPage },
  { path: "/transportation-worcester", component: LocationPage },
  { path: "/truck-and-driver-hire-alberton", component: LocationPage },
  { path: "/truck-and-driver-hire-aliwal-north", component: LocationPage },
  { path: "/truck-and-driver-hire-beaufort-west", component: LocationPage },
  { path: "/truck-and-driver-hire-benoni", component: LocationPage },
  { path: "/truck-and-driver-hire-bethal", component: LocationPage },
  { path: "/truck-and-driver-hire-bhisho", component: LocationPage },
  { path: "/truck-and-driver-hire-bloemfontein", component: LocationPage },
  { path: "/truck-and-driver-hire-brakpan", component: LocationPage },
  { path: "/truck-and-driver-hire-brits", component: LocationPage },
  { path: "/truck-and-driver-hire-cape-town", component: LocationPage },
  { path: "/truck-and-driver-hire-carletonville", component: LocationPage },
  { path: "/truck-and-driver-hire-centurion", component: LocationPage },
  { path: "/truck-and-driver-hire-cradock", component: LocationPage },
  { path: "/truck-and-driver-hire-de-aar", component: LocationPage },
  { path: "/truck-and-driver-hire-durban", component: LocationPage },
  { path: "/truck-and-driver-hire-east-london", component: LocationPage },
  { path: "/truck-and-driver-hire-ferndale", component: LocationPage },
  { path: "/truck-and-driver-hire-george", component: LocationPage },
  { path: "/truck-and-driver-hire-graaff-reinet", component: LocationPage },
  { path: "/truck-and-driver-hire-grahamstown", component: LocationPage },
  { path: "/truck-and-driver-hire-hermanus", component: LocationPage },
  { path: "/truck-and-driver-hire-jeffreys-bay", component: LocationPage },
  { path: "/truck-and-driver-hire-johannesburg", component: LocationPage },
  { path: "/truck-and-driver-hire-kimberley", component: LocationPage },
  { path: "/truck-and-driver-hire-klerksdorp", component: LocationPage },
  { path: "/truck-and-driver-hire-knysna", component: LocationPage },
  { path: "/truck-and-driver-hire-kroonstad", component: LocationPage },
  { path: "/truck-and-driver-hire-kuilsrivier", component: LocationPage },
  { path: "/truck-and-driver-hire-ladysmith", component: LocationPage },
  { path: "/truck-and-driver-hire-lebowakgomo", component: LocationPage },
  { path: "/truck-and-driver-hire-lichtenburg", component: LocationPage },
  { path: "/truck-and-driver-hire-mahikeng", component: LocationPage },
  { path: "/truck-and-driver-hire-malmesbury", component: LocationPage },
  { path: "/truck-and-driver-hire-middelburg", component: LocationPage },
  { path: "/truck-and-driver-hire-midrand", component: LocationPage },
  { path: "/truck-and-driver-hire-mmabatho", component: LocationPage },
  { path: "/truck-and-driver-hire-mossel-bay", component: LocationPage },
  { path: "/truck-and-driver-hire-musina", component: LocationPage },
  { path: "/truck-and-driver-hire-nelspruit", component: LocationPage },
  { path: "/truck-and-driver-hire-oudtshoorn", component: LocationPage },
  { path: "/truck-and-driver-hire-paarl", component: LocationPage },
  { path: "/truck-and-driver-hire-pietermaritzburg", component: LocationPage },
  { path: "/truck-and-driver-hire-polokwane", component: LocationPage },
  { path: "/truck-and-driver-hire-port-elizabeth", component: LocationPage },
  { path: "/truck-and-driver-hire-port-shepstone", component: LocationPage },
  { path: "/truck-and-driver-hire-potchefstroom", component: LocationPage },
  { path: "/truck-and-driver-hire-pretoria", component: LocationPage },
  { path: "/truck-and-driver-hire-queenstown", component: LocationPage },
  { path: "/truck-and-driver-hire-randburg", component: LocationPage },
  { path: "/truck-and-driver-hire-roodepoort", component: LocationPage },
  { path: "/truck-and-driver-hire-rustenburg", component: LocationPage },
  { path: "/truck-and-driver-hire-saldanha", component: LocationPage },
  { path: "/truck-and-driver-hire-sandton", component: LocationPage },
  { path: "/truck-and-driver-hire-soweto", component: LocationPage },
  { path: "/truck-and-driver-hire-springs", component: LocationPage },
  { path: "/truck-and-driver-hire-standerton", component: LocationPage },
  { path: "/truck-and-driver-hire-thohoyandou", component: LocationPage },
  { path: "/truck-and-driver-hire-tzaneen", component: LocationPage },
  { path: "/truck-and-driver-hire-uitenhage", component: LocationPage },
  { path: "/truck-and-driver-hire-ulundi", component: LocationPage },
  { path: "/truck-and-driver-hire-umtata", component: LocationPage },
  { path: "/truck-and-driver-hire-upington", component: LocationPage },
  { path: "/truck-and-driver-hire-vanderbijlpark", component: LocationPage },
  { path: "/truck-and-driver-hire-vereeniging", component: LocationPage },
  { path: "/truck-and-driver-hire-volksrust", component: LocationPage },
  { path: "/truck-and-driver-hire-vredenburg", component: LocationPage },
  { path: "/truck-and-driver-hire-vryburg", component: LocationPage },
  { path: "/truck-and-driver-hire-vryheid", component: LocationPage },
  { path: "/truck-and-driver-hire-welkom", component: LocationPage },
  { path: "/truck-and-driver-hire-worcester", component: LocationPage },
  { path: "/uber-alberton", component: LocationPage },
  { path: "/uber-aliwal-north", component: LocationPage },
  { path: "/uber-beaufort-west", component: LocationPage },
  { path: "/uber-benoni", component: LocationPage },
  { path: "/uber-bethal", component: LocationPage },
  { path: "/uber-bhisho", component: LocationPage },
  { path: "/uber-bloemfontein", component: LocationPage },
  { path: "/uber-brakpan", component: LocationPage },
  { path: "/uber-brits", component: LocationPage },
  { path: "/uber-cape-town", component: LocationPage },
  { path: "/uber-carletonville", component: LocationPage },
  { path: "/uber-centurion", component: LocationPage },
  { path: "/uber-cradock", component: LocationPage },
  { path: "/uber-de-aar", component: LocationPage },
  { path: "/uber-durban", component: LocationPage },
  { path: "/uber-east-london", component: LocationPage },
  { path: "/uber-ferndale", component: LocationPage },
  { path: "/uber-george", component: LocationPage },
  { path: "/uber-graaff-reinet", component: LocationPage },
  { path: "/uber-grahamstown", component: LocationPage },
  { path: "/uber-hermanus", component: LocationPage },
  { path: "/uber-jeffreys-bay", component: LocationPage },
  { path: "/uber-johannesburg", component: LocationPage },
  { path: "/uber-kimberley", component: LocationPage },
  { path: "/uber-klerksdorp", component: LocationPage },
  { path: "/uber-knysna", component: LocationPage },
  { path: "/uber-kroonstad", component: LocationPage },
  { path: "/uber-kuilsrivier", component: LocationPage },
  { path: "/uber-ladysmith", component: LocationPage },
  { path: "/uber-lebowakgomo", component: LocationPage },
  { path: "/uber-lichtenburg", component: LocationPage },
  { path: "/uber-mahikeng", component: LocationPage },
  { path: "/uber-malmesbury", component: LocationPage },
  { path: "/uber-middelburg", component: LocationPage },
  { path: "/uber-midrand", component: LocationPage },
  { path: "/uber-mmabatho", component: LocationPage },
  { path: "/uber-mossel-bay", component: LocationPage },
  { path: "/uber-musina", component: LocationPage },
  { path: "/uber-nelspruit", component: LocationPage },
  { path: "/uber-oudtshoorn", component: LocationPage },
  { path: "/uber-paarl", component: LocationPage },
  { path: "/uber-pietermaritzburg", component: LocationPage },
  { path: "/uber-polokwane", component: LocationPage },
  { path: "/uber-port-elizabeth", component: LocationPage },
  { path: "/uber-port-shepstone", component: LocationPage },
  { path: "/uber-potchefstroom", component: LocationPage },
  { path: "/uber-pretoria", component: LocationPage },
  { path: "/uber-queenstown", component: LocationPage },
  { path: "/uber-randburg", component: LocationPage },
  { path: "/uber-roodepoort", component: LocationPage },
  { path: "/uber-rustenburg", component: LocationPage },
  { path: "/uber-saldanha", component: LocationPage },
  { path: "/uber-sandton", component: LocationPage },
  { path: "/uber-soweto", component: LocationPage },
  { path: "/uber-springs", component: LocationPage },
  { path: "/uber-standerton", component: LocationPage },
  { path: "/uber-thohoyandou", component: LocationPage },
  { path: "/uber-truck-alberton", component: LocationPage },
  { path: "/uber-truck-aliwal-north", component: LocationPage },
  { path: "/uber-truck-beaufort-west", component: LocationPage },
  { path: "/uber-truck-benoni", component: LocationPage },
  { path: "/uber-truck-bethal", component: LocationPage },
  { path: "/uber-truck-bhisho", component: LocationPage },
  { path: "/uber-truck-bloemfontein", component: LocationPage },
  { path: "/uber-truck-brakpan", component: LocationPage },
  { path: "/uber-truck-brits", component: LocationPage },
  { path: "/uber-truck-cape-town", component: LocationPage },
  { path: "/uber-truck-carletonville", component: LocationPage },
  { path: "/uber-truck-centurion", component: LocationPage },
  { path: "/uber-truck-cradock", component: LocationPage },
  { path: "/uber-truck-de-aar", component: LocationPage },
  { path: "/uber-truck-durban", component: LocationPage },
  { path: "/uber-truck-east-london", component: LocationPage },
  { path: "/uber-truck-ferndale", component: LocationPage },
  { path: "/uber-truck-george", component: LocationPage },
  { path: "/uber-truck-graaff-reinet", component: LocationPage },
  { path: "/uber-truck-grahamstown", component: LocationPage },
  { path: "/uber-truck-hermanus", component: LocationPage },
  { path: "/uber-truck-jeffreys-bay", component: LocationPage },
  { path: "/uber-truck-johannesburg", component: LocationPage },
  { path: "/uber-truck-kimberley", component: LocationPage },
  { path: "/uber-truck-klerksdorp", component: LocationPage },
  { path: "/uber-truck-knysna", component: LocationPage },
  { path: "/uber-truck-kroonstad", component: LocationPage },
  { path: "/uber-truck-kuilsrivier", component: LocationPage },
  { path: "/uber-truck-ladysmith", component: LocationPage },
  { path: "/uber-truck-lebowakgomo", component: LocationPage },
  { path: "/uber-truck-lichtenburg", component: LocationPage },
  { path: "/uber-truck-mahikeng", component: LocationPage },
  { path: "/uber-truck-malmesbury", component: LocationPage },
  { path: "/uber-truck-middelburg", component: LocationPage },
  { path: "/uber-truck-midrand", component: LocationPage },
  { path: "/uber-truck-mmabatho", component: LocationPage },
  { path: "/uber-truck-mossel-bay", component: LocationPage },
  { path: "/uber-truck-musina", component: LocationPage },
  { path: "/uber-truck-nelspruit", component: LocationPage },
  { path: "/uber-truck-oudtshoorn", component: LocationPage },
  { path: "/uber-truck-paarl", component: LocationPage },
  { path: "/uber-truck-pietermaritzburg", component: LocationPage },
  { path: "/uber-truck-polokwane", component: LocationPage },
  { path: "/uber-truck-port-elizabeth", component: LocationPage },
  { path: "/uber-truck-port-shepstone", component: LocationPage },
  { path: "/uber-truck-potchefstroom", component: LocationPage },
  { path: "/uber-truck-pretoria", component: LocationPage },
  { path: "/uber-truck-queenstown", component: LocationPage },
  { path: "/uber-truck-randburg", component: LocationPage },
  { path: "/uber-truck-roodepoort", component: LocationPage },
  { path: "/uber-truck-rustenburg", component: LocationPage },
  { path: "/uber-truck-saldanha", component: LocationPage },
  { path: "/uber-truck-sandton", component: LocationPage },
  { path: "/uber-truck-soweto", component: LocationPage },
  { path: "/uber-truck-springs", component: LocationPage },
  { path: "/uber-truck-standerton", component: LocationPage },
  { path: "/uber-truck-thohoyandou", component: LocationPage },
  { path: "/uber-truck-tzaneen", component: LocationPage },
  { path: "/uber-truck-uitenhage", component: LocationPage },
  { path: "/uber-truck-ulundi", component: LocationPage },
  { path: "/uber-truck-umtata", component: LocationPage },
  { path: "/uber-truck-upington", component: LocationPage },
  { path: "/uber-truck-vanderbijlpark", component: LocationPage },
  { path: "/uber-truck-vereeniging", component: LocationPage },
  { path: "/uber-truck-volksrust", component: LocationPage },
  { path: "/uber-truck-vredenburg", component: LocationPage },
  { path: "/uber-truck-vryburg", component: LocationPage },
  { path: "/uber-truck-vryheid", component: LocationPage },
  { path: "/uber-truck-welkom", component: LocationPage },
  { path: "/uber-truck-worcester", component: LocationPage },
  { path: "/uber-tzaneen", component: LocationPage },
  { path: "/uber-uitenhage", component: LocationPage },
  { path: "/uber-ulundi", component: LocationPage },
  { path: "/uber-umtata", component: LocationPage },
  { path: "/uber-upington", component: LocationPage },
  { path: "/uber-vanderbijlpark", component: LocationPage },
  { path: "/uber-vereeniging", component: LocationPage },
  { path: "/uber-volksrust", component: LocationPage },
  { path: "/uber-vredenburg", component: LocationPage },
  { path: "/uber-vryburg", component: LocationPage },
  { path: "/uber-vryheid", component: LocationPage },
  { path: "/uber-welkom", component: LocationPage },
  { path: "/uber-worcester", component: LocationPage },
  { path: "/van-hire--alberton", component: LocationPage },
  { path: "/van-hire--aliwal-north", component: LocationPage },
  { path: "/van-hire--beaufort-west", component: LocationPage },
  { path: "/van-hire--benoni", component: LocationPage },
  { path: "/van-hire--bethal", component: LocationPage },
  { path: "/van-hire--bhisho", component: LocationPage },
  { path: "/van-hire--bloemfontein", component: LocationPage },
  { path: "/van-hire--brakpan", component: LocationPage },
  { path: "/van-hire--brits", component: LocationPage },
  { path: "/van-hire--cape-town", component: LocationPage },
  { path: "/van-hire--carletonville", component: LocationPage },
  { path: "/van-hire--centurion", component: LocationPage },
  { path: "/van-hire--cradock", component: LocationPage },
  { path: "/van-hire--de-aar", component: LocationPage },
  { path: "/van-hire--durban", component: LocationPage },
  { path: "/van-hire--east-london", component: LocationPage },
  { path: "/van-hire--ferndale", component: LocationPage },
  { path: "/van-hire--george", component: LocationPage },
  { path: "/van-hire--graaff-reinet", component: LocationPage },
  { path: "/van-hire--grahamstown", component: LocationPage },
  { path: "/van-hire--hermanus", component: LocationPage },
  { path: "/van-hire--jeffreys-bay", component: LocationPage },
  { path: "/van-hire--johannesburg", component: LocationPage },
  { path: "/van-hire--kimberley", component: LocationPage },
  { path: "/van-hire--klerksdorp", component: LocationPage },
  { path: "/van-hire--knysna", component: LocationPage },
  { path: "/van-hire--kroonstad", component: LocationPage },
  { path: "/van-hire--kuilsrivier", component: LocationPage },
  { path: "/van-hire--ladysmith", component: LocationPage },
  { path: "/van-hire--lebowakgomo", component: LocationPage },
  { path: "/van-hire--lichtenburg", component: LocationPage },
  { path: "/van-hire--mahikeng", component: LocationPage },
  { path: "/van-hire--malmesbury", component: LocationPage },
  { path: "/van-hire--middelburg", component: LocationPage },
  { path: "/van-hire--midrand", component: LocationPage },
  { path: "/van-hire--mmabatho", component: LocationPage },
  { path: "/van-hire--mossel-bay", component: LocationPage },
  { path: "/van-hire--musina", component: LocationPage },
  { path: "/van-hire--nelspruit", component: LocationPage },
  { path: "/van-hire--oudtshoorn", component: LocationPage },
  { path: "/van-hire--paarl", component: LocationPage },
  { path: "/van-hire--pietermaritzburg", component: LocationPage },
  { path: "/van-hire--polokwane", component: LocationPage },
  { path: "/van-hire--port-elizabeth", component: LocationPage },
  { path: "/van-hire--port-shepstone", component: LocationPage },
  { path: "/van-hire--potchefstroom", component: LocationPage },
  { path: "/van-hire--pretoria", component: LocationPage },
  { path: "/van-hire--queenstown", component: LocationPage },
  { path: "/van-hire--randburg", component: LocationPage },
  { path: "/van-hire--roodepoort", component: LocationPage },
  { path: "/van-hire--rustenburg", component: LocationPage },
  { path: "/van-hire--saldanha", component: LocationPage },
  { path: "/van-hire--sandton", component: LocationPage },
  { path: "/van-hire--soweto", component: LocationPage },
  { path: "/van-hire--springs", component: LocationPage },
  { path: "/van-hire--standerton", component: LocationPage },
  { path: "/van-hire--thohoyandou", component: LocationPage },
  { path: "/van-hire--tzaneen", component: LocationPage },
  { path: "/van-hire--uitenhage", component: LocationPage },
  { path: "/van-hire--ulundi", component: LocationPage },
  { path: "/van-hire--umtata", component: LocationPage },
  { path: "/van-hire--upington", component: LocationPage },
  { path: "/van-hire--vanderbijlpark", component: LocationPage },
  { path: "/van-hire--vereeniging", component: LocationPage },
  { path: "/van-hire--volksrust", component: LocationPage },
  { path: "/van-hire--vredenburg", component: LocationPage },
  { path: "/van-hire--vryburg", component: LocationPage },
  { path: "/van-hire--vryheid", component: LocationPage },
  { path: "/van-hire--welkom", component: LocationPage },
  { path: "/van-hire--worcester", component: LocationPage },
  { path: "/vehicle-rental-alberton", component: LocationPage },
  { path: "/vehicle-rental-aliwal-north", component: LocationPage },
  { path: "/vehicle-rental-beaufort-west", component: LocationPage },
  { path: "/vehicle-rental-benoni", component: LocationPage },
  { path: "/vehicle-rental-bethal", component: LocationPage },
  { path: "/vehicle-rental-bhisho", component: LocationPage },
  { path: "/vehicle-rental-bloemfontein", component: LocationPage },
  { path: "/vehicle-rental-brakpan", component: LocationPage },
  { path: "/vehicle-rental-brits", component: LocationPage },
  { path: "/vehicle-rental-cape-town", component: LocationPage },
  { path: "/vehicle-rental-carletonville", component: LocationPage },
  { path: "/vehicle-rental-centurion", component: LocationPage },
  { path: "/vehicle-rental-cradock", component: LocationPage },
  { path: "/vehicle-rental-de-aar", component: LocationPage },
  { path: "/vehicle-rental-durban", component: LocationPage },
  { path: "/vehicle-rental-east-london", component: LocationPage },
  { path: "/vehicle-rental-ferndale", component: LocationPage },
  { path: "/vehicle-rental-george", component: LocationPage },
  { path: "/vehicle-rental-graaff-reinet", component: LocationPage },
  { path: "/vehicle-rental-grahamstown", component: LocationPage },
  { path: "/vehicle-rental-hermanus", component: LocationPage },
  { path: "/vehicle-rental-jeffreys-bay", component: LocationPage },
  { path: "/vehicle-rental-johannesburg", component: LocationPage },
  { path: "/vehicle-rental-kimberley", component: LocationPage },
  { path: "/vehicle-rental-klerksdorp", component: LocationPage },
  { path: "/vehicle-rental-knysna", component: LocationPage },
  { path: "/vehicle-rental-kroonstad", component: LocationPage },
  { path: "/vehicle-rental-kuilsrivier", component: LocationPage },
  { path: "/vehicle-rental-ladysmith", component: LocationPage },
  { path: "/vehicle-rental-lebowakgomo", component: LocationPage },
  { path: "/vehicle-rental-lichtenburg", component: LocationPage },
  { path: "/vehicle-rental-mahikeng", component: LocationPage },
  { path: "/vehicle-rental-malmesbury", component: LocationPage },
  { path: "/vehicle-rental-middelburg", component: LocationPage },
  { path: "/vehicle-rental-midrand", component: LocationPage },
  { path: "/vehicle-rental-mmabatho", component: LocationPage },
  { path: "/vehicle-rental-mossel-bay", component: LocationPage },
  { path: "/vehicle-rental-musina", component: LocationPage },
  { path: "/vehicle-rental-nelspruit", component: LocationPage },
  { path: "/vehicle-rental-oudtshoorn", component: LocationPage },
  { path: "/vehicle-rental-paarl", component: LocationPage },
  { path: "/vehicle-rental-pietermaritzburg", component: LocationPage },
  { path: "/vehicle-rental-polokwane", component: LocationPage },
  { path: "/vehicle-rental-port-elizabeth", component: LocationPage },
  { path: "/vehicle-rental-port-shepstone", component: LocationPage },
  { path: "/vehicle-rental-potchefstroom", component: LocationPage },
  { path: "/vehicle-rental-pretoria", component: LocationPage },
  { path: "/vehicle-rental-queenstown", component: LocationPage },
  { path: "/vehicle-rental-randburg", component: LocationPage },
  { path: "/vehicle-rental-roodepoort", component: LocationPage },
  { path: "/vehicle-rental-rustenburg", component: LocationPage },
  { path: "/vehicle-rental-saldanha", component: LocationPage },
  { path: "/vehicle-rental-sandton", component: LocationPage },
  { path: "/vehicle-rental-soweto", component: LocationPage },
  { path: "/vehicle-rental-springs", component: LocationPage },
  { path: "/vehicle-rental-standerton", component: LocationPage },
  { path: "/vehicle-rental-thohoyandou", component: LocationPage },
  { path: "/vehicle-rental-tzaneen", component: LocationPage },
  { path: "/vehicle-rental-uitenhage", component: LocationPage },
  { path: "/vehicle-rental-ulundi", component: LocationPage },
  { path: "/vehicle-rental-umtata", component: LocationPage },
  { path: "/vehicle-rental-upington", component: LocationPage },
  { path: "/vehicle-rental-vanderbijlpark", component: LocationPage },
  { path: "/vehicle-rental-vereeniging", component: LocationPage },
  { path: "/vehicle-rental-volksrust", component: LocationPage },
  { path: "/vehicle-rental-vredenburg", component: LocationPage },
  { path: "/vehicle-rental-vryburg", component: LocationPage },
  { path: "/vehicle-rental-vryheid", component: LocationPage },
  { path: "/vehicle-rental-welkom", component: LocationPage },
  { path: "/vehicle-rental-worcester", component: LocationPage },
{ path: "/1-ton-boksburg", component: LocationPage },
  { path: "/1-ton-ekurhuleni", component: LocationPage },
  { path: "/1-ton-gqeberha", component: LocationPage },
  { path: "/1-ton-kempton-park", component: LocationPage },
  { path: "/1-ton-mitchells-plain", component: LocationPage },
  { path: "/1-ton-tembisa", component: LocationPage },
  { path: "/1-ton-witbank", component: LocationPage },
  { path: "/1.5-ton-boksburg", component: LocationPage },
  { path: "/1.5-ton-ekurhuleni", component: LocationPage },
  { path: "/1.5-ton-gqeberha", component: LocationPage },
  { path: "/1.5-ton-kempton-park", component: LocationPage },
  { path: "/1.5-ton-mitchells-plain", component: LocationPage },
  { path: "/1.5-ton-tembisa", component: LocationPage },
  { path: "/1.5-ton-witbank", component: LocationPage },
  { path: "/4-ton-boksburg", component: LocationPage },
  { path: "/4-ton-ekurhuleni", component: LocationPage },
  { path: "/4-ton-gqeberha", component: LocationPage },
  { path: "/4-ton-kempton-park", component: LocationPage },
  { path: "/4-ton-mitchells-plain", component: LocationPage },
  { path: "/4-ton-tembisa", component: LocationPage },
  { path: "/4-ton-witbank", component: LocationPage },
  { path: "/8-ton-boksburg", component: LocationPage },
  { path: "/8-ton-ekurhuleni", component: LocationPage },
  { path: "/8-ton-gqeberha", component: LocationPage },
  { path: "/8-ton-kempton-park", component: LocationPage },
  { path: "/8-ton-mitchells-plain", component: LocationPage },
  { path: "/8-ton-tembisa", component: LocationPage },
  { path: "/8-ton-witbank", component: LocationPage },
  { path: "/appliance-delivery--alberton", component: LocationPage },
  { path: "/appliance-delivery--aliwal-north", component: LocationPage },
  { path: "/appliance-delivery--beaufort-west", component: LocationPage },
  { path: "/appliance-delivery--benoni", component: LocationPage },
  { path: "/appliance-delivery--bethal", component: LocationPage },
  { path: "/appliance-delivery--bhisho", component: LocationPage },
  { path: "/appliance-delivery--bloemfontein", component: LocationPage },
  { path: "/appliance-delivery--boksburg", component: LocationPage },
  { path: "/appliance-delivery--brakpan", component: LocationPage },
  { path: "/appliance-delivery--brits", component: LocationPage },
  { path: "/appliance-delivery--cape-town", component: LocationPage },
  { path: "/appliance-delivery--carletonville", component: LocationPage },
  { path: "/appliance-delivery--centurion", component: LocationPage },
  { path: "/appliance-delivery--cradock", component: LocationPage },
  { path: "/appliance-delivery--de-aar", component: LocationPage },
  { path: "/appliance-delivery--durban", component: LocationPage },
  { path: "/appliance-delivery--east-london", component: LocationPage },
  { path: "/appliance-delivery--ekurhuleni", component: LocationPage },
  { path: "/appliance-delivery--ferndale", component: LocationPage },
  { path: "/appliance-delivery--george", component: LocationPage },
  { path: "/appliance-delivery--gqeberha", component: LocationPage },
  { path: "/appliance-delivery--graaff-reinet", component: LocationPage },
  { path: "/appliance-delivery--grahamstown", component: LocationPage },
  { path: "/appliance-delivery--hermanus", component: LocationPage },
  { path: "/appliance-delivery--jeffreys-bay", component: LocationPage },
  { path: "/appliance-delivery--johannesburg", component: LocationPage },
  { path: "/appliance-delivery--kempton-park", component: LocationPage },
  { path: "/appliance-delivery--kimberley", component: LocationPage },
  { path: "/appliance-delivery--klerksdorp", component: LocationPage },
  { path: "/appliance-delivery--knysna", component: LocationPage },
  { path: "/appliance-delivery--kroonstad", component: LocationPage },
  { path: "/appliance-delivery--kuilsrivier", component: LocationPage },
  { path: "/appliance-delivery--ladysmith", component: LocationPage },
  { path: "/appliance-delivery--lebowakgomo", component: LocationPage },
  { path: "/appliance-delivery--lichtenburg", component: LocationPage },
  { path: "/appliance-delivery--mahikeng", component: LocationPage },
  { path: "/appliance-delivery--malmesbury", component: LocationPage },
  { path: "/appliance-delivery--middelburg", component: LocationPage },
  { path: "/appliance-delivery--midrand", component: LocationPage },
  { path: "/appliance-delivery--mitchells-plain", component: LocationPage },
  { path: "/appliance-delivery--mmabatho", component: LocationPage },
  { path: "/appliance-delivery--mossel-bay", component: LocationPage },
  { path: "/appliance-delivery--musina", component: LocationPage },
  { path: "/appliance-delivery--nelspruit", component: LocationPage },
  { path: "/appliance-delivery--oudtshoorn", component: LocationPage },
  { path: "/appliance-delivery--paarl", component: LocationPage },
  { path: "/appliance-delivery--pietermaritzburg", component: LocationPage },
  { path: "/appliance-delivery--polokwane", component: LocationPage },
  { path: "/appliance-delivery--port-elizabeth", component: LocationPage },
  { path: "/appliance-delivery--port-shepstone", component: LocationPage },
  { path: "/appliance-delivery--potchefstroom", component: LocationPage },
  { path: "/appliance-delivery--pretoria", component: LocationPage },
  { path: "/appliance-delivery--queenstown", component: LocationPage },
  { path: "/appliance-delivery--randburg", component: LocationPage },
  { path: "/appliance-delivery--roodepoort", component: LocationPage },
  { path: "/appliance-delivery--rustenburg", component: LocationPage },
  { path: "/appliance-delivery--saldanha", component: LocationPage },
  { path: "/appliance-delivery--sandton", component: LocationPage },
  { path: "/appliance-delivery--soweto", component: LocationPage },
  { path: "/appliance-delivery--springs", component: LocationPage },
  { path: "/appliance-delivery--standerton", component: LocationPage },
  { path: "/appliance-delivery--tembisa", component: LocationPage },
  { path: "/appliance-delivery--thohoyandou", component: LocationPage },
  { path: "/appliance-delivery--tzaneen", component: LocationPage },
  { path: "/appliance-delivery--uitenhage", component: LocationPage },
  { path: "/appliance-delivery--ulundi", component: LocationPage },
  { path: "/appliance-delivery--umtata", component: LocationPage },
  { path: "/appliance-delivery--upington", component: LocationPage },
  { path: "/appliance-delivery--vanderbijlpark", component: LocationPage },
  { path: "/appliance-delivery--vereeniging", component: LocationPage },
  { path: "/appliance-delivery--volksrust", component: LocationPage },
  { path: "/appliance-delivery--vredenburg", component: LocationPage },
  { path: "/appliance-delivery--vryburg", component: LocationPage },
  { path: "/appliance-delivery--vryheid", component: LocationPage },
  { path: "/appliance-delivery--welkom", component: LocationPage },
  { path: "/appliance-delivery--witbank", component: LocationPage },
  { path: "/appliance-delivery--worcester", component: LocationPage },
  { path: "/bakkie-hire--boksburg", component: LocationPage },
  { path: "/bakkie-hire--ekurhuleni", component: LocationPage },
  { path: "/bakkie-hire--gqeberha", component: LocationPage },
  { path: "/bakkie-hire--kempton-park", component: LocationPage },
  { path: "/bakkie-hire--mitchells-plain", component: LocationPage },
  { path: "/bakkie-hire--tembisa", component: LocationPage },
  { path: "/bakkie-hire--witbank", component: LocationPage },
  { path: "/business-delivery-boksburg", component: LocationPage },
  { path: "/business-delivery-ekurhuleni", component: LocationPage },
  { path: "/business-delivery-gqeberha", component: LocationPage },
  { path: "/business-delivery-kempton-park", component: LocationPage },
  { path: "/business-delivery-mitchells-plain", component: LocationPage },
  { path: "/business-delivery-tembisa", component: LocationPage },
  { path: "/business-delivery-witbank", component: LocationPage },
  { path: "/cheap-movers-near-me-boksburg", component: LocationPage },
  { path: "/cheap-movers-near-me-ekurhuleni", component: LocationPage },
  { path: "/cheap-movers-near-me-gqeberha", component: LocationPage },
  { path: "/cheap-movers-near-me-kempton-park", component: LocationPage },
  { path: "/cheap-movers-near-me-mitchells-plain", component: LocationPage },
  { path: "/cheap-movers-near-me-tembisa", component: LocationPage },
  { path: "/cheap-movers-near-me-witbank", component: LocationPage },
  { path: "/cheap-removals-boksburg", component: LocationPage },
  { path: "/cheap-removals-ekurhuleni", component: LocationPage },
  { path: "/cheap-removals-gqeberha", component: LocationPage },
  { path: "/cheap-removals-kempton-park", component: LocationPage },
  { path: "/cheap-removals-mitchells-plain", component: LocationPage },
  { path: "/cheap-removals-tembisa", component: LocationPage },
  { path: "/cheap-removals-witbank", component: LocationPage },
  { path: "/cheap-transportation-boksburg", component: LocationPage },
  { path: "/cheap-transportation-ekurhuleni", component: LocationPage },
  { path: "/cheap-transportation-gqeberha", component: LocationPage },
  { path: "/cheap-transportation-kempton-park", component: LocationPage },
  { path: "/cheap-transportation-mitchells-plain", component: LocationPage },
  { path: "/cheap-transportation-tembisa", component: LocationPage },
  { path: "/cheap-transportation-witbank", component: LocationPage },
  { path: "/delivery-boksburg", component: LocationPage },
  { path: "/delivery-ekurhuleni", component: LocationPage },
  { path: "/delivery-gqeberha", component: LocationPage },
  { path: "/delivery-kempton-park", component: LocationPage },
  { path: "/delivery-mitchells-plain", component: LocationPage },
  { path: "/delivery-tembisa", component: LocationPage },
  { path: "/delivery-witbank", component: LocationPage },
  { path: "/door-to-door-delivery-boksburg", component: LocationPage },
  { path: "/door-to-door-delivery-ekurhuleni", component: LocationPage },
  { path: "/door-to-door-delivery-gqeberha", component: LocationPage },
  { path: "/door-to-door-delivery-kempton-park", component: LocationPage },
  { path: "/door-to-door-delivery-mitchells-plain", component: LocationPage },
  { path: "/door-to-door-delivery-tembisa", component: LocationPage },
  { path: "/door-to-door-delivery-witbank", component: LocationPage },
  { path: "/driver-jobs--alberton", component: LocationPage },
  { path: "/driver-jobs--aliwal-north", component: LocationPage },
  { path: "/driver-jobs--beaufort-west", component: LocationPage },
  { path: "/driver-jobs--benoni", component: LocationPage },
  { path: "/driver-jobs--bethal", component: LocationPage },
  { path: "/driver-jobs--bhisho", component: LocationPage },
  { path: "/driver-jobs--bloemfontein", component: LocationPage },
  { path: "/driver-jobs--boksburg", component: LocationPage },
  { path: "/driver-jobs--brakpan", component: LocationPage },
  { path: "/driver-jobs--brits", component: LocationPage },
  { path: "/driver-jobs--cape-town", component: LocationPage },
  { path: "/driver-jobs--carletonville", component: LocationPage },
  { path: "/driver-jobs--centurion", component: LocationPage },
  { path: "/driver-jobs--cradock", component: LocationPage },
  { path: "/driver-jobs--de-aar", component: LocationPage },
  { path: "/driver-jobs--durban", component: LocationPage },
  { path: "/driver-jobs--east-london", component: LocationPage },
  { path: "/driver-jobs--ekurhuleni", component: LocationPage },
  { path: "/driver-jobs--ferndale", component: LocationPage },
  { path: "/driver-jobs--george", component: LocationPage },
  { path: "/driver-jobs--gqeberha", component: LocationPage },
  { path: "/driver-jobs--graaff-reinet", component: LocationPage },
  { path: "/driver-jobs--grahamstown", component: LocationPage },
  { path: "/driver-jobs--hermanus", component: LocationPage },
  { path: "/driver-jobs--jeffreys-bay", component: LocationPage },
  { path: "/driver-jobs--johannesburg", component: LocationPage },
  { path: "/driver-jobs--kempton-park", component: LocationPage },
  { path: "/driver-jobs--kimberley", component: LocationPage },
  { path: "/driver-jobs--klerksdorp", component: LocationPage },
  { path: "/driver-jobs--knysna", component: LocationPage },
  { path: "/driver-jobs--kroonstad", component: LocationPage },
  { path: "/driver-jobs--kuilsrivier", component: LocationPage },
  { path: "/driver-jobs--ladysmith", component: LocationPage },
  { path: "/driver-jobs--lebowakgomo", component: LocationPage },
  { path: "/driver-jobs--lichtenburg", component: LocationPage },
  { path: "/driver-jobs--mahikeng", component: LocationPage },
  { path: "/driver-jobs--malmesbury", component: LocationPage },
  { path: "/driver-jobs--middelburg", component: LocationPage },
  { path: "/driver-jobs--midrand", component: LocationPage },
  { path: "/driver-jobs--mitchells-plain", component: LocationPage },
  { path: "/driver-jobs--mmabatho", component: LocationPage },
  { path: "/driver-jobs--mossel-bay", component: LocationPage },
  { path: "/driver-jobs--musina", component: LocationPage },
  { path: "/driver-jobs--nelspruit", component: LocationPage },
  { path: "/driver-jobs--oudtshoorn", component: LocationPage },
  { path: "/driver-jobs--paarl", component: LocationPage },
  { path: "/driver-jobs--pietermaritzburg", component: LocationPage },
  { path: "/driver-jobs--polokwane", component: LocationPage },
  { path: "/driver-jobs--port-elizabeth", component: LocationPage },
  { path: "/driver-jobs--port-shepstone", component: LocationPage },
  { path: "/driver-jobs--potchefstroom", component: LocationPage },
  { path: "/driver-jobs--pretoria", component: LocationPage },
  { path: "/driver-jobs--queenstown", component: LocationPage },
  { path: "/driver-jobs--randburg", component: LocationPage },
  { path: "/driver-jobs--roodepoort", component: LocationPage },
  { path: "/driver-jobs--rustenburg", component: LocationPage },
  { path: "/driver-jobs--saldanha", component: LocationPage },
  { path: "/driver-jobs--sandton", component: LocationPage },
  { path: "/driver-jobs--soweto", component: LocationPage },
  { path: "/driver-jobs--springs", component: LocationPage },
  { path: "/driver-jobs--standerton", component: LocationPage },
  { path: "/driver-jobs--tembisa", component: LocationPage },
  { path: "/driver-jobs--thohoyandou", component: LocationPage },
  { path: "/driver-jobs--tzaneen", component: LocationPage },
  { path: "/driver-jobs--uitenhage", component: LocationPage },
  { path: "/driver-jobs--ulundi", component: LocationPage },
  { path: "/driver-jobs--umtata", component: LocationPage },
  { path: "/driver-jobs--upington", component: LocationPage },
  { path: "/driver-jobs--vanderbijlpark", component: LocationPage },
  { path: "/driver-jobs--vereeniging", component: LocationPage },
  { path: "/driver-jobs--volksrust", component: LocationPage },
  { path: "/driver-jobs--vredenburg", component: LocationPage },
  { path: "/driver-jobs--vryburg", component: LocationPage },
  { path: "/driver-jobs--vryheid", component: LocationPage },
  { path: "/driver-jobs--welkom", component: LocationPage },
  { path: "/driver-jobs--witbank", component: LocationPage },
  { path: "/driver-jobs--worcester", component: LocationPage },
  { path: "/equipment-transport-boksburg", component: LocationPage },
  { path: "/equipment-transport-ekurhuleni", component: LocationPage },
  { path: "/equipment-transport-gqeberha", component: LocationPage },
  { path: "/equipment-transport-kempton-park", component: LocationPage },
  { path: "/equipment-transport-mitchells-plain", component: LocationPage },
  { path: "/equipment-transport-tembisa", component: LocationPage },
  { path: "/equipment-transport-witbank", component: LocationPage },
  { path: "/express-delivery-boksburg", component: LocationPage },
  { path: "/express-delivery-ekurhuleni", component: LocationPage },
  { path: "/express-delivery-gqeberha", component: LocationPage },
  { path: "/express-delivery-kempton-park", component: LocationPage },
  { path: "/express-delivery-mitchells-plain", component: LocationPage },
  { path: "/express-delivery-tembisa", component: LocationPage },
  { path: "/express-delivery-witbank", component: LocationPage },
  { path: "/flexible-delivery-options-boksburg", component: LocationPage },
  { path: "/flexible-delivery-options-ekurhuleni", component: LocationPage },
  { path: "/flexible-delivery-options-gqeberha", component: LocationPage },
  { path: "/flexible-delivery-options-kempton-park", component: LocationPage },
  { path: "/flexible-delivery-options-mitchells-plain", component: LocationPage },
  { path: "/flexible-delivery-options-tembisa", component: LocationPage },
  { path: "/flexible-delivery-options-witbank", component: LocationPage },
  { path: "/fragile-delivery-boksburg", component: LocationPage },
  { path: "/fragile-delivery-ekurhuleni", component: LocationPage },
  { path: "/fragile-delivery-gqeberha", component: LocationPage },
  { path: "/fragile-delivery-kempton-park", component: LocationPage },
  { path: "/fragile-delivery-mitchells-plain", component: LocationPage },
  { path: "/fragile-delivery-tembisa", component: LocationPage },
  { path: "/fragile-delivery-witbank", component: LocationPage },
  { path: "/freight-delivery-boksburg", component: LocationPage },
  { path: "/freight-delivery-ekurhuleni", component: LocationPage },
  { path: "/freight-delivery-gqeberha", component: LocationPage },
  { path: "/freight-delivery-kempton-park", component: LocationPage },
  { path: "/freight-delivery-mitchells-plain", component: LocationPage },
  { path: "/freight-delivery-tembisa", component: LocationPage },
  { path: "/freight-delivery-witbank", component: LocationPage },
  { path: "/fridge-delivery--alberton", component: LocationPage },
  { path: "/fridge-delivery--aliwal-north", component: LocationPage },
  { path: "/fridge-delivery--beaufort-west", component: LocationPage },
  { path: "/fridge-delivery--benoni", component: LocationPage },
  { path: "/fridge-delivery--bethal", component: LocationPage },
  { path: "/fridge-delivery--bhisho", component: LocationPage },
  { path: "/fridge-delivery--bloemfontein", component: LocationPage },
  { path: "/fridge-delivery--boksburg", component: LocationPage },
  { path: "/fridge-delivery--brakpan", component: LocationPage },
  { path: "/fridge-delivery--brits", component: LocationPage },
  { path: "/fridge-delivery--cape-town", component: LocationPage },
  { path: "/fridge-delivery--carletonville", component: LocationPage },
  { path: "/fridge-delivery--centurion", component: LocationPage },
  { path: "/fridge-delivery--cradock", component: LocationPage },
  { path: "/fridge-delivery--de-aar", component: LocationPage },
  { path: "/fridge-delivery--durban", component: LocationPage },
  { path: "/fridge-delivery--east-london", component: LocationPage },
  { path: "/fridge-delivery--ekurhuleni", component: LocationPage },
  { path: "/fridge-delivery--ferndale", component: LocationPage },
  { path: "/fridge-delivery--george", component: LocationPage },
  { path: "/fridge-delivery--gqeberha", component: LocationPage },
  { path: "/fridge-delivery--graaff-reinet", component: LocationPage },
  { path: "/fridge-delivery--grahamstown", component: LocationPage },
  { path: "/fridge-delivery--hermanus", component: LocationPage },
  { path: "/fridge-delivery--jeffreys-bay", component: LocationPage },
  { path: "/fridge-delivery--johannesburg", component: LocationPage },
  { path: "/fridge-delivery--kempton-park", component: LocationPage },
  { path: "/fridge-delivery--kimberley", component: LocationPage },
  { path: "/fridge-delivery--klerksdorp", component: LocationPage },
  { path: "/fridge-delivery--knysna", component: LocationPage },
  { path: "/fridge-delivery--kroonstad", component: LocationPage },
  { path: "/fridge-delivery--kuilsrivier", component: LocationPage },
  { path: "/fridge-delivery--ladysmith", component: LocationPage },
  { path: "/fridge-delivery--lebowakgomo", component: LocationPage },
  { path: "/fridge-delivery--lichtenburg", component: LocationPage },
  { path: "/fridge-delivery--mahikeng", component: LocationPage },
  { path: "/fridge-delivery--malmesbury", component: LocationPage },
  { path: "/fridge-delivery--middelburg", component: LocationPage },
  { path: "/fridge-delivery--midrand", component: LocationPage },
  { path: "/fridge-delivery--mitchells-plain", component: LocationPage },
  { path: "/fridge-delivery--mmabatho", component: LocationPage },
  { path: "/fridge-delivery--mossel-bay", component: LocationPage },
  { path: "/fridge-delivery--musina", component: LocationPage },
  { path: "/fridge-delivery--nelspruit", component: LocationPage },
  { path: "/fridge-delivery--oudtshoorn", component: LocationPage },
  { path: "/fridge-delivery--paarl", component: LocationPage },
  { path: "/fridge-delivery--pietermaritzburg", component: LocationPage },
  { path: "/fridge-delivery--polokwane", component: LocationPage },
  { path: "/fridge-delivery--port-elizabeth", component: LocationPage },
  { path: "/fridge-delivery--port-shepstone", component: LocationPage },
  { path: "/fridge-delivery--potchefstroom", component: LocationPage },
  { path: "/fridge-delivery--pretoria", component: LocationPage },
  { path: "/fridge-delivery--queenstown", component: LocationPage },
  { path: "/fridge-delivery--randburg", component: LocationPage },
  { path: "/fridge-delivery--roodepoort", component: LocationPage },
  { path: "/fridge-delivery--rustenburg", component: LocationPage },
  { path: "/fridge-delivery--saldanha", component: LocationPage },
  { path: "/fridge-delivery--sandton", component: LocationPage },
  { path: "/fridge-delivery--soweto", component: LocationPage },
  { path: "/fridge-delivery--springs", component: LocationPage },
  { path: "/fridge-delivery--standerton", component: LocationPage },
  { path: "/fridge-delivery--tembisa", component: LocationPage },
  { path: "/fridge-delivery--thohoyandou", component: LocationPage },
  { path: "/fridge-delivery--tzaneen", component: LocationPage },
  { path: "/fridge-delivery--uitenhage", component: LocationPage },
  { path: "/fridge-delivery--ulundi", component: LocationPage },
  { path: "/fridge-delivery--umtata", component: LocationPage },
  { path: "/fridge-delivery--upington", component: LocationPage },
  { path: "/fridge-delivery--vanderbijlpark", component: LocationPage },
  { path: "/fridge-delivery--vereeniging", component: LocationPage },
  { path: "/fridge-delivery--volksrust", component: LocationPage },
  { path: "/fridge-delivery--vredenburg", component: LocationPage },
  { path: "/fridge-delivery--vryburg", component: LocationPage },
  { path: "/fridge-delivery--vryheid", component: LocationPage },
  { path: "/fridge-delivery--welkom", component: LocationPage },
  { path: "/fridge-delivery--witbank", component: LocationPage },
  { path: "/fridge-delivery--worcester", component: LocationPage },
  { path: "/furniture-delivery-boksburg", component: LocationPage },
  { path: "/furniture-delivery-ekurhuleni", component: LocationPage },
  { path: "/furniture-delivery-gqeberha", component: LocationPage },
  { path: "/furniture-delivery-kempton-park", component: LocationPage },
  { path: "/furniture-delivery-mitchells-plain", component: LocationPage },
  { path: "/furniture-delivery-services--alberton", component: LocationPage },
  { path: "/furniture-delivery-services--aliwal-north", component: LocationPage },
  { path: "/furniture-delivery-services--beaufort-west", component: LocationPage },
  { path: "/furniture-delivery-services--benoni", component: LocationPage },
  { path: "/furniture-delivery-services--bethal", component: LocationPage },
  { path: "/furniture-delivery-services--bhisho", component: LocationPage },
  { path: "/furniture-delivery-services--bloemfontein", component: LocationPage },
  { path: "/furniture-delivery-services--boksburg", component: LocationPage },
  { path: "/furniture-delivery-services--brakpan", component: LocationPage },
  { path: "/furniture-delivery-services--brits", component: LocationPage },
  { path: "/furniture-delivery-services--cape-town", component: LocationPage },
  { path: "/furniture-delivery-services--carletonville", component: LocationPage },
  { path: "/furniture-delivery-services--centurion", component: LocationPage },
  { path: "/furniture-delivery-services--cradock", component: LocationPage },
  { path: "/furniture-delivery-services--de-aar", component: LocationPage },
  { path: "/furniture-delivery-services--durban", component: LocationPage },
  { path: "/furniture-delivery-services--east-london", component: LocationPage },
  { path: "/furniture-delivery-services--ekurhuleni", component: LocationPage },
  { path: "/furniture-delivery-services--ferndale", component: LocationPage },
  { path: "/furniture-delivery-services--george", component: LocationPage },
  { path: "/furniture-delivery-services--gqeberha", component: LocationPage },
  { path: "/furniture-delivery-services--graaff-reinet", component: LocationPage },
  { path: "/furniture-delivery-services--grahamstown", component: LocationPage },
  { path: "/furniture-delivery-services--hermanus", component: LocationPage },
  { path: "/furniture-delivery-services--jeffreys-bay", component: LocationPage },
  { path: "/furniture-delivery-services--johannesburg", component: LocationPage },
  { path: "/furniture-delivery-services--kempton-park", component: LocationPage },
  { path: "/furniture-delivery-services--kimberley", component: LocationPage },
  { path: "/furniture-delivery-services--klerksdorp", component: LocationPage },
  { path: "/furniture-delivery-services--knysna", component: LocationPage },
  { path: "/furniture-delivery-services--kroonstad", component: LocationPage },
  { path: "/furniture-delivery-services--kuilsrivier", component: LocationPage },
  { path: "/furniture-delivery-services--ladysmith", component: LocationPage },
  { path: "/furniture-delivery-services--lebowakgomo", component: LocationPage },
  { path: "/furniture-delivery-services--lichtenburg", component: LocationPage },
  { path: "/furniture-delivery-services--mahikeng", component: LocationPage },
  { path: "/furniture-delivery-services--malmesbury", component: LocationPage },
  { path: "/furniture-delivery-services--middelburg", component: LocationPage },
  { path: "/furniture-delivery-services--midrand", component: LocationPage },
  { path: "/furniture-delivery-services--mitchells-plain", component: LocationPage },
  { path: "/furniture-delivery-services--mmabatho", component: LocationPage },
  { path: "/furniture-delivery-services--mossel-bay", component: LocationPage },
  { path: "/furniture-delivery-services--musina", component: LocationPage },
  { path: "/furniture-delivery-services--nelspruit", component: LocationPage },
  { path: "/furniture-delivery-services--oudtshoorn", component: LocationPage },
  { path: "/furniture-delivery-services--paarl", component: LocationPage },
  { path: "/furniture-delivery-services--pietermaritzburg", component: LocationPage },
  { path: "/furniture-delivery-services--polokwane", component: LocationPage },
  { path: "/furniture-delivery-services--port-elizabeth", component: LocationPage },
  { path: "/furniture-delivery-services--port-shepstone", component: LocationPage },
  { path: "/furniture-delivery-services--potchefstroom", component: LocationPage },
  { path: "/furniture-delivery-services--pretoria", component: LocationPage },
  { path: "/furniture-delivery-services--queenstown", component: LocationPage },
  { path: "/furniture-delivery-services--randburg", component: LocationPage },
  { path: "/furniture-delivery-services--roodepoort", component: LocationPage },
  { path: "/furniture-delivery-services--rustenburg", component: LocationPage },
  { path: "/furniture-delivery-services--saldanha", component: LocationPage },
  { path: "/furniture-delivery-services--sandton", component: LocationPage },
  { path: "/furniture-delivery-services--soweto", component: LocationPage },
  { path: "/furniture-delivery-services--springs", component: LocationPage },
  { path: "/furniture-delivery-services--standerton", component: LocationPage },
  { path: "/furniture-delivery-services--tembisa", component: LocationPage },
  { path: "/furniture-delivery-services--thohoyandou", component: LocationPage },
  { path: "/furniture-delivery-services--tzaneen", component: LocationPage },
  { path: "/furniture-delivery-services--uitenhage", component: LocationPage },
  { path: "/furniture-delivery-services--ulundi", component: LocationPage },
  { path: "/furniture-delivery-services--umtata", component: LocationPage },
  { path: "/furniture-delivery-services--upington", component: LocationPage },
  { path: "/furniture-delivery-services--vanderbijlpark", component: LocationPage },
  { path: "/furniture-delivery-services--vereeniging", component: LocationPage },
  { path: "/furniture-delivery-services--volksrust", component: LocationPage },
  { path: "/furniture-delivery-services--vredenburg", component: LocationPage },
  { path: "/furniture-delivery-services--vryburg", component: LocationPage },
  { path: "/furniture-delivery-services--vryheid", component: LocationPage },
  { path: "/furniture-delivery-services--welkom", component: LocationPage },
  { path: "/furniture-delivery-services--witbank", component: LocationPage },
  { path: "/furniture-delivery-services--worcester", component: LocationPage },
  { path: "/furniture-delivery-tembisa", component: LocationPage },
  { path: "/furniture-delivery-witbank", component: LocationPage },
  { path: "/furniture-movers-boksburg", component: LocationPage },
  { path: "/furniture-movers-ekurhuleni", component: LocationPage },
  { path: "/furniture-movers-gqeberha", component: LocationPage },
  { path: "/furniture-movers-kempton-park", component: LocationPage },
  { path: "/furniture-movers-mitchells-plain", component: LocationPage },
  { path: "/furniture-movers-tembisa", component: LocationPage },
  { path: "/furniture-movers-witbank", component: LocationPage },
  { path: "/goods-transport-boksburg", component: LocationPage },
  { path: "/goods-transport-ekurhuleni", component: LocationPage },
  { path: "/goods-transport-gqeberha", component: LocationPage },
  { path: "/goods-transport-kempton-park", component: LocationPage },
  { path: "/goods-transport-mitchells-plain", component: LocationPage },
  { path: "/goods-transport-tembisa", component: LocationPage },
  { path: "/goods-transport-witbank", component: LocationPage },
  { path: "/heavy-equipment-delivery-boksburg", component: LocationPage },
  { path: "/heavy-equipment-delivery-ekurhuleni", component: LocationPage },
  { path: "/heavy-equipment-delivery-gqeberha", component: LocationPage },
  { path: "/heavy-equipment-delivery-kempton-park", component: LocationPage },
  { path: "/heavy-equipment-delivery-mitchells-plain", component: LocationPage },
  { path: "/heavy-equipment-delivery-tembisa", component: LocationPage },
  { path: "/heavy-equipment-delivery-witbank", component: LocationPage },
  { path: "/house-moves--alberton", component: LocationPage },
  { path: "/house-moves--aliwal-north", component: LocationPage },
  { path: "/house-moves--beaufort-west", component: LocationPage },
  { path: "/house-moves--benoni", component: LocationPage },
  { path: "/house-moves--bethal", component: LocationPage },
  { path: "/house-moves--bhisho", component: LocationPage },
  { path: "/house-moves--bloemfontein", component: LocationPage },
  { path: "/house-moves--boksburg", component: LocationPage },
  { path: "/house-moves--brakpan", component: LocationPage },
  { path: "/house-moves--brits", component: LocationPage },
  { path: "/house-moves--cape-town", component: LocationPage },
  { path: "/house-moves--carletonville", component: LocationPage },
  { path: "/house-moves--centurion", component: LocationPage },
  { path: "/house-moves--cradock", component: LocationPage },
  { path: "/house-moves--de-aar", component: LocationPage },
  { path: "/house-moves--durban", component: LocationPage },
  { path: "/house-moves--east-london", component: LocationPage },
  { path: "/house-moves--ekurhuleni", component: LocationPage },
  { path: "/house-moves--ferndale", component: LocationPage },
  { path: "/house-moves--george", component: LocationPage },
  { path: "/house-moves--gqeberha", component: LocationPage },
  { path: "/house-moves--graaff-reinet", component: LocationPage },
  { path: "/house-moves--grahamstown", component: LocationPage },
  { path: "/house-moves--hermanus", component: LocationPage },
  { path: "/house-moves--jeffreys-bay", component: LocationPage },
  { path: "/house-moves--johannesburg", component: LocationPage },
  { path: "/house-moves--kempton-park", component: LocationPage },
  { path: "/house-moves--kimberley", component: LocationPage },
  { path: "/house-moves--klerksdorp", component: LocationPage },
  { path: "/house-moves--knysna", component: LocationPage },
  { path: "/house-moves--kroonstad", component: LocationPage },
  { path: "/house-moves--kuilsrivier", component: LocationPage },
  { path: "/house-moves--ladysmith", component: LocationPage },
  { path: "/house-moves--lebowakgomo", component: LocationPage },
  { path: "/house-moves--lichtenburg", component: LocationPage },
  { path: "/house-moves--mahikeng", component: LocationPage },
  { path: "/house-moves--malmesbury", component: LocationPage },
  { path: "/house-moves--middelburg", component: LocationPage },
  { path: "/house-moves--midrand", component: LocationPage },
  { path: "/house-moves--mitchells-plain", component: LocationPage },
  { path: "/house-moves--mmabatho", component: LocationPage },
  { path: "/house-moves--mossel-bay", component: LocationPage },
  { path: "/house-moves--musina", component: LocationPage },
  { path: "/house-moves--nelspruit", component: LocationPage },
  { path: "/house-moves--oudtshoorn", component: LocationPage },
  { path: "/house-moves--paarl", component: LocationPage },
  { path: "/house-moves--pietermaritzburg", component: LocationPage },
  { path: "/house-moves--polokwane", component: LocationPage },
  { path: "/house-moves--port-elizabeth", component: LocationPage },
  { path: "/house-moves--port-shepstone", component: LocationPage },
  { path: "/house-moves--potchefstroom", component: LocationPage },
  { path: "/house-moves--pretoria", component: LocationPage },
  { path: "/house-moves--queenstown", component: LocationPage },
  { path: "/house-moves--randburg", component: LocationPage },
  { path: "/house-moves--roodepoort", component: LocationPage },
  { path: "/house-moves--rustenburg", component: LocationPage },
  { path: "/house-moves--saldanha", component: LocationPage },
  { path: "/house-moves--sandton", component: LocationPage },
  { path: "/house-moves--soweto", component: LocationPage },
  { path: "/house-moves--springs", component: LocationPage },
  { path: "/house-moves--standerton", component: LocationPage },
  { path: "/house-moves--tembisa", component: LocationPage },
  { path: "/house-moves--thohoyandou", component: LocationPage },
  { path: "/house-moves--tzaneen", component: LocationPage },
  { path: "/house-moves--uitenhage", component: LocationPage },
  { path: "/house-moves--ulundi", component: LocationPage },
  { path: "/house-moves--umtata", component: LocationPage },
  { path: "/house-moves--upington", component: LocationPage },
  { path: "/house-moves--vanderbijlpark", component: LocationPage },
  { path: "/house-moves--vereeniging", component: LocationPage },
  { path: "/house-moves--volksrust", component: LocationPage },
  { path: "/house-moves--vredenburg", component: LocationPage },
  { path: "/house-moves--vryburg", component: LocationPage },
  { path: "/house-moves--vryheid", component: LocationPage },
  { path: "/house-moves--welkom", component: LocationPage },
  { path: "/house-moves--witbank", component: LocationPage },
  { path: "/house-moves--worcester", component: LocationPage },
  { path: "/last-mile-delivery--alberton", component: LocationPage },
  { path: "/last-mile-delivery--aliwal-north", component: LocationPage },
  { path: "/last-mile-delivery--beaufort-west", component: LocationPage },
  { path: "/last-mile-delivery--benoni", component: LocationPage },
  { path: "/last-mile-delivery--bethal", component: LocationPage },
  { path: "/last-mile-delivery--bhisho", component: LocationPage },
  { path: "/last-mile-delivery--bloemfontein", component: LocationPage },
  { path: "/last-mile-delivery--boksburg", component: LocationPage },
  { path: "/last-mile-delivery--brakpan", component: LocationPage },
  { path: "/last-mile-delivery--brits", component: LocationPage },
  { path: "/last-mile-delivery--cape-town", component: LocationPage },
  { path: "/last-mile-delivery--carletonville", component: LocationPage },
  { path: "/last-mile-delivery--centurion", component: LocationPage },
  { path: "/last-mile-delivery--cradock", component: LocationPage },
  { path: "/last-mile-delivery--de-aar", component: LocationPage },
  { path: "/last-mile-delivery--durban", component: LocationPage },
  { path: "/last-mile-delivery--east-london", component: LocationPage },
  { path: "/last-mile-delivery--ekurhuleni", component: LocationPage },
  { path: "/last-mile-delivery--ferndale", component: LocationPage },
  { path: "/last-mile-delivery--george", component: LocationPage },
  { path: "/last-mile-delivery--gqeberha", component: LocationPage },
  { path: "/last-mile-delivery--graaff-reinet", component: LocationPage },
  { path: "/last-mile-delivery--grahamstown", component: LocationPage },
  { path: "/last-mile-delivery--hermanus", component: LocationPage },
  { path: "/last-mile-delivery--jeffreys-bay", component: LocationPage },
  { path: "/last-mile-delivery--johannesburg", component: LocationPage },
  { path: "/last-mile-delivery--kempton-park", component: LocationPage },
  { path: "/last-mile-delivery--kimberley", component: LocationPage },
  { path: "/last-mile-delivery--klerksdorp", component: LocationPage },
  { path: "/last-mile-delivery--knysna", component: LocationPage },
  { path: "/last-mile-delivery--kroonstad", component: LocationPage },
  { path: "/last-mile-delivery--kuilsrivier", component: LocationPage },
  { path: "/last-mile-delivery--ladysmith", component: LocationPage },
  { path: "/last-mile-delivery--lebowakgomo", component: LocationPage },
  { path: "/last-mile-delivery--lichtenburg", component: LocationPage },
  { path: "/last-mile-delivery--mahikeng", component: LocationPage },
  { path: "/last-mile-delivery--malmesbury", component: LocationPage },
  { path: "/last-mile-delivery--middelburg", component: LocationPage },
  { path: "/last-mile-delivery--midrand", component: LocationPage },
  { path: "/last-mile-delivery--mitchells-plain", component: LocationPage },
  { path: "/last-mile-delivery--mmabatho", component: LocationPage },
  { path: "/last-mile-delivery--mossel-bay", component: LocationPage },
  { path: "/last-mile-delivery--musina", component: LocationPage },
  { path: "/last-mile-delivery--nelspruit", component: LocationPage },
  { path: "/last-mile-delivery--oudtshoorn", component: LocationPage },
  { path: "/last-mile-delivery--paarl", component: LocationPage },
  { path: "/last-mile-delivery--pietermaritzburg", component: LocationPage },
  { path: "/last-mile-delivery--polokwane", component: LocationPage },
  { path: "/last-mile-delivery--port-elizabeth", component: LocationPage },
  { path: "/last-mile-delivery--port-shepstone", component: LocationPage },
  { path: "/last-mile-delivery--potchefstroom", component: LocationPage },
  { path: "/last-mile-delivery--pretoria", component: LocationPage },
  { path: "/last-mile-delivery--queenstown", component: LocationPage },
  { path: "/last-mile-delivery--randburg", component: LocationPage },
  { path: "/last-mile-delivery--roodepoort", component: LocationPage },
  { path: "/last-mile-delivery--rustenburg", component: LocationPage },
  { path: "/last-mile-delivery--saldanha", component: LocationPage },
  { path: "/last-mile-delivery--sandton", component: LocationPage },
  { path: "/last-mile-delivery--soweto", component: LocationPage },
  { path: "/last-mile-delivery--springs", component: LocationPage },
  { path: "/last-mile-delivery--standerton", component: LocationPage },
  { path: "/last-mile-delivery--tembisa", component: LocationPage },
  { path: "/last-mile-delivery--thohoyandou", component: LocationPage },
  { path: "/last-mile-delivery--tzaneen", component: LocationPage },
  { path: "/last-mile-delivery--uitenhage", component: LocationPage },
  { path: "/last-mile-delivery--ulundi", component: LocationPage },
  { path: "/last-mile-delivery--umtata", component: LocationPage },
  { path: "/last-mile-delivery--upington", component: LocationPage },
  { path: "/last-mile-delivery--vanderbijlpark", component: LocationPage },
  { path: "/last-mile-delivery--vereeniging", component: LocationPage },
  { path: "/last-mile-delivery--volksrust", component: LocationPage },
  { path: "/last-mile-delivery--vredenburg", component: LocationPage },
  { path: "/last-mile-delivery--vryburg", component: LocationPage },
  { path: "/last-mile-delivery--vryheid", component: LocationPage },
  { path: "/last-mile-delivery--welkom", component: LocationPage },
  { path: "/last-mile-delivery--witbank", component: LocationPage },
  { path: "/last-mile-delivery--worcester", component: LocationPage },
  { path: "/local-delivery-boksburg", component: LocationPage },
  { path: "/local-delivery-ekurhuleni", component: LocationPage },
  { path: "/local-delivery-gqeberha", component: LocationPage },
  { path: "/local-delivery-kempton-park", component: LocationPage },
  { path: "/local-delivery-mitchells-plain", component: LocationPage },
  { path: "/local-delivery-tembisa", component: LocationPage },
  { path: "/local-delivery-witbank", component: LocationPage },
  { path: "/local-movers-near-me-boksburg", component: LocationPage },
  { path: "/local-movers-near-me-ekurhuleni", component: LocationPage },
  { path: "/local-movers-near-me-gqeberha", component: LocationPage },
  { path: "/local-movers-near-me-kempton-park", component: LocationPage },
  { path: "/local-movers-near-me-mitchells-plain", component: LocationPage },
  { path: "/local-movers-near-me-tembisa", component: LocationPage },
  { path: "/local-movers-near-me-witbank", component: LocationPage },
  { path: "/long-distance-boksburg", component: LocationPage },
  { path: "/long-distance-ekurhuleni", component: LocationPage },
  { path: "/long-distance-gqeberha", component: LocationPage },
  { path: "/long-distance-kempton-park", component: LocationPage },
  { path: "/long-distance-mitchells-plain", component: LocationPage },
  { path: "/long-distance-tembisa", component: LocationPage },
  { path: "/long-distance-witbank", component: LocationPage },
  { path: "/mattress-delivery--alberton", component: LocationPage },
  { path: "/mattress-delivery--aliwal-north", component: LocationPage },
  { path: "/mattress-delivery--beaufort-west", component: LocationPage },
  { path: "/mattress-delivery--benoni", component: LocationPage },
  { path: "/mattress-delivery--bethal", component: LocationPage },
  { path: "/mattress-delivery--bhisho", component: LocationPage },
  { path: "/mattress-delivery--bloemfontein", component: LocationPage },
  { path: "/mattress-delivery--boksburg", component: LocationPage },
  { path: "/mattress-delivery--brakpan", component: LocationPage },
  { path: "/mattress-delivery--brits", component: LocationPage },
  { path: "/mattress-delivery--cape-town", component: LocationPage },
  { path: "/mattress-delivery--carletonville", component: LocationPage },
  { path: "/mattress-delivery--centurion", component: LocationPage },
  { path: "/mattress-delivery--cradock", component: LocationPage },
  { path: "/mattress-delivery--de-aar", component: LocationPage },
  { path: "/mattress-delivery--durban", component: LocationPage },
  { path: "/mattress-delivery--east-london", component: LocationPage },
  { path: "/mattress-delivery--ekurhuleni", component: LocationPage },
  { path: "/mattress-delivery--ferndale", component: LocationPage },
  { path: "/mattress-delivery--george", component: LocationPage },
  { path: "/mattress-delivery--gqeberha", component: LocationPage },
  { path: "/mattress-delivery--graaff-reinet", component: LocationPage },
  { path: "/mattress-delivery--grahamstown", component: LocationPage },
  { path: "/mattress-delivery--hermanus", component: LocationPage },
  { path: "/mattress-delivery--jeffreys-bay", component: LocationPage },
  { path: "/mattress-delivery--johannesburg", component: LocationPage },
  { path: "/mattress-delivery--kempton-park", component: LocationPage },
  { path: "/mattress-delivery--kimberley", component: LocationPage },
  { path: "/mattress-delivery--klerksdorp", component: LocationPage },
  { path: "/mattress-delivery--knysna", component: LocationPage },
  { path: "/mattress-delivery--kroonstad", component: LocationPage },
  { path: "/mattress-delivery--kuilsrivier", component: LocationPage },
  { path: "/mattress-delivery--ladysmith", component: LocationPage },
  { path: "/mattress-delivery--lebowakgomo", component: LocationPage },
  { path: "/mattress-delivery--lichtenburg", component: LocationPage },
  { path: "/mattress-delivery--mahikeng", component: LocationPage },
  { path: "/mattress-delivery--malmesbury", component: LocationPage },
  { path: "/mattress-delivery--middelburg", component: LocationPage },
  { path: "/mattress-delivery--midrand", component: LocationPage },
  { path: "/mattress-delivery--mitchells-plain", component: LocationPage },
  { path: "/mattress-delivery--mmabatho", component: LocationPage },
  { path: "/mattress-delivery--mossel-bay", component: LocationPage },
  { path: "/mattress-delivery--musina", component: LocationPage },
  { path: "/mattress-delivery--nelspruit", component: LocationPage },
  { path: "/mattress-delivery--oudtshoorn", component: LocationPage },
  { path: "/mattress-delivery--paarl", component: LocationPage },
  { path: "/mattress-delivery--pietermaritzburg", component: LocationPage },
  { path: "/mattress-delivery--polokwane", component: LocationPage },
  { path: "/mattress-delivery--port-elizabeth", component: LocationPage },
  { path: "/mattress-delivery--port-shepstone", component: LocationPage },
  { path: "/mattress-delivery--potchefstroom", component: LocationPage },
  { path: "/mattress-delivery--pretoria", component: LocationPage },
  { path: "/mattress-delivery--queenstown", component: LocationPage },
  { path: "/mattress-delivery--randburg", component: LocationPage },
  { path: "/mattress-delivery--roodepoort", component: LocationPage },
  { path: "/mattress-delivery--rustenburg", component: LocationPage },
  { path: "/mattress-delivery--saldanha", component: LocationPage },
  { path: "/mattress-delivery--sandton", component: LocationPage },
  { path: "/mattress-delivery--soweto", component: LocationPage },
  { path: "/mattress-delivery--springs", component: LocationPage },
  { path: "/mattress-delivery--standerton", component: LocationPage },
  { path: "/mattress-delivery--tembisa", component: LocationPage },
  { path: "/mattress-delivery--thohoyandou", component: LocationPage },
  { path: "/mattress-delivery--tzaneen", component: LocationPage },
  { path: "/mattress-delivery--uitenhage", component: LocationPage },
  { path: "/mattress-delivery--ulundi", component: LocationPage },
  { path: "/mattress-delivery--umtata", component: LocationPage },
  { path: "/mattress-delivery--upington", component: LocationPage },
  { path: "/mattress-delivery--vanderbijlpark", component: LocationPage },
  { path: "/mattress-delivery--vereeniging", component: LocationPage },
  { path: "/mattress-delivery--volksrust", component: LocationPage },
  { path: "/mattress-delivery--vredenburg", component: LocationPage },
  { path: "/mattress-delivery--vryburg", component: LocationPage },
  { path: "/mattress-delivery--vryheid", component: LocationPage },
  { path: "/mattress-delivery--welkom", component: LocationPage },
  { path: "/mattress-delivery--witbank", component: LocationPage },
  { path: "/mattress-delivery--worcester", component: LocationPage },
  { path: "/moving-boksburg", component: LocationPage },
  { path: "/moving-ekurhuleni", component: LocationPage },
  { path: "/moving-gqeberha", component: LocationPage },
  { path: "/moving-kempton-park", component: LocationPage },
  { path: "/moving-mitchells-plain", component: LocationPage },
  { path: "/moving-out-boksburg", component: LocationPage },
  { path: "/moving-out-ekurhuleni", component: LocationPage },
  { path: "/moving-out-gqeberha", component: LocationPage },
  { path: "/moving-out-kempton-park", component: LocationPage },
  { path: "/moving-out-mitchells-plain", component: LocationPage },
  { path: "/moving-out-tembisa", component: LocationPage },
  { path: "/moving-out-witbank", component: LocationPage },
  { path: "/moving-tembisa", component: LocationPage },
  { path: "/moving-witbank", component: LocationPage },
  { path: "/need-delivery-boksburg", component: LocationPage },
  { path: "/need-delivery-ekurhuleni", component: LocationPage },
  { path: "/need-delivery-gqeberha", component: LocationPage },
  { path: "/need-delivery-kempton-park", component: LocationPage },
  { path: "/need-delivery-mitchells-plain", component: LocationPage },
  { path: "/need-delivery-tembisa", component: LocationPage },
  { path: "/need-delivery-witbank", component: LocationPage },
  { path: "/office-removals--alberton", component: LocationPage },
  { path: "/office-removals--aliwal-north", component: LocationPage },
  { path: "/office-removals--beaufort-west", component: LocationPage },
  { path: "/office-removals--benoni", component: LocationPage },
  { path: "/office-removals--bethal", component: LocationPage },
  { path: "/office-removals--bhisho", component: LocationPage },
  { path: "/office-removals--bloemfontein", component: LocationPage },
  { path: "/office-removals--boksburg", component: LocationPage },
  { path: "/office-removals--brakpan", component: LocationPage },
  { path: "/office-removals--brits", component: LocationPage },
  { path: "/office-removals--cape-town", component: LocationPage },
  { path: "/office-removals--carletonville", component: LocationPage },
  { path: "/office-removals--centurion", component: LocationPage },
  { path: "/office-removals--cradock", component: LocationPage },
  { path: "/office-removals--de-aar", component: LocationPage },
  { path: "/office-removals--durban", component: LocationPage },
  { path: "/office-removals--east-london", component: LocationPage },
  { path: "/office-removals--ekurhuleni", component: LocationPage },
  { path: "/office-removals--ferndale", component: LocationPage },
  { path: "/office-removals--george", component: LocationPage },
  { path: "/office-removals--gqeberha", component: LocationPage },
  { path: "/office-removals--graaff-reinet", component: LocationPage },
  { path: "/office-removals--grahamstown", component: LocationPage },
  { path: "/office-removals--hermanus", component: LocationPage },
  { path: "/office-removals--jeffreys-bay", component: LocationPage },
  { path: "/office-removals--johannesburg", component: LocationPage },
  { path: "/office-removals--kempton-park", component: LocationPage },
  { path: "/office-removals--kimberley", component: LocationPage },
  { path: "/office-removals--klerksdorp", component: LocationPage },
  { path: "/office-removals--knysna", component: LocationPage },
  { path: "/office-removals--kroonstad", component: LocationPage },
  { path: "/office-removals--kuilsrivier", component: LocationPage },
  { path: "/office-removals--ladysmith", component: LocationPage },
  { path: "/office-removals--lebowakgomo", component: LocationPage },
  { path: "/office-removals--lichtenburg", component: LocationPage },
  { path: "/office-removals--mahikeng", component: LocationPage },
  { path: "/office-removals--malmesbury", component: LocationPage },
  { path: "/office-removals--middelburg", component: LocationPage },
  { path: "/office-removals--midrand", component: LocationPage },
  { path: "/office-removals--mitchells-plain", component: LocationPage },
  { path: "/office-removals--mmabatho", component: LocationPage },
  { path: "/office-removals--mossel-bay", component: LocationPage },
  { path: "/office-removals--musina", component: LocationPage },
  { path: "/office-removals--nelspruit", component: LocationPage },
  { path: "/office-removals--oudtshoorn", component: LocationPage },
  { path: "/office-removals--paarl", component: LocationPage },
  { path: "/office-removals--pietermaritzburg", component: LocationPage },
  { path: "/office-removals--polokwane", component: LocationPage },
  { path: "/office-removals--port-elizabeth", component: LocationPage },
  { path: "/office-removals--port-shepstone", component: LocationPage },
  { path: "/office-removals--potchefstroom", component: LocationPage },
  { path: "/office-removals--pretoria", component: LocationPage },
  { path: "/office-removals--queenstown", component: LocationPage },
  { path: "/office-removals--randburg", component: LocationPage },
  { path: "/office-removals--roodepoort", component: LocationPage },
  { path: "/office-removals--rustenburg", component: LocationPage },
  { path: "/office-removals--saldanha", component: LocationPage },
  { path: "/office-removals--sandton", component: LocationPage },
  { path: "/office-removals--soweto", component: LocationPage },
  { path: "/office-removals--springs", component: LocationPage },
  { path: "/office-removals--standerton", component: LocationPage },
  { path: "/office-removals--tembisa", component: LocationPage },
  { path: "/office-removals--thohoyandou", component: LocationPage },
  { path: "/office-removals--tzaneen", component: LocationPage },
  { path: "/office-removals--uitenhage", component: LocationPage },
  { path: "/office-removals--ulundi", component: LocationPage },
  { path: "/office-removals--umtata", component: LocationPage },
  { path: "/office-removals--upington", component: LocationPage },
  { path: "/office-removals--vanderbijlpark", component: LocationPage },
  { path: "/office-removals--vereeniging", component: LocationPage },
  { path: "/office-removals--volksrust", component: LocationPage },
  { path: "/office-removals--vredenburg", component: LocationPage },
  { path: "/office-removals--vryburg", component: LocationPage },
  { path: "/office-removals--vryheid", component: LocationPage },
  { path: "/office-removals--welkom", component: LocationPage },
  { path: "/office-removals--witbank", component: LocationPage },
  { path: "/office-removals--worcester", component: LocationPage },
  { path: "/one-way-truck-boksburg", component: LocationPage },
  { path: "/one-way-truck-ekurhuleni", component: LocationPage },
  { path: "/one-way-truck-gqeberha", component: LocationPage },
  { path: "/one-way-truck-kempton-park", component: LocationPage },
  { path: "/one-way-truck-mitchells-plain", component: LocationPage },
  { path: "/one-way-truck-tembisa", component: LocationPage },
  { path: "/one-way-truck-witbank", component: LocationPage },
  { path: "/online-order-delivery-boksburg", component: LocationPage },
  { path: "/online-order-delivery-ekurhuleni", component: LocationPage },
  { path: "/online-order-delivery-gqeberha", component: LocationPage },
  { path: "/online-order-delivery-kempton-park", component: LocationPage },
  { path: "/online-order-delivery-mitchells-plain", component: LocationPage },
  { path: "/online-order-delivery-tembisa", component: LocationPage },
  { path: "/online-order-delivery-witbank", component: LocationPage },
  { path: "/overnight-delivery-boksburg", component: LocationPage },
  { path: "/overnight-delivery-ekurhuleni", component: LocationPage },
  { path: "/overnight-delivery-gqeberha", component: LocationPage },
  { path: "/overnight-delivery-kempton-park", component: LocationPage },
  { path: "/overnight-delivery-mitchells-plain", component: LocationPage },
  { path: "/overnight-delivery-tembisa", component: LocationPage },
  { path: "/overnight-delivery-witbank", component: LocationPage },
  { path: "/package-delivery-boksburg", component: LocationPage },
  { path: "/package-delivery-ekurhuleni", component: LocationPage },
  { path: "/package-delivery-gqeberha", component: LocationPage },
  { path: "/package-delivery-kempton-park", component: LocationPage },
  { path: "/package-delivery-mitchells-plain", component: LocationPage },
  { path: "/package-delivery-tembisa", component: LocationPage },
  { path: "/package-delivery-witbank", component: LocationPage },
  { path: "/piano-moving--alberton", component: LocationPage },
  { path: "/piano-moving--aliwal-north", component: LocationPage },
  { path: "/piano-moving--beaufort-west", component: LocationPage },
  { path: "/piano-moving--benoni", component: LocationPage },
  { path: "/piano-moving--bethal", component: LocationPage },
  { path: "/piano-moving--bhisho", component: LocationPage },
  { path: "/piano-moving--bloemfontein", component: LocationPage },
  { path: "/piano-moving--boksburg", component: LocationPage },
  { path: "/piano-moving--brakpan", component: LocationPage },
  { path: "/piano-moving--brits", component: LocationPage },
  { path: "/piano-moving--cape-town", component: LocationPage },
  { path: "/piano-moving--carletonville", component: LocationPage },
  { path: "/piano-moving--centurion", component: LocationPage },
  { path: "/piano-moving--cradock", component: LocationPage },
  { path: "/piano-moving--de-aar", component: LocationPage },
  { path: "/piano-moving--durban", component: LocationPage },
  { path: "/piano-moving--east-london", component: LocationPage },
  { path: "/piano-moving--ekurhuleni", component: LocationPage },
  { path: "/piano-moving--ferndale", component: LocationPage },
  { path: "/piano-moving--george", component: LocationPage },
  { path: "/piano-moving--gqeberha", component: LocationPage },
  { path: "/piano-moving--graaff-reinet", component: LocationPage },
  { path: "/piano-moving--grahamstown", component: LocationPage },
  { path: "/piano-moving--hermanus", component: LocationPage },
  { path: "/piano-moving--jeffreys-bay", component: LocationPage },
  { path: "/piano-moving--johannesburg", component: LocationPage },
  { path: "/piano-moving--kempton-park", component: LocationPage },
  { path: "/piano-moving--kimberley", component: LocationPage },
  { path: "/piano-moving--klerksdorp", component: LocationPage },
  { path: "/piano-moving--knysna", component: LocationPage },
  { path: "/piano-moving--kroonstad", component: LocationPage },
  { path: "/piano-moving--kuilsrivier", component: LocationPage },
  { path: "/piano-moving--ladysmith", component: LocationPage },
  { path: "/piano-moving--lebowakgomo", component: LocationPage },
  { path: "/piano-moving--lichtenburg", component: LocationPage },
  { path: "/piano-moving--mahikeng", component: LocationPage },
  { path: "/piano-moving--malmesbury", component: LocationPage },
  { path: "/piano-moving--middelburg", component: LocationPage },
  { path: "/piano-moving--midrand", component: LocationPage },
  { path: "/piano-moving--mitchells-plain", component: LocationPage },
  { path: "/piano-moving--mmabatho", component: LocationPage },
  { path: "/piano-moving--mossel-bay", component: LocationPage },
  { path: "/piano-moving--musina", component: LocationPage },
  { path: "/piano-moving--nelspruit", component: LocationPage },
  { path: "/piano-moving--oudtshoorn", component: LocationPage },
  { path: "/piano-moving--paarl", component: LocationPage },
  { path: "/piano-moving--pietermaritzburg", component: LocationPage },
  { path: "/piano-moving--polokwane", component: LocationPage },
  { path: "/piano-moving--port-elizabeth", component: LocationPage },
  { path: "/piano-moving--port-shepstone", component: LocationPage },
  { path: "/piano-moving--potchefstroom", component: LocationPage },
  { path: "/piano-moving--pretoria", component: LocationPage },
  { path: "/piano-moving--queenstown", component: LocationPage },
  { path: "/piano-moving--randburg", component: LocationPage },
  { path: "/piano-moving--roodepoort", component: LocationPage },
  { path: "/piano-moving--rustenburg", component: LocationPage },
  { path: "/piano-moving--saldanha", component: LocationPage },
  { path: "/piano-moving--sandton", component: LocationPage },
  { path: "/piano-moving--soweto", component: LocationPage },
  { path: "/piano-moving--springs", component: LocationPage },
  { path: "/piano-moving--standerton", component: LocationPage },
  { path: "/piano-moving--tembisa", component: LocationPage },
  { path: "/piano-moving--thohoyandou", component: LocationPage },
  { path: "/piano-moving--tzaneen", component: LocationPage },
  { path: "/piano-moving--uitenhage", component: LocationPage },
  { path: "/piano-moving--ulundi", component: LocationPage },
  { path: "/piano-moving--umtata", component: LocationPage },
  { path: "/piano-moving--upington", component: LocationPage },
  { path: "/piano-moving--vanderbijlpark", component: LocationPage },
  { path: "/piano-moving--vereeniging", component: LocationPage },
  { path: "/piano-moving--volksrust", component: LocationPage },
  { path: "/piano-moving--vredenburg", component: LocationPage },
  { path: "/piano-moving--vryburg", component: LocationPage },
  { path: "/piano-moving--vryheid", component: LocationPage },
  { path: "/piano-moving--welkom", component: LocationPage },
  { path: "/piano-moving--witbank", component: LocationPage },
  { path: "/piano-moving--worcester", component: LocationPage },
  { path: "/quick-truck-hire-boksburg", component: LocationPage },
  { path: "/quick-truck-hire-ekurhuleni", component: LocationPage },
  { path: "/quick-truck-hire-gqeberha", component: LocationPage },
  { path: "/quick-truck-hire-kempton-park", component: LocationPage },
  { path: "/quick-truck-hire-mitchells-plain", component: LocationPage },
  { path: "/quick-truck-hire-tembisa", component: LocationPage },
  { path: "/quick-truck-hire-witbank", component: LocationPage },
  { path: "/quick-truck-rental-boksburg", component: LocationPage },
  { path: "/quick-truck-rental-ekurhuleni", component: LocationPage },
  { path: "/quick-truck-rental-gqeberha", component: LocationPage },
  { path: "/quick-truck-rental-kempton-park", component: LocationPage },
  { path: "/quick-truck-rental-mitchells-plain", component: LocationPage },
  { path: "/quick-truck-rental-tembisa", component: LocationPage },
  { path: "/quick-truck-rental-witbank", component: LocationPage },
  { path: "/removals-boksburg", component: LocationPage },
  { path: "/removals-ekurhuleni", component: LocationPage },
  { path: "/removals-gqeberha", component: LocationPage },
  { path: "/removals-kempton-park", component: LocationPage },
  { path: "/removals-mitchells-plain", component: LocationPage },
  { path: "/removals-tembisa", component: LocationPage },
  { path: "/removals-witbank", component: LocationPage },
  { path: "/rent-a-bakkie-boksburg", component: LocationPage },
  { path: "/rent-a-bakkie-ekurhuleni", component: LocationPage },
  { path: "/rent-a-bakkie-gqeberha", component: LocationPage },
  { path: "/rent-a-bakkie-kempton-park", component: LocationPage },
  { path: "/rent-a-bakkie-mitchells-plain", component: LocationPage },
  { path: "/rent-a-bakkie-tembisa", component: LocationPage },
  { path: "/rent-a-bakkie-witbank", component: LocationPage },
  { path: "/retail-delivery-boksburg", component: LocationPage },
  { path: "/retail-delivery-ekurhuleni", component: LocationPage },
  { path: "/retail-delivery-gqeberha", component: LocationPage },
  { path: "/retail-delivery-kempton-park", component: LocationPage },
  { path: "/retail-delivery-mitchells-plain", component: LocationPage },
  { path: "/retail-delivery-tembisa", component: LocationPage },
  { path: "/retail-delivery-witbank", component: LocationPage },
  { path: "/same-day-delivery-boksburg", component: LocationPage },
  { path: "/same-day-delivery-ekurhuleni", component: LocationPage },
  { path: "/same-day-delivery-gqeberha", component: LocationPage },
  { path: "/same-day-delivery-kempton-park", component: LocationPage },
  { path: "/same-day-delivery-mitchells-plain", component: LocationPage },
  { path: "/same-day-delivery-tembisa", component: LocationPage },
  { path: "/same-day-delivery-witbank", component: LocationPage },
  { path: "/student-moves--alberton", component: LocationPage },
  { path: "/student-moves--aliwal-north", component: LocationPage },
  { path: "/student-moves--beaufort-west", component: LocationPage },
  { path: "/student-moves--benoni", component: LocationPage },
  { path: "/student-moves--bethal", component: LocationPage },
  { path: "/student-moves--bhisho", component: LocationPage },
  { path: "/student-moves--bloemfontein", component: LocationPage },
  { path: "/student-moves--boksburg", component: LocationPage },
  { path: "/student-moves--brakpan", component: LocationPage },
  { path: "/student-moves--brits", component: LocationPage },
  { path: "/student-moves--cape-town", component: LocationPage },
  { path: "/student-moves--carletonville", component: LocationPage },
  { path: "/student-moves--centurion", component: LocationPage },
  { path: "/student-moves--cradock", component: LocationPage },
  { path: "/student-moves--de-aar", component: LocationPage },
  { path: "/student-moves--durban", component: LocationPage },
  { path: "/student-moves--east-london", component: LocationPage },
  { path: "/student-moves--ekurhuleni", component: LocationPage },
  { path: "/student-moves--ferndale", component: LocationPage },
  { path: "/student-moves--george", component: LocationPage },
  { path: "/student-moves--gqeberha", component: LocationPage },
  { path: "/student-moves--graaff-reinet", component: LocationPage },
  { path: "/student-moves--grahamstown", component: LocationPage },
  { path: "/student-moves--hermanus", component: LocationPage },
  { path: "/student-moves--jeffreys-bay", component: LocationPage },
  { path: "/student-moves--johannesburg", component: LocationPage },
  { path: "/student-moves--kempton-park", component: LocationPage },
  { path: "/student-moves--kimberley", component: LocationPage },
  { path: "/student-moves--klerksdorp", component: LocationPage },
  { path: "/student-moves--knysna", component: LocationPage },
  { path: "/student-moves--kroonstad", component: LocationPage },
  { path: "/student-moves--kuilsrivier", component: LocationPage },
  { path: "/student-moves--ladysmith", component: LocationPage },
  { path: "/student-moves--lebowakgomo", component: LocationPage },
  { path: "/student-moves--lichtenburg", component: LocationPage },
  { path: "/student-moves--mahikeng", component: LocationPage },
  { path: "/student-moves--malmesbury", component: LocationPage },
  { path: "/student-moves--middelburg", component: LocationPage },
  { path: "/student-moves--midrand", component: LocationPage },
  { path: "/student-moves--mitchells-plain", component: LocationPage },
  { path: "/student-moves--mmabatho", component: LocationPage },
  { path: "/student-moves--mossel-bay", component: LocationPage },
  { path: "/student-moves--musina", component: LocationPage },
  { path: "/student-moves--nelspruit", component: LocationPage },
  { path: "/student-moves--oudtshoorn", component: LocationPage },
  { path: "/student-moves--paarl", component: LocationPage },
  { path: "/student-moves--pietermaritzburg", component: LocationPage },
  { path: "/student-moves--polokwane", component: LocationPage },
  { path: "/student-moves--port-elizabeth", component: LocationPage },
  { path: "/student-moves--port-shepstone", component: LocationPage },
  { path: "/student-moves--potchefstroom", component: LocationPage },
  { path: "/student-moves--pretoria", component: LocationPage },
  { path: "/student-moves--queenstown", component: LocationPage },
  { path: "/student-moves--randburg", component: LocationPage },
  { path: "/student-moves--roodepoort", component: LocationPage },
  { path: "/student-moves--rustenburg", component: LocationPage },
  { path: "/student-moves--saldanha", component: LocationPage },
  { path: "/student-moves--sandton", component: LocationPage },
  { path: "/student-moves--soweto", component: LocationPage },
  { path: "/student-moves--springs", component: LocationPage },
  { path: "/student-moves--standerton", component: LocationPage },
  { path: "/student-moves--tembisa", component: LocationPage },
  { path: "/student-moves--thohoyandou", component: LocationPage },
  { path: "/student-moves--tzaneen", component: LocationPage },
  { path: "/student-moves--uitenhage", component: LocationPage },
  { path: "/student-moves--ulundi", component: LocationPage },
  { path: "/student-moves--umtata", component: LocationPage },
  { path: "/student-moves--upington", component: LocationPage },
  { path: "/student-moves--vanderbijlpark", component: LocationPage },
  { path: "/student-moves--vereeniging", component: LocationPage },
  { path: "/student-moves--volksrust", component: LocationPage },
  { path: "/student-moves--vredenburg", component: LocationPage },
  { path: "/student-moves--vryburg", component: LocationPage },
  { path: "/student-moves--vryheid", component: LocationPage },
  { path: "/student-moves--welkom", component: LocationPage },
  { path: "/student-moves--witbank", component: LocationPage },
  { path: "/student-moves--worcester", component: LocationPage },
  { path: "/transportation-boksburg", component: LocationPage },
  { path: "/transportation-company-boksburg", component: LocationPage },
  { path: "/transportation-company-ekurhuleni", component: LocationPage },
  { path: "/transportation-company-gqeberha", component: LocationPage },
  { path: "/transportation-company-kempton-park", component: LocationPage },
  { path: "/transportation-company-mitchells-plain", component: LocationPage },
  { path: "/transportation-company-tembisa", component: LocationPage },
  { path: "/transportation-company-witbank", component: LocationPage },
  { path: "/transportation-ekurhuleni", component: LocationPage },
  { path: "/transportation-gqeberha", component: LocationPage },
  { path: "/transportation-kempton-park", component: LocationPage },
  { path: "/transportation-mitchells-plain", component: LocationPage },
  { path: "/transportation-tembisa", component: LocationPage },
  { path: "/transportation-witbank", component: LocationPage },
  { path: "/truck-and-driver-hire-boksburg", component: LocationPage },
  { path: "/truck-and-driver-hire-ekurhuleni", component: LocationPage },
  { path: "/truck-and-driver-hire-gqeberha", component: LocationPage },
  { path: "/truck-and-driver-hire-kempton-park", component: LocationPage },
  { path: "/truck-and-driver-hire-mitchells-plain", component: LocationPage },
  { path: "/truck-and-driver-hire-tembisa", component: LocationPage },
  { path: "/truck-and-driver-hire-witbank", component: LocationPage },
  { path: "/truck-driver-jobs--alberton", component: LocationPage },
  { path: "/truck-driver-jobs--aliwal-north", component: LocationPage },
  { path: "/truck-driver-jobs--beaufort-west", component: LocationPage },
  { path: "/truck-driver-jobs--benoni", component: LocationPage },
  { path: "/truck-driver-jobs--bethal", component: LocationPage },
  { path: "/truck-driver-jobs--bhisho", component: LocationPage },
  { path: "/truck-driver-jobs--bloemfontein", component: LocationPage },
  { path: "/truck-driver-jobs--boksburg", component: LocationPage },
  { path: "/truck-driver-jobs--brakpan", component: LocationPage },
  { path: "/truck-driver-jobs--brits", component: LocationPage },
  { path: "/truck-driver-jobs--cape-town", component: LocationPage },
  { path: "/truck-driver-jobs--carletonville", component: LocationPage },
  { path: "/truck-driver-jobs--centurion", component: LocationPage },
  { path: "/truck-driver-jobs--cradock", component: LocationPage },
  { path: "/truck-driver-jobs--de-aar", component: LocationPage },
  { path: "/truck-driver-jobs--durban", component: LocationPage },
  { path: "/truck-driver-jobs--east-london", component: LocationPage },
  { path: "/truck-driver-jobs--ekurhuleni", component: LocationPage },
  { path: "/truck-driver-jobs--ferndale", component: LocationPage },
  { path: "/truck-driver-jobs--george", component: LocationPage },
  { path: "/truck-driver-jobs--gqeberha", component: LocationPage },
  { path: "/truck-driver-jobs--graaff-reinet", component: LocationPage },
  { path: "/truck-driver-jobs--grahamstown", component: LocationPage },
  { path: "/truck-driver-jobs--hermanus", component: LocationPage },
  { path: "/truck-driver-jobs--jeffreys-bay", component: LocationPage },
  { path: "/truck-driver-jobs--johannesburg", component: LocationPage },
  { path: "/truck-driver-jobs--kempton-park", component: LocationPage },
  { path: "/truck-driver-jobs--kimberley", component: LocationPage },
  { path: "/truck-driver-jobs--klerksdorp", component: LocationPage },
  { path: "/truck-driver-jobs--knysna", component: LocationPage },
  { path: "/truck-driver-jobs--kroonstad", component: LocationPage },
  { path: "/truck-driver-jobs--kuilsrivier", component: LocationPage },
  { path: "/truck-driver-jobs--ladysmith", component: LocationPage },
  { path: "/truck-driver-jobs--lebowakgomo", component: LocationPage },
  { path: "/truck-driver-jobs--lichtenburg", component: LocationPage },
  { path: "/truck-driver-jobs--mahikeng", component: LocationPage },
  { path: "/truck-driver-jobs--malmesbury", component: LocationPage },
  { path: "/truck-driver-jobs--middelburg", component: LocationPage },
  { path: "/truck-driver-jobs--midrand", component: LocationPage },
  { path: "/truck-driver-jobs--mitchells-plain", component: LocationPage },
  { path: "/truck-driver-jobs--mmabatho", component: LocationPage },
  { path: "/truck-driver-jobs--mossel-bay", component: LocationPage },
  { path: "/truck-driver-jobs--musina", component: LocationPage },
  { path: "/truck-driver-jobs--nelspruit", component: LocationPage },
  { path: "/truck-driver-jobs--oudtshoorn", component: LocationPage },
  { path: "/truck-driver-jobs--paarl", component: LocationPage },
  { path: "/truck-driver-jobs--pietermaritzburg", component: LocationPage },
  { path: "/truck-driver-jobs--polokwane", component: LocationPage },
  { path: "/truck-driver-jobs--port-elizabeth", component: LocationPage },
  { path: "/truck-driver-jobs--port-shepstone", component: LocationPage },
  { path: "/truck-driver-jobs--potchefstroom", component: LocationPage },
  { path: "/truck-driver-jobs--pretoria", component: LocationPage },
  { path: "/truck-driver-jobs--queenstown", component: LocationPage },
  { path: "/truck-driver-jobs--randburg", component: LocationPage },
  { path: "/truck-driver-jobs--roodepoort", component: LocationPage },
  { path: "/truck-driver-jobs--rustenburg", component: LocationPage },
  { path: "/truck-driver-jobs--saldanha", component: LocationPage },
  { path: "/truck-driver-jobs--sandton", component: LocationPage },
  { path: "/truck-driver-jobs--soweto", component: LocationPage },
  { path: "/truck-driver-jobs--springs", component: LocationPage },
  { path: "/truck-driver-jobs--standerton", component: LocationPage },
  { path: "/truck-driver-jobs--tembisa", component: LocationPage },
  { path: "/truck-driver-jobs--thohoyandou", component: LocationPage },
  { path: "/truck-driver-jobs--tzaneen", component: LocationPage },
  { path: "/truck-driver-jobs--uitenhage", component: LocationPage },
  { path: "/truck-driver-jobs--ulundi", component: LocationPage },
  { path: "/truck-driver-jobs--umtata", component: LocationPage },
  { path: "/truck-driver-jobs--upington", component: LocationPage },
  { path: "/truck-driver-jobs--vanderbijlpark", component: LocationPage },
  { path: "/truck-driver-jobs--vereeniging", component: LocationPage },
  { path: "/truck-driver-jobs--volksrust", component: LocationPage },
  { path: "/truck-driver-jobs--vredenburg", component: LocationPage },
  { path: "/truck-driver-jobs--vryburg", component: LocationPage },
  { path: "/truck-driver-jobs--vryheid", component: LocationPage },
  { path: "/truck-driver-jobs--welkom", component: LocationPage },
  { path: "/truck-driver-jobs--witbank", component: LocationPage },
  { path: "/truck-driver-jobs--worcester", component: LocationPage },
  { path: "/uber-boksburg", component: LocationPage },
  { path: "/uber-ekurhuleni", component: LocationPage },
  { path: "/uber-gqeberha", component: LocationPage },
  { path: "/uber-kempton-park", component: LocationPage },
  { path: "/uber-mitchells-plain", component: LocationPage },
  { path: "/uber-tembisa", component: LocationPage },
  { path: "/uber-truck-boksburg", component: LocationPage },
  { path: "/uber-truck-ekurhuleni", component: LocationPage },
  { path: "/uber-truck-gqeberha", component: LocationPage },
  { path: "/uber-truck-kempton-park", component: LocationPage },
  { path: "/uber-truck-mitchells-plain", component: LocationPage },
  { path: "/uber-truck-tembisa", component: LocationPage },
  { path: "/uber-truck-witbank", component: LocationPage },
  { path: "/uber-witbank", component: LocationPage },
  { path: "/vehicle-rental-boksburg", component: LocationPage },
  { path: "/vehicle-rental-ekurhuleni", component: LocationPage },
  { path: "/vehicle-rental-gqeberha", component: LocationPage },
  { path: "/vehicle-rental-kempton-park", component: LocationPage },
  { path: "/vehicle-rental-mitchells-plain", component: LocationPage },
  { path: "/vehicle-rental-tembisa", component: LocationPage },
  { path: "/vehicle-rental-witbank", component: LocationPage },

  // Catch-all 404 - MUST be last so all location pages match first
  ...doubleDashAliasRoutes,
  { path: "/furniture-delivery--:city", component: LocationPage },
  { path: "*", component: f404 },
];

export default routes;
